import enclosure from '../shared/placeVisibility.js';
import { acquirePlayBootstrap, expandBootstrapPlaces, createBackgroundReads } from './playBootstrap.js';
import { playerSelectionKey } from '../cdo/shared/playerTransport.js';
import { buildVisiblePlaceCards, visiblePlaceIdsAt, settlementSubPlaceIds } from './playPlaceCards.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMultipartPathData } from './placeOverviewMap.js';
import { mergePlayVitals, applyConsumptionToInventory, applyItemHandoffsToInventory } from './playVitals.js';
import { activeRoutineScene, applyRoutineStateToPlaceContext, hydrateRoutinePlaceContext } from './playRoutines.js';
import { createKnowledgeCheckQueue } from './playKnowledgeChecks.js';
import { applyNarrationCorrections } from './playNarrationCorrections.js';
import { recoverActionFailure, restorePendingResponse } from './playActionRecovery.js';
import cdoAI from '../cdo/cdoAI.js';
import placeIdentity from '../shared/placeIdentity.js';
import cdoBeing from '../cdo/cdoBeing.js';
import cdoEvent from '../cdo/cdoEvent.js';
import cdoGameplay from '../cdo/cdoGameplay.js';
import SkillProgressionPanel, { SkillProgressDetails } from './SkillProgressionPanel.js';
import { localDebugEnabled } from '../shared/localDebug.js';
import cdoPlace from '../cdo/cdoPlace.js';
import cdoService from '../cdo/cdoService.js';
import CharacterSheet from './characterSheet.js';
import ContractTerms, { isItemCommission } from './ContractTerms.js';
import PlayActionComposer from './PlayActionComposer.js';
import PlaySceneCards from './PlaySceneCards.js';
import MapScrollViewport from './MapScrollViewport.js';
import ExitGameButton from './ExitGameButton.js';
import PlayActionProgress from './PlayActionProgress.js';
import PartyPanel, { PartyVitals, HarvestInventory } from './PartyPanel.js';
import { mediaSource } from '../shared/mediaSource';
import { PlayPlaceCard, PlayPlayerCard, PlayBeingCard } from './PlaySceneCard';
import PlayChatLog from './PlayChatLog';
import { buildPortraitAppearanceGuidance } from '../shared/portraitAppearance.js';
import { buildSettlementStartLocation } from './playStartLocation.js';

const ADENREL_CHARACTER_COOKIE = 'Adenrel';
const SETTLEMENT_INDEX = 2;
const DEFAULT_START_TIME_MINUTES = 10 * 60;
const NEARBY_BEING_RADIUS_FEET = 160;
const NEARBY_PLACE_RADIUS_FEET = 140;
const PLACE_OVERVIEW_SIZE = 720;
const PLACE_OVERVIEW_MIN_ZOOM = 1;
const REGIONAL_MAP_MIN_ZOOM = 0.0001;
const REGIONAL_MAP_INITIAL_ZOOM = 20;
const REGIONAL_MAP_RADIUS_FEET = 5 * 5280;
const PLACE_OVERVIEW_MAX_ZOOM = 256;
const PLACE_OVERVIEW_ZOOM_STEP = 1.35;

const SHAPE_TYPE_ID_TO_NAME = {
  7000: 'point',
  7001: 'sphere',
  7002: 'cylinder',
  7003: 'box',
  7004: 'polygon',
  7005: 'path',
  7006: 'polyhedron',
  7007: 'heightfield',
};

const PLACE_MAP_COLORS = [
  { fill: 'rgba(76, 137, 180, 0.35)', stroke: '#7fb4d9' },
  { fill: 'rgba(83, 157, 102, 0.32)', stroke: '#8dd29b' },
  { fill: 'rgba(196, 151, 77, 0.34)', stroke: '#d7ba74' },
  { fill: 'rgba(141, 112, 190, 0.34)', stroke: '#b6a4e8' },
  { fill: 'rgba(178, 92, 91, 0.32)', stroke: '#e09a95' },
  { fill: 'rgba(130, 144, 164, 0.32)', stroke: '#b8c5d5' },
];

const FIXED_PLACE_MAP_COLORS = [
  { match: 'bridge', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'brook', fill: 'none', stroke: '#62b1e8', preserveStroke: true },
  { match: 'canal', fill: 'none', stroke: '#62b1e8', preserveStroke: true },
  { match: 'causeway', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'creek', fill: 'none', stroke: '#62b1e8', preserveStroke: true },
  { match: 'ford', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'lane', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'passage', fill: 'rgba(55, 65, 81, 0.68)', stroke: '#94a3b8', preserveStroke: true },
  { match: 'road', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'street', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'river', fill: 'none', stroke: '#62b1e8', preserveStroke: true },
  { match: 'stream', fill: 'none', stroke: '#62b1e8', preserveStroke: true },
  { match: 'waterway', fill: 'none', stroke: '#62b1e8', preserveStroke: true },
  { match: 'water feature', fill: 'rgba(34, 111, 155, 0.38)', stroke: '#75c7f0' },
  { match: 'way', fill: 'none', stroke: '#a8b3c2', preserveStroke: true },
  { match: 'lake', fill: 'rgba(34, 111, 155, 0.38)', stroke: '#75c7f0' },
  { match: 'forest', fill: 'rgba(22, 101, 52, 0.42)', stroke: '#75c589' },
];

const LINEAR_PLACE_MAP_KEYWORDS = [
  'bridge',
  'bridges',
  'brook',
  'brooks',
  'canal',
  'canals',
  'causeway',
  'causeways',
  'creek',
  'creeks',
  'ford',
  'fords',
  'lane',
  'lanes',
  'road',
  'roads',
  'river',
  'rivers',
  'street',
  'streets',
  'route',
  'routes',
  'trail',
  'trails',
  'highway',
  'highways',
  'passage',
  'passages',
  'path',
  'paths',
  'stream',
  'streams',
  'waterway',
  'waterways',
  'way',
  'ways',
];

const ROAD_ENTRY_PLACE_KEYWORDS = [
  'bridge',
  'bridges',
  'causeway',
  'causeways',
  'ford',
  'fords',
  'lane',
  'lanes',
  'passage',
  'passages',
  'path',
  'paths',
  'road',
  'roads',
  'route',
  'routes',
  'street',
  'streets',
  'trail',
  'trails',
  'way',
  'ways',
];

const NORTH_ENTRY_BEING_TYPES = new Set(['dwarf', 'gnome', 'halfling']);
const SOUTH_ENTRY_BEING_TYPES = new Set(['human', 'elf']);
const TIME_ADVANCE_INTENTS = new Set([
  'wait',
  'rest',
  'sleep',
]);

const DEFAULT_TIME_ADVANCE_MINUTES = {
  wait: 10,
  rest: 30,
  sleep: 480,
};

const MAX_TIME_ADVANCE_MINUTES = 60 * 24 * 30;

const SOCIAL_RESPONSE_INTENTS = new Set([
  'speak',
  'persuade',
  'deceive',
  'intimidate',
  'barter',
  'trade',
  'command',
]);

const COMMON_BEING_ROLES = [
  'tavern keeper',
  'innkeeper',
  'shopkeeper',
  'weaponsmith',
  'blacksmith',
  'merchant',
  'guard',
  'watchman',
  'watchpost guard',
  'stable hand',
  'outfitter',
  'priest',
  'healer',
  'scribe',
  'worker',
  'farmer',
  'porter',
  'captain',
  'soldier',
];

const createEmptyInteractionState = () => ({
  messages: [],
  draft: '',
  isSubmitting: false,
  error: '',
  lastPlayerAction: null,
  npcResponseGroups: [],
  npcPlaceholder: '',
  narrationPlaceholder: '',
  lastNarration: null,
  history: [],
  recentNarration: [],
  rollData: null,
  tradeResults: [],
  contractResults: [],
  spellAcquisitionResult: null,
  spellAcquisitionState: null,
  combatResult: null,
  combatParticipationResult: null,
  currentSocialTarget: null,
});

const formatTradeQuantity = (row) => {
  const quantity = Math.max(0, Math.floor(Number(row?.quantity) || 0));
  const name = String(row?.currencyName || row?.name || 'coin').trim().toLowerCase();
  return `${quantity} ${name}`;
};

const formatTradeCurrencyRows = (rows) => {
  const parts = toArray(rows)
    .filter((row) => Number(row?.quantity) > 0)
    .map(formatTradeQuantity);

  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0];
  }

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const formatTradeItems = (items) => {
  const names = toArray(items)
    .map((item) => String(item?.itemName || item?.name || item?.title || item?.itemId || '').trim())
    .filter(Boolean);

  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const getTradeConfirmationLines = (items, currencyRows) =>
  [
    formatTradeItems(items),
    formatTradeCurrencyRows(currencyRows),
  ].filter(Boolean);

const firstTransactionConfirmation = (...resultGroups) =>
  resultGroups
    .flatMap((results) => toArray(results))
    .find((result) => result?.confirmationRequired && result?.tradeConfirmation)?.tradeConfirmation || null;

const createEmptyMovementState = () => ({
  placeId: '',
  pendingRoute: [],
  lastMovementResult: null,
  lastMovementTarget: null,
});

const collectErrorText = (value, seen = new Set()) => {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (typeof value !== 'object' || seen.has(value)) {
    return [];
  }

  seen.add(value);
  const fields = ['message', 'sqlMessage', 'error', 'code', 'status', 'statusText', 'rawBody', 'apiResponse'];
  const parts = fields.flatMap((field) => collectErrorText(value[field], seen));

  try {
    parts.push(JSON.stringify(value));
  } catch {
    return parts.filter(Boolean);
  }

  return parts.filter(Boolean);
};

const getFriendlyAiErrorMessage = (source) => {
  const text = collectErrorText(source).join(' ').toLowerCase();
  if (!text) {
    return '';
  }

  if (
    text.includes('exceeded your current quota') ||
    text.includes('insufficient_quota') ||
    text.includes('billing details')
  ) {
    return 'AI token balance is empty. Deposit more AI tokens into your account and try again.';
  }

  if (text.includes('429 too many requests') || text.includes('request failed (429') || text.includes('rate limit')) {
    return 'AI request limit reached. Wait a moment and try again.';
  }

  return '';
};

const formatErrorMessage = (err, response) => {
  const source = response?.error ?? response ?? err;
  const friendlyAiError = getFriendlyAiErrorMessage(source);
  if (friendlyAiError) {
    return friendlyAiError;
  }

  if (typeof source === 'string') {
    return source;
  }

  if (typeof source === 'number') {
    return `Request failed with status ${source}.`;
  }

  if (!source || typeof source !== 'object') {
    return 'Request failed.';
  }

  return source.message || source.sqlMessage || source.error || JSON.stringify(source);
};

const callCdo = (fn, ...args) =>
  new Promise((resolve, reject) => {
    fn(...args, (err, response) => {
      if (err || response?.error) {
        let details = response;
        if (typeof details === 'string') {
          try { details = JSON.parse(details); } catch { /* Retain non-JSON transport errors. */ }
        }
        const fault = details?.error || details || err;
        reject(Object.assign(new Error(formatErrorMessage(err, details)), {
          status: fault?.status ?? (typeof err === 'number' ? err : undefined), code: fault?.code,
        }));
        return;
      }

      resolve(response);
    });
  });

const readCookie = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : '';
};

const normalizeLookupValue = (value) => String(value ?? '').trim().toLowerCase();

const getEntityLabel = (entity, fallback = 'Unnamed') =>
  entity?.name || entity?.title || entity?.entityName || entity?.entityTitle || entity?.idEntity || fallback;

const getPlaceLabel = (place) => getEntityLabel(place, 'Unnamed place');

const getBeingLabel = (being) => getEntityLabel(being, 'Unnamed being');

const compactUnknownBeingLabel = (label) => {
  const text = String(label ?? '').trim();
  if (!text) {
    return 'Unknown person';
  }

  const roleMatch = text.match(new RegExp(`\\b(${COMMON_BEING_ROLES.map((role) => escapeRegExp(role)).join('|')})\\b`, 'i'));
  if (roleMatch) {
    return `the ${roleMatch[1].toLowerCase()}`;
  }

  return text.length <= 42 ? text : `${text.slice(0, 39).trimEnd()}...`;
};

const escapeRegExp = (value) => String(value ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getHiddenBeingReplacementSources = (being) => {
  const names = [
    being?.name,
    being?.entityName,
  ]
    .map((name) => String(name ?? '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const firstNames = names
    .map((name) => name.split(/\s+/)[0])
    .filter((name) => name && name.length >= 4);
  return [...names, ...firstNames];
};

const removeHiddenNameSources = (value, being) => {
  let text = String(value ?? '');
  getHiddenBeingReplacementSources(being).forEach((source) => {
    if (!source) {
      return;
    }
    text = text.replace(new RegExp(`\\b${escapeRegExp(source)}\\b`, 'gi'), '').replace(/\s+/g, ' ').trim();
  });
  return text;
};

const buildUnknownBeingLabel = (being) => {
  if (being?.encounter && being?.publicLabel) return being.publicLabel;
  const title = removeHiddenNameSources(being?.title ?? being?.entityTitle ?? '', being).toLowerCase();
  const description = removeHiddenNameSources(being?.description ?? being?.entityDesc ?? '', being);
  const roleText = `${title} ${description.toLowerCase()}`;
  const role = COMMON_BEING_ROLES.find((candidate) => roleText.includes(candidate));
  if (role) {
    return `the ${role}`;
  }

  const beingType = String(being?.beingType ?? '').trim().toLowerCase();
  if (beingType && !['being', 'person', 'human'].includes(beingType)) {
    return `the ${beingType}`;
  }

  if (description) {
    const shortDescription = description.split(/[.!?]/)[0]?.trim();
    if (shortDescription && shortDescription.length <= 42) {
      return shortDescription;
    }
  }

  return 'the nearby person';
};

const getBeingCardLabel = (being) =>
  being?.identityKnownToPc === false
    ? compactUnknownBeingLabel(being?.publicLabel || being?.knownLabel || 'Unknown person')
    : being?.knownLabel || getBeingLabel(being);

const buildNpcPortraitPrompt = (being) => {
  const statSummary = ['strength', 'agility', 'constitution', 'intellect', 'wisdom', 'charisma']
    .map((field) => `${field}: ${being?.[field] ?? ''}`)
    .join(', ');
  const placeContext = [
    being?.currentPlaceName ? `Current place: ${being.currentPlaceName}` : '',
    being?.containingPlaceName ? `Containing place: ${being.containingPlaceName}` : '',
    being?.memberPlaceName ? `Associated place: ${being.memberPlaceName}` : '',
  ].filter(Boolean).join('\n');
  const label = getBeingCardLabel(being);

  return `Create a square 1:1 portrait image of this Adenrel NPC.

Name or public label: ${label}
Title: ${being?.identityKnownToPc === false ? 'Unknown' : being?.title || 'None'}
Level: ${being?.level || 'Unspecified'}
Being type: ${being?.beingType || 'Unspecified'}
Default form: ${being?.defaultForm || being?.beingType || 'Unspecified'}
Alignment: ${being?.alignment || 'Unspecified'}
Description: ${being?.description || 'No description provided.'}
Personality: ${being?.personality || 'Unspecified'}
Short-term objective: ${being?.objectivesShortTerm || 'Unspecified'}
Long-term objective: ${being?.objectivesLongTerm || 'Unspecified'}
Stats: ${statSummary}
${placeContext || 'No place context provided.'}

${buildPortraitAppearanceGuidance(being)}

Visual requirements:
- Compose for a square NPC card portrait.
- Show the NPC clearly as the main subject.
- Favor a chest-up or waist-up portrait with readable face, clothing, and posture.
- Use the supplied role, place context, and description as visual grounding.
- Do not include text, captions, UI, logos, watermarks, or labels.`;
};

const toDataImage = (response) => mediaSource(response, 'card');

export const loadOrGenerateBeingPortrait = async ({ being, loadPortrait, generatePortrait, savePortrait }) => {
  const templateId = being?.portraitTemplateId || being?.encounter?.templateId;
  if (templateId) {
    const templatePortrait = await loadPortrait(templateId);
    return { src: toDataImage(templatePortrait), wasGenerated: false };
  }
  const beingId = getEntityId(being);
  const response = await loadPortrait(beingId);
  const savedSrc = toDataImage(response);
  if (savedSrc) {
    return { src: savedSrc, wasGenerated: false };
  }

  const generated = await generatePortrait({
    beingId,
    aogYear: 0,
    size: '1024x1024',
    prompt: buildNpcPortraitPrompt(being),
  });
  if (!generated?.imageBase64) {
    throw new Error('Image generation returned no image data.');
  }

  const saved = await savePortrait({
    idEntity: beingId,
    name: getBeingCardLabel(being),
    title: being?.identityKnownToPc === false ? getBeingCardLabel(being) : being?.title || getBeingCardLabel(being),
    mediaType: generated.mimeType || 'image/png',
    imageBase64: generated.imageBase64,
    revisedPrompt: generated.revisedPrompt || '',
  });
  return {
    src: toDataImage(saved) || `data:${generated.mimeType || 'image/png'};base64,${generated.imageBase64}`,
    wasGenerated: true,
  };
};

const toArray = (value) => (Array.isArray(value) ? value : []);

export const visibleResumeOptions = (placeKnowledge, resumeOptions, journeyActivityId) => {
  const search = placeKnowledge.destinationSearch;
  const resolved = search?.status === 'found' || search?.status === 'cancelled' || search?.outcome === 'identified';
  const searchOptions = toArray(placeKnowledge.destinationSearchResumeOptions ?? toArray(resumeOptions).filter(option => option.searchId))
    .filter(option => !resolved && (!Object.hasOwn(placeKnowledge, 'destinationSearch') || search)
      && (!search?.searchId || (option.executionControl?.searchId || option.searchId) === search.searchId));
  return [...toArray(resumeOptions).filter(option => !option.searchId), ...searchOptions]
    .filter(option => !option.activityId || option.activityId !== journeyActivityId);
};

const getEntityId = (entity) => String(entity?.idEntity ?? '').trim();

const uniqueById = (values) => {
  const seen = new Set();
  const unique = [];

  for (const value of toArray(values)) {
    const idEntity = getEntityId(value);
    if (!idEntity || seen.has(idEntity)) {
      continue;
    }

    seen.add(idEntity);
    unique.push(value);
  }

  return unique;
};

const normalizeMovementSessionState = (movementState = {}, fallbackPlaceId = '') => ({
  placeId: String(movementState?.placeId ?? movementState?.currentInteriorPlaceId ?? fallbackPlaceId ?? '').trim(),
  pendingRoute: toArray(movementState?.pendingRoute),
  lastMovementResult: movementState?.lastMovementResult ?? null,
  lastMovementTarget: movementState?.lastMovementTarget ?? null,
  journey: movementState?.journey ?? null,
});

const getGameplaySessionPlaceId = ({
  movementState,
  currentLocation,
  currentPlaceContext,
  fallbackPlaceId = '',
}) =>
  String(
    currentLocation?.placeId ||
    movementState?.placeId ||
    getEntityId(currentPlaceContext?.place) ||
    fallbackPlaceId ||
    ''
  ).trim();

const compactGameplayContextForSession = (gameplayContext) => {
  if (!gameplayContext || typeof gameplayContext !== 'object') {
    return gameplayContext;
  }

  const {
    locationData,
    restoredSession,
    sessionData,
    ...rest
  } = gameplayContext;

  return rest;
};

const buildGameplaySessionPayload = ({
  playerId,
  placeId = '',
  gameplayContext = null,
  currentLocation = null,
  movementState = null,
  combatState = null,
  interactionState = null,
  narrationContext = null,
  pendingTradeConfirmation = null,
  currentPlaceContext = null,
}) => {
  const normalizedPlaceId = String(placeId || getGameplaySessionPlaceId({
    movementState,
    currentLocation,
    currentPlaceContext,
    fallbackPlaceId: gameplayContext?.placeId || gameplayContext?.settlementId,
  })).trim();
  const normalizedMovementState = normalizeMovementSessionState({
    ...(movementState && typeof movementState === 'object' ? movementState : {}),
    placeId: normalizedPlaceId,
  }, normalizedPlaceId);
  const compactGameplayContext = compactGameplayContextForSession(gameplayContext);

  return {
    playerId,
    placeId: normalizedPlaceId,
    locationData: {
      currentLocation,
      settlementId: compactGameplayContext?.settlementId || '',
      currentPlaceId: getEntityId(currentPlaceContext?.place),
    },
    sessionData: {
      playerId,
      placeId: normalizedPlaceId,
      gameplayContext: compactGameplayContext,
      movementState: normalizedMovementState,
      combatState,
      interactionState,
      narrationContext,
      pendingTradeConfirmation,
    },
  };
};

const saveGameplaySessionSnapshot = async (snapshot) => {
  const playerId = String(snapshot?.playerId ?? '').trim();
  if (!playerId) {
    return null;
  }

  return callCdo(cdoGameplay.saveSession.bind(cdoGameplay), buildGameplaySessionPayload(snapshot));
};

const getRestoredGameplaySessionState = (gameplayContext) => {
  if (gameplayContext?.restoredSession !== true) {
    return null;
  }

  const sessionData = gameplayContext?.sessionData && typeof gameplayContext.sessionData === 'object'
    ? gameplayContext.sessionData
    : {};
  const locationData = gameplayContext?.locationData && typeof gameplayContext.locationData === 'object'
    ? gameplayContext.locationData
    : {};
  const placeId = String(
    locationData.currentLocation?.placeId ||
    sessionData.placeId ||
    gameplayContext.placeId ||
    ''
  ).trim();
  const restoredGameplayContext = sessionData.gameplayContext && typeof sessionData.gameplayContext === 'object'
    ? sessionData.gameplayContext
    : gameplayContext;

  return {
    playerId: String(gameplayContext.playerId || sessionData.playerId || '').trim(),
    placeId,
    currentLocation: locationData.currentLocation || null,
    gameplayContext: {
      ...gameplayContext,
      ...restoredGameplayContext,
      restoredSession: true,
      playerId: gameplayContext.playerId || sessionData.playerId || '',
      placeId,
      locationData,
      sessionData,
    },
    movementState: normalizeMovementSessionState({
      ...(sessionData.movementState && typeof sessionData.movementState === 'object' ? sessionData.movementState : {}),
      placeId,
    }, placeId),
    combatState: sessionData.combatState || null,
    interactionState: sessionData.interactionState || null,
    pendingResponse: sessionData.pendingResponse || null,
    narrationContext: sessionData.narrationContext || null,
    pendingTradeConfirmation: sessionData.pendingTradeConfirmation || null,
  };
};

const shouldCapitalizeReplacementAt = (segment, offset) => {
  const before = String(segment ?? '').slice(0, offset);
  return !before.trim() || /[.!?]\s*$/.test(before);
};

const capitalizeFirst = (value) => {
  const text = String(value ?? '');
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
};

const replaceHiddenBeingNamesInSegment = (segment, replacements = []) => {
  let nextSegment = String(segment ?? '');
  replacements.forEach((replacement) => {
    const source = String(replacement?.source ?? '').trim();
    const label = String(replacement?.label ?? '').trim();
    if (!source || !label) {
      return;
    }

    const pattern = new RegExp(`\\b${escapeRegExp(source)}\\b`, 'gi');
    nextSegment = nextSegment.replace(pattern, (match, offset) =>
      shouldCapitalizeReplacementAt(nextSegment, offset) ? capitalizeFirst(label) : label
    );
  });
  return nextSegment;
};

const replaceHiddenBeingNamesOutsideQuotedText = (value, replacements = []) => {
  const text = String(value ?? '');
  if (!text || replacements.length === 0) {
    return text;
  }

  let result = '';
  let segment = '';
  let inQuote = false;
  for (const char of text) {
    if (char === '"' || char === '\u201c') {
      if (!inQuote) {
        result += replaceHiddenBeingNamesInSegment(segment, replacements);
        segment = '';
        inQuote = true;
      } else if (char === '"') {
        inQuote = false;
      }
      result += char;
      continue;
    }

    if (char === '\u201d') {
      inQuote = false;
      result += char;
      continue;
    }

    if (inQuote) {
      result += char;
    } else {
      segment += char;
    }
  }

  return result + replaceHiddenBeingNamesInSegment(segment, replacements);
};

const isIdentifierLikePromptKey = (key) => {
  const normalized = String(key ?? '').toLowerCase();
  return normalized === 'id' ||
    normalized.endsWith('id') ||
    normalized.endsWith('ids') ||
    normalized.endsWith('key') ||
    normalized.includes('identity');
};

const sanitizePromptDataForHiddenBeingNames = (value, replacements = [], key = '') => {
  if (!value || replacements.length === 0) {
    return value;
  }

  if (typeof value === 'string') {
    return isIdentifierLikePromptKey(key)
      ? value
      : replaceHiddenBeingNamesInSegment(value, replacements);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizePromptDataForHiddenBeingNames(entry, replacements, key));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [
      entryKey,
      sanitizePromptDataForHiddenBeingNames(entryValue, replacements, entryKey),
    ]));
  }

  return value;
};

const sanitizeNarrationTextForHiddenBeingNames = (value, replacements = []) =>
  replaceHiddenBeingNamesOutsideQuotedText(value, replacements);

const buildHiddenBeingNameReplacements = (beings, knownBeingNameIds = new Set(), playerCharacterId = '') => {
  const replacementsBySource = new Map();
  const knownIds = knownBeingNameIds instanceof Set ? knownBeingNameIds : new Set();
  const pcId = String(playerCharacterId ?? '').trim();

  uniqueById(beings).forEach((being) => {
    const beingId = getEntityId(being);
    if (!beingId || beingId === pcId || knownIds.has(beingId)) {
      return;
    }

    const label = buildUnknownBeingLabel(being);
    getHiddenBeingReplacementSources(being).forEach((source) => {
      if (!source || source.toLowerCase() === label.toLowerCase()) {
        return;
      }

      const key = source.toLowerCase();
      if (!replacementsBySource.has(key)) {
        replacementsBySource.set(key, { source, label, beingId });
      }
    });
  });

  return [...replacementsBySource.values()].sort((left, right) => right.source.length - left.source.length);
};

const maskBeingIdentityForPc = (being, knownBeingNameIds = new Set(), playerCharacterId = '') => {
  if (!being) {
    return null;
  }

  const beingId = getEntityId(being);
  const knownIds = knownBeingNameIds instanceof Set ? knownBeingNameIds : new Set();
  const identityKnownToPc = Boolean(beingId && (beingId === playerCharacterId || knownIds.has(beingId)));
  const publicLabel = buildUnknownBeingLabel(being);
  if (identityKnownToPc) {
    return {
      ...being,
      publicLabel,
      knownLabel: being?.knownLabel || getBeingLabel(being),
      identityKnownToPc: true,
    };
  }

  const replacements = buildHiddenBeingNameReplacements([being], new Set(), playerCharacterId);
  const sanitizedBeing = sanitizePromptDataForHiddenBeingNames(being, replacements);
  return {
    ...sanitizedBeing,
    idEntity: beingId,
    identityKnownToPc: false,
    publicLabel,
    knownLabel: publicLabel,
    name: publicLabel,
    title: '',
    entityName: publicLabel,
    entityTitle: '',
  };
};

const maskBeingsForPc = (beings, knownBeingNameIds = new Set(), playerCharacterId = '') =>
  uniqueById(beings)
    .map((being) => maskBeingIdentityForPc(being, knownBeingNameIds, playerCharacterId))
    .filter(Boolean);

const maskPlaceContextForPc = (placeContext, knownBeingNameIds = new Set(), playerCharacterId = '') => {
  if (!placeContext) {
    return placeContext;
  }

  return {
    ...placeContext,
    beingsInLocation: maskBeingsForPc(placeContext.beingsInLocation, knownBeingNameIds, playerCharacterId),
    subPlaces: toArray(placeContext.subPlaces).map((place) => ({
      ...place,
      savedInhabitants: maskBeingsForPc(place?.savedInhabitants, knownBeingNameIds, playerCharacterId),
      containedVisitors: maskBeingsForPc(place?.containedVisitors, knownBeingNameIds, playerCharacterId),
    })),
  };
};

const buildNpcResponseIdentityPolicy = (responders = [], knownBeingNameIds = new Set(), playerCharacterId = '') => ({
  privateIdentitiesByResponderId: Object.fromEntries(uniqueById(responders)
    .map((being) => {
      const beingId = getEntityId(being);
      if (!beingId) {
        return null;
      }

      const identityKnownToPc = beingId === playerCharacterId || knownBeingNameIds.has(beingId);
      return [beingId, {
        idEntity: beingId,
        publicLabel: identityKnownToPc ? getBeingLabel(being) : buildUnknownBeingLabel(being),
        identityKnownToPc,
        privateName: being?.name || being?.entityName || '',
        privateTitle: being?.title || being?.entityTitle || '',
        usePrivateNameOnlyIfSpokenAsIntroduction: identityKnownToPc === false,
      }];
    })
    .filter(Boolean)),
});

const sanitizeMessagesForHiddenBeingNames = (messages, replacements = []) =>
  toArray(messages).map((message) => sanitizePromptDataForHiddenBeingNames(message, replacements));

const parseJsonObject = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const summarizeRelationshipForNarration = (relationship) => ({
  direction: relationship?.direction ?? '',
  relateType: relationship?.relateType ?? '',
  otherEntityId: relationship?.otherEntityId ?? relationship?.idRelated ?? relationship?.idEntity ?? '',
  otherEntityName: relationship?.otherEntityName ?? relationship?.entityName ?? '',
  otherEntityTitle: relationship?.otherEntityTitle ?? relationship?.entityTitle ?? '',
  otherEntityType: relationship?.otherEntityType ?? '',
  score: relationship?.score ?? null,
  demeanor: relationship?.demeanor ?? '',
});

const summarizeItemForNarration = (item) => ({
  idEntity: item?.idEntity ?? '',
  name: item?.name ?? item?.entityName ?? '',
  title: item?.title ?? item?.entityTitle ?? '',
  description: item?.description ?? item?.entityDesc ?? '',
  itemType: item?.itemType ?? '',
  weapon: item?.weapon ?? '',
  worn: item?.worn ?? '',
  general: item?.general ?? '',
  material: item?.material ?? '',
  physicalState: item?.physicalState ?? '',
  physicallyCarried: item?.physicallyCarried ?? null,
  combatHeld: item?.combatHeld ?? null,
  carriedWithBodyPart: item?.carriedWithBodyPart ?? '',
  damage: item?.damage ?? null,
  class: item?.class ?? null,
  qualityLevel: item?.qualityLevel ?? null,
  magicLevel: item?.magicLevel ?? null,
  quantity: item?.quantity ?? item?.count ?? null,
  volume: item?.volume ?? null,
  provisionMaterial: item?.provisionMaterialName ?? item?.provisionMaterial ?? '',
  containedItems: toArray(item?.containedItems).map(summarizeItemForNarration),
  value: item?.value ?? item?.baseValueGp ?? null,
});

const summarizeServiceForNarration = (service) => ({
  idEntity: service?.idEntity ?? '',
  name: service?.name ?? service?.entityName ?? '',
  title: service?.title ?? service?.entityTitle ?? '',
  description: service?.description ?? service?.entityDesc ?? '',
  serviceType: service?.serviceType ?? '',
  serviceTypeName: service?.serviceTypeName ?? '',
  basePrice: service?.basePrice ?? null,
  baseDurationAmount: service?.baseDurationAmount ?? null,
  baseDurationUnit: service?.baseDurationUnit ?? '',
  rules: parseJsonObject(service?.rulesJson),
});

const summarizePlaceForNarration = (place) => ({
  idEntity: place?.idEntity ?? '',
  name: place?.name ?? place?.entityName ?? '',
  title: place?.title ?? place?.entityTitle ?? '',
  description: place?.description ?? place?.entityDesc ?? '',
  level: place?.level ?? '',
  placeType: place?.placeType ?? '',
  zoneType: place?.zoneType ?? '',
  buildingType: place?.buildingType ?? '',
  transitPointType: place?.transitPointType ?? '',
  location: place?.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
  shape: place?.shape ?? {},
  containedItems: toArray(place?.containedItems).map(summarizeItemForNarration),
  subPlaceIds: toArray(place?.composedOfChildren).map((child) => child?.idEntity).filter(Boolean),
  relationships: toArray(place?.relationships).map(summarizeRelationshipForNarration),
});

const summarizeBeingForNarration = (being) => ({
  idEntity: being?.idEntity ?? '',
  name: being?.name ?? being?.entityName ?? '',
  title: being?.title ?? being?.entityTitle ?? '',
  publicLabel: being?.publicLabel ?? '',
  knownLabel: being?.knownLabel ?? '',
  identityKnownToPc: being?.identityKnownToPc ?? null,
  description: being?.description ?? being?.entityDesc ?? '',
  level: being?.level ?? '',
  beingType: being?.beingType ?? '',
  gender: being?.gender ?? '',
  alignment: being?.alignment ?? '',
  personality: being?.personality ?? '',
  objectivesShortTerm: being?.objectivesShortTerm ?? '',
  objectivesLongTerm: being?.objectivesLongTerm ?? '',
  location: being?.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
  hunger: being?.hunger ?? 0,
  thirst: being?.thirst ?? 0,
  currentStamina: being?.currentStamina ?? 100,
  intoxication: being?.intoxication ?? 0,
  ownedItems: toArray(being?.ownedItems).map(summarizeItemForNarration),
  relationships: toArray(being?.relationships).map(summarizeRelationshipForNarration),
  currencies: toArray(being?.currencies),
  routines: toArray(being?.routines),
  currentActivity: being?.currentActivity ?? '',
  routineState: being?.routineState ?? null,
  currentHp: being?.currentHp ?? null,
  maxHp: being?.maxHp ?? null,
  combatStatus: being?.combatStatus ?? '',
});

const getSocialFrameTarget = (being) => {
  if (!being) {
    return null;
  }

  return {
    idEntity: being.idEntity || '',
    name: being.name || being.entityName || '',
    title: being.title || being.entityTitle || '',
    publicLabel: being.publicLabel || '',
    knownLabel: being.knownLabel || '',
    identityKnownToPc: being.identityKnownToPc === true,
    description: being.description || '',
    location: being.location || null,
  };
};

const getSocialFrameTargets = (beings) =>
  toArray(beings).map(getSocialFrameTarget).filter((target) => target?.idEntity);

const resolveCurrentSocialFrameTarget = (currentTarget, nearbyTargets) => {
  const currentTargetId = String(currentTarget?.idEntity ?? '').trim();
  if (!currentTargetId) {
    return null;
  }

  return toArray(nearbyTargets).find((target) => String(target?.idEntity ?? '').trim() === currentTargetId) || null;
};

export const resolveNextSocialTarget = (playerAction, targets, groups, previous) => {
  const spoken = toArray(groups).flatMap(group => toArray(group.result?.responses))
    .filter(response => String(response.verbalResponse || '').trim());
  const lastSpeakerId = spoken.length ? spoken[spoken.length - 1].idEntity : null;
  const addressed = toArray(playerAction?.targetEntityIds);
  const targetId = lastSpeakerId || (addressed.length === 1 ? addressed[0] : previous?.idEntity);
  return toArray(targets).find(target => target.idEntity === targetId) || null;
};

const getResultTargetEntityIds = (result) => toArray(result?.targetEntityIds).map(String).filter(Boolean);

const PLACE_AFFILIATION_RELATION_TYPES = new Set([
  'works at',
  'works for',
  'owner of',
  'owns',
  'owned by',
  'manages',
  'managed by',
  'protects',
  'protected by',
  'guards',
  'guarded by',
  'lives at',
]);

const getRelationshipPlaceId = (relationship) =>
  String(relationship?.otherEntityType ?? '').toLowerCase() === 'place'
    ? relationship?.otherEntityId || relationship?.idRelated || ''
    : '';

const isPlaceAffiliationRelationship = (relationship) =>
  PLACE_AFFILIATION_RELATION_TYPES.has(String(relationship?.relateType ?? '').toLowerCase());

const getAffiliatedPlaceIds = (being) =>
  [...new Set(toArray(being?.relationships)
    .filter(isPlaceAffiliationRelationship)
    .map(getRelationshipPlaceId)
    .filter(Boolean))];

const hasFiniteCoordinate = (value) => Number.isFinite(Number(value));

const normalizeLocation = (location) => ({
  x: hasFiniteCoordinate(location?.x) ? Number(location.x) : 0,
  y: hasFiniteCoordinate(location?.y) ? Number(location.y) : 0,
  z: hasFiniteCoordinate(location?.z) ? Number(location.z) : 0,
  maxRadius: Math.max(0, hasFiniteCoordinate(location?.maxRadius) ? Number(location.maxRadius) : 0),
});

const normalizeShapeTypeName = (shapeType) => {
  const normalized = String(shapeType ?? '').trim().toLowerCase();
  return SHAPE_TYPE_ID_TO_NAME[Number(normalized)] ?? normalized;
};

const getShapeData = (shape) => {
  const shapeData = shape?.shapeData;
  if (!shapeData) {
    return {};
  }

  if (typeof shapeData === 'object') {
    return shapeData;
  }

  return parseJsonObject(shapeData) || {};
};

const getShapePoints = (place) => {
  const shape = place?.shape ?? {};
  const shapeType = normalizeShapeTypeName(shape.shapeType);
  const shapeData = getShapeData(shape);
  const points = Array.isArray(shapeData.points)
    ? shapeData.points
    : Array.isArray(shapeData.vertices)
      ? shapeData.vertices
      : [];

  if (!['polygon', 'path'].includes(shapeType) || points.length < 2) {
    return [];
  }

  return points
    .map((point) => ({
      x: Number(point?.x),
      z: Number(point?.z),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.z));
};

const getPointBounds = (points) =>
  points.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point.x),
      maxX: Math.max(bounds.maxX, point.x),
      minZ: Math.min(bounds.minZ, point.z),
      maxZ: Math.max(bounds.maxZ, point.z),
    }),
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity }
  );

const getShapeBounds = (place) => {
  const shape = place?.shape ?? {};
  const location = place?.location ?? {};
  const shapeType = normalizeShapeTypeName(shape.shapeType);
  const shapePoints = getShapePoints(place);

  if (['polygon', 'path'].includes(shapeType) && shapePoints.length >= 2) {
    return getPointBounds(shapePoints);
  }

  const radius = Math.max(10, Number(location.maxRadius) || 10);
  const centerX = Number(location.x) || 0;
  const centerZ = Number(location.z) || 0;
  const minX = Number(shape.minX);
  const maxX = Number(shape.maxX);
  const minZ = Number(shape.minZ);
  const maxZ = Number(shape.maxZ);

  if ([minX, maxX, minZ, maxZ].every((value) => Number.isFinite(value))) {
    return { minX, maxX, minZ, maxZ };
  }

  return {
    minX: centerX - radius,
    maxX: centerX + radius,
    minZ: centerZ - radius,
    maxZ: centerZ + radius,
  };
};

const getBoxShapePoints = (place) => {
  const bounds = getShapeBounds(place);

  if (![bounds.minX, bounds.maxX, bounds.minZ, bounds.maxZ].every((value) => Number.isFinite(Number(value)))) {
    return [];
  }

  return [
    { x: bounds.minX, z: bounds.minZ },
    { x: bounds.maxX, z: bounds.minZ },
    { x: bounds.maxX, z: bounds.maxZ },
    { x: bounds.minX, z: bounds.maxZ },
  ];
};

const isRectangularOverviewShape = (shapeType) =>
  shapeType === 'box' || !['polygon', 'path', 'sphere', 'cylinder'].includes(shapeType);

const getOverviewShapePoints = (place) => {
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);

  if (isRectangularOverviewShape(shapeType)) {
    return getBoxShapePoints(place);
  }

  return getShapePoints(place);
};

const getOverviewDescriptorText = (place) => [
  place?.placeType,
  place?.zoneType,
  place?.buildingType,
  place?.transitPointType,
  place?.name,
  place?.title,
  place?.entityName,
  place?.entityTitle,
].map((value) => String(value ?? '').trim().toLowerCase()).join(' ');

const isRoadOrRiverOverviewPlace = (place) => {
  const descriptor = ` ${getOverviewDescriptorText(place)} `;
  return LINEAR_PLACE_MAP_KEYWORDS.some((keyword) => descriptor.includes(` ${keyword} `));
};

const isRoadEntryOverviewPlace = (place) => {
  const descriptor = ` ${getOverviewDescriptorText(place)} `;
  return ROAD_ENTRY_PLACE_KEYWORDS.some((keyword) => descriptor.includes(` ${keyword} `));
};

const doBoundsIntersect = (leftBounds, rightBounds, padding = 0) => {
  if (
    ![leftBounds?.minX, leftBounds?.maxX, leftBounds?.minZ, leftBounds?.maxZ,
      rightBounds?.minX, rightBounds?.maxX, rightBounds?.minZ, rightBounds?.maxZ].every(Number.isFinite)
  ) {
    return false;
  }

  return !(
    leftBounds.maxX < rightBounds.minX - padding ||
    leftBounds.minX > rightBounds.maxX + padding ||
    leftBounds.maxZ < rightBounds.minZ - padding ||
    leftBounds.minZ > rightBounds.maxZ + padding
  );
};

const pointWithinHorizontalBounds = (point, bounds, padding = 0) =>
  Number(point?.x) >= bounds.minX - padding &&
  Number(point?.x) <= bounds.maxX + padding &&
  Number(point?.z) >= bounds.minZ - padding &&
  Number(point?.z) <= bounds.maxZ + padding;

export const placeContainsPosition = (place, location) => {
  const point = location?.location || location;
  if (!point) return true;
  if (!pointWithinHorizontalBounds(point, getShapeBounds(place))) return false;
  const vertices = getShapePoints(place);
  if (normalizeShapeTypeName(place?.shape?.shapeType) !== 'polygon' || vertices.length < 3) return true;
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const a = vertices[j], b = vertices[i];
    const cross = (point.x - a.x) * (b.z - a.z) - (point.z - a.z) * (b.x - a.x);
    if (Math.abs(cross) < 1e-6 && point.x >= Math.min(a.x, b.x) && point.x <= Math.max(a.x, b.x)
      && point.z >= Math.min(a.z, b.z) && point.z <= Math.max(a.z, b.z)) return true;
    if ((a.z > point.z) !== (b.z > point.z) && point.x < (b.x - a.x) * (point.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
};

const distancePointToBounds2d = (point, bounds) => {
  if (![point?.x, point?.z, bounds?.minX, bounds?.maxX, bounds?.minZ, bounds?.maxZ].every(Number.isFinite)) {
    return Infinity;
  }

  const dx = point.x < bounds.minX ? bounds.minX - point.x : point.x > bounds.maxX ? point.x - bounds.maxX : 0;
  const dz = point.z < bounds.minZ ? bounds.minZ - point.z : point.z > bounds.maxZ ? point.z - bounds.maxZ : 0;
  return Math.hypot(dx, dz);
};

const distance2d = (left, right) =>
  Math.hypot((Number(left?.x) || 0) - (Number(right?.x) || 0), (Number(left?.z) || 0) - (Number(right?.z) || 0));

const getPlaceCenterLocation = (place) => {
  const bounds = getShapeBounds(place);
  const location = normalizeLocation(place?.location);
  return {
    x: Number.isFinite(bounds.minX) && Number.isFinite(bounds.maxX) ? (bounds.minX + bounds.maxX) / 2 : location.x,
    y: location.y,
    z: Number.isFinite(bounds.minZ) && Number.isFinite(bounds.maxZ) ? (bounds.minZ + bounds.maxZ) / 2 : location.z,
    maxRadius: 3,
  };
};

const getPlaceDistanceFromPoint = (place, point) => distancePointToBounds2d(point, getShapeBounds(place));

const getStartEntrySide = (explorer) => {
  const beingType = String(explorer?.beingType || explorer?.defaultForm || '').trim().toLowerCase();
  if (NORTH_ENTRY_BEING_TYPES.has(beingType)) {
    return 'north';
  }
  if (SOUTH_ENTRY_BEING_TYPES.has(beingType)) {
    return 'south';
  }

  return 'south';
};

const getMapRenderLayer = (place, color = null) => {
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);
  return color?.preserveStroke || shapeType === 'path' || isRoadOrRiverOverviewPlace(place) ? 2 : 0;
};

const getMapColor = (place) => {
  const descriptor = getOverviewDescriptorText(place);
  const fixedColor = FIXED_PLACE_MAP_COLORS.find((entry) => descriptor.includes(entry.match));

  if (fixedColor) {
    return fixedColor;
  }

  const key = String(place?.zoneType || place?.buildingType || place?.placeType || place?.idEntity || 'unknown').toLowerCase();
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }

  return PLACE_MAP_COLORS[hash % PLACE_MAP_COLORS.length];
};

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const getChildPlaceIds = (place) => new Set(toArray(place?.composedOfChildren).map(getEntityId).filter(Boolean));

const getMapInteriorAwareLocation = ({ explorerLocation, currentInteriorPlaceId, focusPlace }) => {
  const focusPlaceId = getEntityId(focusPlace);
  if (focusPlaceId && currentInteriorPlaceId && focusPlaceId === String(currentInteriorPlaceId)) {
    return getPlaceCenterLocation(focusPlace);
  }

  return explorerLocation?.location ? normalizeLocation(explorerLocation.location) : null;
};

export const buildPlaceMapPlan = ({
  focusPlace,
  places,
  explorerLocation,
  currentInteriorPlaceId = '',
  beings = [],
  beingAnchorPlace = null,
  knowledge = {},
  knownMapPlaceIds = [],
  searchCoverage = [],
  recentTraversal = null,
}) => {
  if (!focusPlace?.idEntity) {
    return null;
  }

  const childPlaceIds = getChildPlaceIds(focusPlace);
  const pcLocation = getMapInteriorAwareLocation({ explorerLocation, currentInteriorPlaceId, focusPlace });
  const regional = Boolean(pcLocation && /^(world|continent|nation|country|region)$/i.test(focusPlace.placeType || ''));
  const recognizedPlaces = new Set(knownMapPlaceIds);
  const focusBounds = getShapeBounds(focusPlace);
  const canMapPlace = place => knowledge[getEntityId(place)]?.locationKnown === true
    || recognizedPlaces.has(getEntityId(place)) || getEntityId(place) === explorerLocation?.placeId;
  const childPlaces = toArray(places).filter(place => childPlaceIds.has(getEntityId(place)) && canMapPlace(place));
  const infrastructurePlaces = toArray(places).filter((place) => {
    const placeId = getEntityId(place);
    if (!placeId || placeId === getEntityId(focusPlace) || childPlaceIds.has(placeId) || !isRoadOrRiverOverviewPlace(place)) {
      return false;
    }

    return canMapPlace(place) && doBoundsIntersect(getShapeBounds(place), focusBounds);
  });
  const entries = uniqueById([
    focusPlace,
    ...(regional ? toArray(places).filter(place => knowledge[getEntityId(place)]?.locationKnown === true || recognizedPlaces.has(getEntityId(place)))
      : [...childPlaces, ...infrastructurePlaces]),
  ].filter(Boolean));
  const scaleEntries = uniqueById([
    focusPlace,
    ...childPlaces,
  ].filter(Boolean));

  if (entries.length === 0) {
    return null;
  }

  const settlementTypes = /^(settlement|town|city|village|hamlet)$/i;
  const placesById = new Map(toArray(places).concat(focusPlace).map(entry => [getEntityId(entry), entry]));
  const settlementSubPlaceIds = new Set();
  const visitSettlementChildren = (entry) => {
    const children = uniqueById([
      ...toArray(entry?.composedOfChildren),
      ...toArray(places).filter(candidate => toArray(candidate?.composedOfParents)
        .some(parent => getEntityId(parent) === getEntityId(entry))),
    ]);
    children.forEach(child => {
      const id = getEntityId(child);
      if (!id || settlementSubPlaceIds.has(id)) return;
      settlementSubPlaceIds.add(id);
      visitSettlementChildren(placesById.get(id) || child);
    });
  };
  placesById.forEach(entry => {
    if (settlementTypes.test(entry?.placeType || '')) visitSettlementChildren(entry);
  });

  const focusPlaceBounds = getShapeBounds(focusPlace);
  const anchorPlace = beingAnchorPlace?.idEntity ? beingAnchorPlace : focusPlace;
  const anchorPlaceCenter = getPlaceCenterLocation(anchorPlace);
  const anchorPlaceBounds = getShapeBounds(anchorPlace);
  const beingLocations = toArray(beings)
    .map((being, index) => {
      const rawLocation = normalizeLocation(being?.location);
      const hasUsableLocation = Boolean(being?.routineState) || ['visible', 'remembered'].includes(being.visibility)
        || pointWithinHorizontalBounds(rawLocation, focusPlaceBounds, NEARBY_PLACE_RADIUS_FEET);
      const angle = index * 2.399963229728653;
      const radius = 11 + (index % 4) * 5;
      const location = hasUsableLocation
        ? rawLocation
        : {
          ...anchorPlaceCenter,
          x: clampNumber(anchorPlaceCenter.x + Math.cos(angle) * radius, anchorPlaceBounds.minX, anchorPlaceBounds.maxX),
          z: clampNumber(anchorPlaceCenter.z + Math.sin(angle) * radius, anchorPlaceBounds.minZ, anchorPlaceBounds.maxZ),
          maxRadius: 3,
        };

      return {
        being,
        location,
      };
    });
  const calculatedBounds = scaleEntries.reduce(
    (bounds, entry) => {
      const entryBounds = getShapeBounds(entry);
      return {
        minX: Math.min(bounds.minX, entryBounds.minX),
        maxX: Math.max(bounds.maxX, entryBounds.maxX),
        minZ: Math.min(bounds.minZ, entryBounds.minZ),
        maxZ: Math.max(bounds.maxZ, entryBounds.maxZ),
      };
    },
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity }
  );

  const boundsWithPc = pcLocation
    ? {
      minX: Math.min(calculatedBounds.minX, pcLocation.x),
      maxX: Math.max(calculatedBounds.maxX, pcLocation.x),
      minZ: Math.min(calculatedBounds.minZ, pcLocation.z),
      maxZ: Math.max(calculatedBounds.maxZ, pcLocation.z),
    }
    : calculatedBounds;
  const boundsWithBeings = regional ? {
    minX: pcLocation.x - REGIONAL_MAP_RADIUS_FEET, maxX: pcLocation.x + REGIONAL_MAP_RADIUS_FEET,
    minZ: pcLocation.z - REGIONAL_MAP_RADIUS_FEET, maxZ: pcLocation.z + REGIONAL_MAP_RADIUS_FEET,
  } : beingLocations.reduce(
    (bounds, { location }) => ({
      minX: Math.min(bounds.minX, location.x),
      maxX: Math.max(bounds.maxX, location.x),
      minZ: Math.min(bounds.minZ, location.z),
      maxZ: Math.max(bounds.maxZ, location.z),
    }),
    boundsWithPc
  );

  if (![boundsWithBeings.minX, boundsWithBeings.maxX, boundsWithBeings.minZ, boundsWithBeings.maxZ].every(Number.isFinite)) {
    return null;
  }

  const width = Math.max(1, boundsWithBeings.maxX - boundsWithBeings.minX);
  const height = Math.max(1, boundsWithBeings.maxZ - boundsWithBeings.minZ);
  const padding = regional ? 0 : 42;
  const drawableSize = PLACE_OVERVIEW_SIZE - padding * 2;
  const scale = Math.min(drawableSize / width, drawableSize / height);
  const offsetX = padding + (drawableSize - width * scale) / 2;
  const offsetY = padding + (drawableSize - height * scale) / 2;
  const toCanvasX = (x) => offsetX + (x - boundsWithBeings.minX) * scale;
  const toCanvasY = (z) => offsetY + (z - boundsWithBeings.minZ) * scale;
  const pcPin = pcLocation
    ? {
      x: toCanvasX(pcLocation.x),
      y: toCanvasY(pcLocation.z),
    }
    : null;

  return {
    width: PLACE_OVERVIEW_SIZE,
    height: PLACE_OVERVIEW_SIZE,
    playerCentered: regional,
    radiusFeet: regional ? REGIONAL_MAP_RADIUS_FEET : null,
    pcPin,
    beingPins: beingLocations.map(({ being, location }) => ({
      id: getEntityId(being),
      label: getBeingLabel(being),
      initials: getInitials(getBeingLabel(being)),
      x: toCanvasX(location.x),
      y: toCanvasY(location.z),
      visibility: being.visibility,
      lastSeenMinute: being.lastSeenMinute,
    })).filter((pin) => pin.id),
    traversedRoutes: toArray(recentTraversal?.paths).filter(path => path.length > 1).map(path => ({
      points: path.map(p => `${toCanvasX(p.x)},${toCanvasY(p.z)}`).join(' '),
      arrows: path.slice(1).map((b, i) => ({ x: (toCanvasX(path[i].x) + toCanvasX(b.x)) / 2,
        y: (toCanvasY(path[i].z) + toCanvasY(b.z)) / 2,
        angle: Math.atan2(b.z - path[i].z, b.x - path[i].x) * 180 / Math.PI,
        length: Math.hypot(b.x - path[i].x, b.z - path[i].z) * scale })).filter(a => a.length > 20),
    })),
    searchCoverage: toArray(searchCoverage).filter(entry => entry.areaId === getEntityId(focusPlace)).map(entry => ({ ...entry,
      points: toArray(entry.points).map(p => `${toCanvasX(p.x)},${toCanvasY(p.z)}`).join(' '),
      label: `${entry.status === 'examined' ? 'Searched' : entry.status === 'partial' ? 'Partially searched' : 'Planned route'}: ${entry.objectiveText}`,
    })),
    nodes: entries
      .map((entry) => {
        const bounds = getShapeBounds(entry);
        const color = getMapColor(entry);
        const shapePoints = getOverviewShapePoints(entry);
        const shapeType = normalizeShapeTypeName(entry?.shape?.shapeType ?? 'box');
        const isFocus = getEntityId(entry) === getEntityId(focusPlace);
        const isPath = shapeType === 'path';

        return {
          id: getEntityId(entry),
          placeId: getEntityId(entry),
          label: getPlaceLabel(entry),
          x: toCanvasX(bounds.minX),
          y: toCanvasY(bounds.minZ),
          width: Math.max(0, (bounds.maxX - bounds.minX) * scale),
          height: Math.max(0, (bounds.maxZ - bounds.minZ) * scale),
          centerX: toCanvasX((bounds.minX + bounds.maxX) / 2),
          centerY: toCanvasY((bounds.minZ + bounds.maxZ) / 2),
          radius: Math.max(0, Math.min(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) * scale / 2),
          shapeType,
          pathData: getMultipartPathData(entry, toCanvasX, toCanvasY),
          points: shapePoints.map((point) => `${toCanvasX(point.x)},${toCanvasY(point.z)}`).join(' '),
          fill: isFocus && !isPath ? 'rgba(74, 165, 254, 0.14)' : color.fill,
          stroke: isFocus ? '#8ec5ff' : color.stroke,
          strokeWidth: isFocus ? 2.6 : isPath ? 3 : 1.4,
          // The enclosing area's fill must stay behind its sites, including
          // when its title gives it an infrastructure color/layer.
          renderLayer: isFocus && !isPath ? -1 : getMapRenderLayer(entry, color),
          isFocus,
          showLabel: (isFocus || regional) && !settlementSubPlaceIds.has(getEntityId(entry)),
        };
      })
      .sort((left, right) => {
        if (left.renderLayer !== right.renderLayer) {
          return left.renderLayer - right.renderLayer;
        }

        return right.width * right.height - left.width * left.height;
      }),
  };
};

export const buildMapViewBox = (plan, zoom) => {
  const width = plan?.width ?? PLACE_OVERVIEW_SIZE;
  const height = plan?.height ?? PLACE_OVERVIEW_SIZE;
  const normalizedZoom = clampNumber(Number(zoom) || 1, plan?.playerCentered ? REGIONAL_MAP_MIN_ZOOM : PLACE_OVERVIEW_MIN_ZOOM, PLACE_OVERVIEW_MAX_ZOOM);
  const viewWidth = width / normalizedZoom;
  const viewHeight = height / normalizedZoom;
  const focusX = plan?.pcPin?.x ?? width / 2;
  const focusY = plan?.pcPin?.y ?? height / 2;

  return {
    x: plan?.playerCentered ? focusX - viewWidth / 2 : clampNumber(focusX - viewWidth / 2, 0, Math.max(0, width - viewWidth)),
    y: plan?.playerCentered ? focusY - viewHeight / 2 : clampNumber(focusY - viewHeight / 2, 0, Math.max(0, height - viewHeight)),
    width: viewWidth,
    height: viewHeight,
  };
};

const resolveExplorerStartLocation = (explorer, currentPlace, infrastructurePlaces = []) =>
  buildSettlementStartLocation({
    place: currentPlace,
    polygon: getOverviewShapePoints(currentPlace),
    bounds: getShapeBounds(currentPlace),
    roads: toArray(infrastructurePlaces).filter(isRoadEntryOverviewPlace).map(road => ({
      idEntity: getEntityId(road), name: getPlaceLabel(road), points: getOverviewShapePoints(road),
    })),
    side: getStartEntrySide(explorer),
  });

const buildWeatherContext = (gameplayContext) => ({
  weatherState: gameplayContext?.weatherState ?? null,
  currentWeather: gameplayContext?.currentWeather ?? null,
  weatherForecast: toArray(gameplayContext?.weatherForecast),
});

const buildRecentNarration = (messages) =>
  toArray(messages)
    .filter((message) => message?.role === 'assistant' && message?.content)
    .slice(-3)
    .map((message) => message.content);

const summarizePlaceForScene = (place) => ({
  idEntity: place?.idEntity ?? '',
  name: place?.name ?? place?.entityName ?? '',
  title: place?.title ?? place?.entityTitle ?? '',
  description: place?.description ?? place?.entityDesc ?? '',
  level: place?.level ?? '',
  placeType: place?.placeType ?? '',
  zoneType: place?.zoneType ?? '',
  buildingType: place?.buildingType ?? '',
  location: place?.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
  shape: place?.shape ?? {},
  shapes: place?.shapes,
  parentIds: enclosure.parentIds(place),
  childPlaceIds: [],
  containedItems: [],
  currencies: [],
  inhabitantCount: 0,
  itemCount: 0,
});

const buildMinimalSettlementScene = (placeChain, scenePlaces = []) => {
  const settlement = placeChain[SETTLEMENT_INDEX];
  if (!settlement?.idEntity) {
    return null;
  }
  const places = uniqueById([
    ...toArray(placeChain),
    ...toArray(scenePlaces),
  ]).map(summarizePlaceForScene);
  const settlementChildren = places.filter((place) =>
    toArray(settlement?.composedOfChildren).some((child) => getEntityId(child) === getEntityId(place))
  );

  return {
    rootPlaceId: settlement.idEntity,
    settlement: summarizePlaceForScene(settlement),
    counts: {
      places: places.length,
      inhabitants: 0,
      items: 0,
    },
    hierarchy: {
      ...summarizePlaceForScene(settlement),
      children: settlementChildren,
    },
    places,
  };
};

const loadPlaceDetailsInProximityOrder = async ({ places, point, onPlaceStart, onPlaceLoaded, shouldContinue }) => {
  const orderedPlaces = uniqueById(places)
    .filter((place) => getEntityId(place))
    .map((place) => ({
      place,
      distance: point ? getPlaceDistanceFromPoint(place, point) : Infinity,
    }))
    .sort((left, right) => (left.distance ?? Infinity) - (right.distance ?? Infinity))
    .map((entry) => entry.place);

  for (const place of orderedPlaces) {
    if (typeof shouldContinue === 'function' && !shouldContinue()) {
      return;
    }

    try {
      const shouldLoad = onPlaceStart?.(place);
      if (shouldLoad === false) {
        continue;
      }

      const loadedPlace = await callCdo(cdoPlace.select.bind(cdoPlace), getEntityId(place));
      if (loadedPlace && (typeof shouldContinue !== 'function' || shouldContinue())) {
        onPlaceLoaded?.(loadedPlace);
      }
    } catch {
      // Background place hydration should not block play.
    }
  }
};

const loadBeingDetailsByReference = async (beingRefs) => {
  const refs = uniqueById(beingRefs);
  if (refs.length === 0) {
    return [];
  }

  return Promise.all(refs.map((being) => callCdo(cdoBeing.select.bind(cdoBeing), being.idEntity)));
};

const loadCurrentPlaceContext = async (currentPlace, npcRoutineState = null) => {
  const trackedNpcIds = new Set(Object.keys(activeRoutineScene(npcRoutineState)?.beingsById || {}));
  const [beingsInLocation, services] = await Promise.all([
    loadBeingDetailsByReference([
      ...toArray(currentPlace?.savedInhabitants),
      ...toArray(currentPlace?.containedVisitors),
    ].filter(being => !trackedNpcIds.has(getEntityId(being)))),
    callCdo(cdoService.selectAll.bind(cdoService)),
  ]);
  const subPlaces = uniqueById(currentPlace?.composedOfChildren);
  const childPlaceById = new Map(subPlaces.map((place) => [getEntityId(place), place]).filter(([placeId]) => placeId));
  const scopedBeingsInLocation = uniqueById(beingsInLocation.filter(Boolean).flatMap((being) => {
    const affiliatedChildPlace = getAffiliatedPlaceIds(being)
      .map((placeId) => childPlaceById.get(placeId))
      .find(Boolean);

    if (affiliatedChildPlace && isEnclosedOccupantPlace(affiliatedChildPlace)) {
      return [];
    }

    return [withPlaceAnchoredBeingLocation(being, affiliatedChildPlace || currentPlace)];
  }));

  return {
    place: currentPlace,
    subPlaces,
    beingsInLocation: scopedBeingsInLocation,
    services: toArray(services).map(summarizeServiceForNarration),
  };
};

const findKnownPlaceById = (places, placeId) => {
  const normalizedPlaceId = String(placeId ?? '').trim();
  if (!normalizedPlaceId) {
    return null;
  }

  return toArray(places).find((place) => getEntityId(place) === normalizedPlaceId) || null;
};

const buildPlaceAncestorChain = (place, knownPlaces = [], visited = new Set()) =>
  uniqueById(toArray(place?.composedOfParents).flatMap((parentRef) => {
    const parentId = getEntityId(parentRef);
    if (!parentId || visited.has(parentId)) {
      return [];
    }

    visited.add(parentId);
    const parentPlace = findKnownPlaceById(knownPlaces, parentId) || parentRef;
    return [
      ...buildPlaceAncestorChain(parentPlace, knownPlaces, visited),
      parentPlace,
    ];
  }));

const buildKnownScenePlaces = (placeChain, currentPlaceContext) => {
  const contextPlaces = [
    currentPlaceContext?.place,
    ...toArray(currentPlaceContext?.subPlaces),
  ].filter(Boolean);
  const knownPlaces = uniqueById([
    ...toArray(placeChain),
    ...contextPlaces,
  ]);

  return uniqueById([
    ...toArray(placeChain),
    ...buildPlaceAncestorChain(currentPlaceContext?.place, knownPlaces),
    currentPlaceContext?.place,
    ...toArray(currentPlaceContext?.subPlaces),
  ].filter(Boolean));
};

const mergeHydratedPlaceDetails = (places, detailsById) =>
  toArray(places).map((place) => detailsById[getEntityId(place)] || place);

const withPlaceAnchoredBeingLocation = (being, place) => {
  const placeBounds = getShapeBounds(place);
  const beingLocation = normalizeLocation(being?.location);
  const hasUsableLocation = pointWithinHorizontalBounds(beingLocation, placeBounds, 20);
  return {
    ...being,
    location: hasUsableLocation ? beingLocation : getPlaceCenterLocation(place),
    containingPlaceId: getEntityId(place),
    containingPlaceName: getPlaceLabel(place),
  };
};

const getPlaceOccupants = (place) =>
  uniqueById([
    ...toArray(place?.savedInhabitants),
    ...toArray(place?.containedVisitors),
  ].filter(Boolean)).map((being) => withPlaceAnchoredBeingLocation(being, place));

const isEnclosedOccupantPlace = enclosure.isEnclosedPlace;

const getNearbyHydratedSubPlaceOccupants = ({ currentPlaceContext, currentLocation, currentInteriorPlaceId = '' }) => {
  const point = currentLocation?.location ? normalizeLocation(currentLocation.location) : null;

  return toArray(currentPlaceContext?.subPlaces)
    .filter((place) =>
      toArray(place?.savedInhabitants).length > 0 ||
      toArray(place?.containedVisitors).length > 0
    )
    .flatMap((place) => {
      const placeId = getEntityId(place);
      const isSameInterior = currentInteriorPlaceId && currentInteriorPlaceId === placeId;
      const isEnclosed = isEnclosedOccupantPlace(place);
      const placeDistance = point ? getPlaceDistanceFromPoint(place, point) : Infinity;
      if (!isSameInterior && (currentInteriorPlaceId || isEnclosed || placeDistance > NEARBY_PLACE_RADIUS_FEET)) {
        return [];
      }

      return getPlaceOccupants(place).map((being) => ({
        ...being,
        proximityFeet: isSameInterior ? 0 : Math.round(placeDistance),
        proximityType: isSameInterior ? 'same_interior' : 'nearby_child_place',
      }));
    });
};

export const getVisibleBeingsForCurrentLocation = ({ currentLocation, currentPlaceContext, currentInteriorPlaceId = '' }) => {
  const point = currentLocation?.location ? normalizeLocation(currentLocation.location) : null;
  const currentPlaceId = getEntityId(currentPlaceContext?.place);
  const isInsideCurrentPlace = currentInteriorPlaceId && currentInteriorPlaceId === currentPlaceId;
  const isCurrentPlaceEnclosed = currentPlaceId && isEnclosedOccupantPlace(currentPlaceContext?.place);
  const childPlaceById = new Map(toArray(currentPlaceContext?.subPlaces)
    .map((place) => [getEntityId(place), place])
    .filter(([placeId]) => placeId));

  const enclosedIds = new Set([
    ...toArray(currentPlaceContext?.enclosedPlaceIds),
    ...enclosure.enclosedPlaceIds([currentPlaceContext?.place, ...toArray(currentPlaceContext?.subPlaces)].filter(Boolean)),
  ]);
  return uniqueById([
    ...toArray(currentPlaceContext?.beingsInLocation),
    ...getNearbyHydratedSubPlaceOccupants({
      currentPlaceContext,
      currentLocation,
      currentInteriorPlaceId,
    }),
  ])
    .map((being) => {
      const beingContainingPlaceId = String(being?.containingPlaceId || currentPlaceId || '').trim();
      if ((currentInteriorPlaceId || enclosedIds.has(beingContainingPlaceId)) && beingContainingPlaceId !== currentInteriorPlaceId) return null;
      if (!isInsideCurrentPlace && isCurrentPlaceEnclosed && beingContainingPlaceId === currentPlaceId) {
        return null;
      }

      const enclosedAffiliatedChildPlace = getAffiliatedPlaceIds(being)
        .map((placeId) => childPlaceById.get(placeId))
        .find((place) => place && isEnclosedOccupantPlace(place));
      if (
        !isInsideCurrentPlace &&
        !being.routineState && enclosedAffiliatedChildPlace &&
        getEntityId(enclosedAffiliatedChildPlace) !== currentInteriorPlaceId
      ) {
        return null;
      }

      const location = normalizeLocation(being?.location);
      const distance = point ? distance2d(location, point) : Infinity;
      const suppliedProximityType = String(being?.proximityType ?? '');
      const suppliedProximityFeet = Number(being?.proximityFeet);
      const proximityFeet = isInsideCurrentPlace
        ? 0
        : Number.isFinite(suppliedProximityFeet)
          ? suppliedProximityFeet
          : Number.isFinite(distance)
            ? Math.round(distance)
            : null;
      const proximityType = isInsideCurrentPlace
        ? 'same_interior'
        : suppliedProximityType
          ? suppliedProximityType
          : distance <= NEARBY_BEING_RADIUS_FEET
            ? 'nearby'
            : '';

      return proximityType
        ? {
          ...being,
          proximityFeet,
          proximityType,
        }
        : null;
    })
    .filter(Boolean)
    .sort((left, right) => (left.proximityFeet ?? Infinity) - (right.proximityFeet ?? Infinity))
    .slice(0, 10);
};

const buildEffectiveCurrentPlaceContext = (currentPlaceContext, hydratedPlaceDetailsById) => {
  if (!currentPlaceContext) {
    return null;
  }

  return {
    ...currentPlaceContext,
    subPlaces: mergeHydratedPlaceDetails(currentPlaceContext.subPlaces, hydratedPlaceDetailsById),
  };
};

const buildMovementFrame = ({
  explorerLocation,
  movementState,
  currentInteriorPlaceId = movementState?.placeId || '',
  currentPlaceContext,
  placeChain,
  knownScenePlaces,
}) => ({
  currentLocation: explorerLocation,
  currentInteriorPlaceId,
  places: uniqueById([
    ...toArray(knownScenePlaces),
    ...buildKnownScenePlaces(placeChain, currentPlaceContext),
  ]).map(summarizePlaceForNarration),
  beings: getVisibleBeingsForCurrentLocation({
    currentPlaceContext,
    currentLocation: explorerLocation,
    currentInteriorPlaceId,
  }).map(summarizeBeingForNarration),
});

const mergeCombatantIntoBeing = (being, combatant) => {
  if (!being || !combatant) {
    return being || combatant || null;
  }

  return {
    ...being,
    currentHp: combatant.currentHp ?? being.currentHp,
    maxHp: combatant.maxHp ?? being.maxHp,
    currentPp: combatant.currentPp ?? being.currentPp,
    maxPp: combatant.maxPp ?? being.maxPp,
    bleeding: combatant.bleeding ?? being.bleeding,
    poisoned: combatant.poisoned ?? being.poisoned,
    stunned: combatant.stunned ?? being.stunned,
    disabled: combatant.disabled ?? being.disabled,
    other: combatant.other ?? being.other,
    otherDesc: combatant.otherDesc ?? being.otherDesc,
    parryRounds: combatant.parryRounds ?? being.parryRounds,
    combatStatus: combatant.combatStatus ?? being.combatStatus,
    currentCombatItem: combatant.currentCombatItem ?? being.currentCombatItem,
  };
};

const getCombatStateCombatant = (combatState, idEntity) =>
  idEntity ? combatState?.combatantsById?.[idEntity] ?? null : null;

const getEffectiveCombatExplorer = (playerCharacter, combatState) =>
  mergeCombatantIntoBeing(playerCharacter, getCombatStateCombatant(combatState, getEntityId(playerCharacter)));

const mergeCombatStateIntoBeings = (beings, combatState) =>
  toArray(beings).map((being) => mergeCombatantIntoBeing(being, getCombatStateCombatant(combatState, getEntityId(being))));

const buildCombatFrame = ({
  playerCharacter,
  combatState,
  nearbyBeings = [],
  explorerLocation,
  movementState,
  currentInteriorPlaceId = movementState?.placeId || '',
  currentPlaceContext,
  placeChain,
  knownScenePlaces,
}) => ({
  active: combatState?.active === true,
  explorer: summarizeBeingForNarration(getEffectiveCombatExplorer(playerCharacter, combatState)),
  visibleCombatants: mergeCombatStateIntoBeings(nearbyBeings, combatState).map(summarizeBeingForNarration),
  currentLocation: explorerLocation,
  currentInteriorPlaceId,
  combatState,
  places: uniqueById([
    ...toArray(knownScenePlaces),
    ...buildKnownScenePlaces(placeChain, currentPlaceContext),
  ]).map(summarizePlaceForNarration),
});

const buildCombatExplorerLocation = ({ combatResult, fallbackLocation, movementState }) => {
  const combatLocation = combatResult?.currentLocation;
  if (!combatLocation) {
    return fallbackLocation;
  }

  return {
    ...(fallbackLocation || {}),
    source: 'play2_combat',
    label: combatResult.active === true ? 'In combat' : combatResult.sceneActive ? 'Fighting nearby' : 'After combat',
    placeId: movementState?.placeId || fallbackLocation?.placeId || '',
    placeName: fallbackLocation?.placeName || '',
    location: normalizeLocation(combatLocation.location ?? combatLocation),
  };
};

const getCombatSpeechMessages = (combatResult, nameById, playerCharacterId = '', hiddenNameReplacements = []) => {
  const messages = [];
  const pcId = String(playerCharacterId ?? '').trim();

  toArray(combatResult?.combatRound?.participantResults).forEach((participant, participantIndex) => {
    if (pcId && String(participant?.combatantId ?? '').trim() === pcId) {
      return;
    }

    toArray(participant?.results).forEach((entry, entryIndex) => {
      const outcome = entry?.outcome;
      if (!outcome?.spoken || !String(outcome.text ?? '').trim()) {
        return;
      }

      messages.push({
        role: 'npc',
        speaker: nameById.get(outcome.speakerId) ||
          sanitizeNarrationTextForHiddenBeingNames(outcome.speakerName, hiddenNameReplacements) ||
          'NPC',
        content: outcome.text,
        key: `combat:${participant?.combatantId || participantIndex}:${entryIndex}`,
      });
    });
  });

  return messages;
};

const buildNearbyPlacesForNarration = ({ currentLocation, currentPlaceContext, scenePlaces = [], movementResult = null }) => {
  const point = currentLocation?.location ? normalizeLocation(currentLocation.location) : null;
  const passedPlaceById = new Map(toArray(movementResult?.passedPlaces)
    .map((place) => [getEntityId(place), place])
    .filter(([placeId]) => Boolean(placeId)));
  const observedPlaceById = new Map(toArray(movementResult?.observedPlaces)
    .map((place) => [getEntityId(place), place])
    .filter(([placeId]) => Boolean(placeId)));
  const places = uniqueById([
    currentPlaceContext?.place,
    ...toArray(currentPlaceContext?.subPlaces),
    ...toArray(scenePlaces),
    ...toArray(movementResult?.passedPlaces),
    ...toArray(movementResult?.observedPlaces),
  ].filter(Boolean));

  return places
    .map((place) => {
      const placeId = getEntityId(place);
      const passedPlace = passedPlaceById.get(placeId);
      const observedPlace = observedPlaceById.get(placeId);
      const movementPlace = passedPlace || observedPlace;
      const distance = point ? getPlaceDistanceFromPoint(place, point) : Infinity;
      const routeProximityFeet = Number(passedPlace?.routeProximityFeet);
      const observationDistanceFeet = Number(observedPlace?.observationDistanceFeet);
      return {
        ...summarizePlaceForNarration({
          ...place,
          ...movementPlace,
        }),
        proximityFeet: passedPlace
          ? Number.isFinite(routeProximityFeet)
            ? Math.round(routeProximityFeet)
            : 0
          : observedPlace
            ? Number.isFinite(observationDistanceFeet)
              ? Math.round(observationDistanceFeet)
              : null
          : Number.isFinite(distance)
            ? Math.round(distance)
            : null,
        proximityType: passedPlace
          ? 'passed_this_turn'
          : observedPlace
            ? 'visible_from_route'
          : distance <= NEARBY_PLACE_RADIUS_FEET
            ? 'nearby'
            : '',
        routeProximityFeet: passedPlace?.routeProximityFeet ?? null,
        routeProximityType: passedPlace?.proximityType ?? '',
        observationDistanceFeet: observedPlace?.observationDistanceFeet ?? null,
        observedFromPoint: observedPlace?.observedFromPoint ?? null,
        lookAroundFeet: observedPlace?.lookAroundFeet ?? null,
        perceptionTotal: observedPlace?.perceptionTotal ?? null,
      };
    })
    .filter((place) => place.proximityType)
    .sort((left, right) => {
      if (left.proximityType !== right.proximityType) {
        const priority = { passed_this_turn: 0, visible_from_route: 1, nearby: 2 };
        return (priority[left.proximityType] ?? 99) - (priority[right.proximityType] ?? 99);
      }

      return (left.proximityFeet ?? Infinity) - (right.proximityFeet ?? Infinity);
    })
    .slice(0, 8);
};

const buildNearbyBeingsForNarration = ({ currentLocation, currentPlaceContext, currentInteriorPlaceId = '' }) =>
  getVisibleBeingsForCurrentLocation({
    currentLocation,
    currentPlaceContext,
    currentInteriorPlaceId,
  }).map((being) => ({
    ...summarizeBeingForNarration(being),
    containingPlaceId: being?.containingPlaceId ?? '',
    containingPlaceName: being?.containingPlaceName ?? '',
    proximityFeet: being?.proximityFeet ?? null,
    proximityType: being?.proximityType ?? '',
  }));

const getSettlementRootPlaceId = (settlementScene) =>
  String(settlementScene?.rootPlaceId || settlementScene?.settlement?.idEntity || '').trim();

export const getMovementInteriorPlaceId = ({ movementState, settlementScene }) => {
  const placeId = String(movementState?.placeId ?? '').trim();
  return enclosure.enclosedPlaceIds(toArray(settlementScene?.places)).has(placeId) ? placeId : '';
};

const getPlaceContextInteriorPlaceId = ({ currentPlaceContext, settlementScene }) => {
  const placeId = getEntityId(currentPlaceContext?.place);
  const enclosed = enclosure.enclosedPlaceIds(uniqueById([
    ...toArray(settlementScene?.places), currentPlaceContext?.place,
    ...toArray(currentPlaceContext?.subPlaces),
  ].filter(Boolean)));
  return enclosed.has(placeId) ? placeId : '';
};

const buildRollData = ({ npcResponseGroups, movementResult, combatResult, spellAcquisitionResult }) => {
  const npcRollData = extractRollData(npcResponseGroups);
  if (!movementResult && !combatResult && !spellAcquisitionResult) {
    return npcRollData;
  }

  const rollData = {
    npcResponses: npcRollData ?? [],
  };

  if (movementResult) {
    rollData.movement = {
      movementTarget: movementResult.movementTarget ?? null,
      movementRoll: movementResult.movementRoll ?? null,
      perceptionRoll: movementResult.perceptionRoll ?? null,
      movedDistance: movementResult.movedDistance ?? 0,
      actionComplete: Boolean(movementResult.actionComplete),
      travelledPath: toArray(movementResult.travelledPath),
      passedPlaces: toArray(movementResult.passedPlaces),
      observedPlaces: toArray(movementResult.observedPlaces),
    };
  }

  if (combatResult) {
    rollData.combat = {
      active: combatResult.active === true,
      playerPlan: combatResult.playerPlan ?? null,
      npcPlans: toArray(combatResult.npcPlans),
      initiativeOrder: toArray(combatResult.initiativeOrder),
      participantResults: toArray(combatResult.participantResults),
      combatRound: combatResult.combatRound ?? null,
      endCondition: combatResult.endCondition ?? null,
      awarenessResults: toArray(combatResult.awarenessResults),
      participationDecisions: toArray(combatResult.participationDecisions),
      witnessReactions: toArray(combatResult.witnessReactions),
      initiativePenaltyById: combatResult.initiativePenaltyById ?? {},
    };
  }

  if (spellAcquisitionResult) {
    rollData.spellAcquisition = {
      status: spellAcquisitionResult.status ?? '',
      attempted: Boolean(spellAcquisitionResult.attempted),
      success: Boolean(spellAcquisitionResult.success),
      actionComplete: Boolean(spellAcquisitionResult.actionComplete),
      characterMutated: Boolean(spellAcquisitionResult.characterMutated),
      source: spellAcquisitionResult.source ?? null,
      learnedSpell: spellAcquisitionResult.learnedSpell ?? null,
      roll: spellAcquisitionResult.roll ?? null,
      studyRequirement: spellAcquisitionResult.studyRequirement ?? null,
      message: spellAcquisitionResult.message ?? '',
    };
  }

  return rollData;
};

const buildMovementResultForNarration = (movementResult) => {
  if (!movementResult?.fastTravel) {
    return movementResult;
  }

  const {
    fastTravel,
    movementRoll,
    ...rest
  } = movementResult;

  return {
    ...rest,
    currentLocation: rest.currentLocation
      ? {
        ...rest.currentLocation,
        source: 'play2_route_arrival',
      }
      : rest.currentLocation,
    movementRoll: movementRoll
      ? {
        ...movementRoll,
        fastTravel: undefined,
        quality: 'uneventful route summary',
      }
      : movementRoll,
    routeResolution: {
      type: 'uneventful_route_summary',
      encountersResolved: false,
      narrationGuidance: 'The route resolved without an encounter. Describe ordinary travel along the route briefly, or begin at the arrival point.',
    },
  };
};

const buildCurrentLocationForNarration = (currentLocation) => {
  if (currentLocation?.source !== 'play2_fast_travel') {
    return currentLocation;
  }

  return {
    ...currentLocation,
    source: 'play2_route_arrival',
  };
};

const buildRollDataForNarration = (rollData) => {
  if (!rollData?.movement?.movementRoll?.fastTravel) {
    return rollData;
  }

  return {
    ...rollData,
    movement: {
      ...rollData.movement,
      movementRoll: {
        ...rollData.movement.movementRoll,
        fastTravel: undefined,
        quality: 'uneventful route summary',
      },
    },
  };
};

const buildPlayerActionForNarration = (playerAction) => {
  if (!playerAction?.fastTravel) {
    return playerAction;
  }

  return {
    ...playerAction,
    fastTravel: undefined,
    routeResolution: 'uneventful_route_summary',
  };
};

const formatGameTime = (totalMinutes) => {
  const parsed = Number(totalMinutes);
  if (!Number.isFinite(parsed)) {
    return '';
  }

  const minutes = ((Math.floor(parsed) % 1440) + 1440) % 1440;
  if (minutes < 60) return 'Midnight';
  if (minutes < 180) return 'Deep night';
  if (minutes < 300) return 'Late night';
  if (minutes < 420) return 'Before dawn';
  if (minutes < 540) return 'Early morning';
  if (minutes < 660) return 'Midmorning';
  if (minutes < 720) return 'Late morning';
  if (minutes < 780) return 'Noon';
  if (minutes < 900) return 'Early afternoon';
  if (minutes < 1020) return 'Late afternoon';
  if (minutes < 1140) return 'Early evening';
  if (minutes < 1260) return 'Late evening';
  return 'Night';
};

const buildPlayerActionTimeFrame = (gameplayContext) => {
  const currentTimeMinutes = Number(gameplayContext?.currentTimeMinutes ?? DEFAULT_START_TIME_MINUTES);
  const currentTimeLabel = formatGameTime(currentTimeMinutes);

  return {
    currentTimeMinutes,
    currentTimeLabel,
    timeOfDay: currentTimeLabel,
    publicTimeLabel: currentTimeLabel,
    guidance: 'Use this only to resolve wait, rest, or sleep durations and endpoints for the first Play2 action call.',
  };
};

const buildNarrationInstruction = ({ isInitialScene, currentPlaceLabel, currentLocation }) => {
  if (isInitialScene) {
    return [
      `This is the first narration for Play2.`,
      `History and recentNarration may be blank because play has just opened.`,
      `Introduce ${currentPlaceLabel || 'Harolton'} from the explorer's starting position.`,
      ['play2_settlement_road_entry', 'play2_myrhaven_road_entry'].includes(currentLocation?.source)
        ? `The explorer starts on the ${currentLocation.entrySide || 'entry'} road just outside ${currentPlaceLabel || currentLocation.placeName}, with the town ahead.`
        : '',
      ['play2_settlement_outskirts', 'play2_myrhaven_outskirts'].includes(currentLocation?.source)
        ? `The explorer starts just outside ${currentPlaceLabel || currentLocation.placeName} for this opening scene.`
        : '',
    ].filter(Boolean).join(' ');
  }

  return 'Narrate the current turn from the supplied instruction, first call output, roll data, second call output, and visible context. Include every vitalCondition.requiredNarration warning unless it is already visible in displayedSystemMessages. Routine, activity and hazard events are not shown as System messages. Weave their noticed actions into one natural passage; do not repeat an interruption reason as a second event. Stamina and health changes are authoritative; do not invent damage or recovery. An interrupted activity has not been completed. Penalties are already included in resolved rolls; do not apply them again. Consumption happens only when consumptionResult.success is true; use only its listed effects. Buying does not eat or drink the purchase.';
};

const getDisplayedNpcSpeechForNarration = ({ npcResponseGroups = [], combatResult = null }) => {
  const speech = [];

  toArray(npcResponseGroups).forEach((group) => {
    toArray(group?.result?.responses).forEach((response) => {
      const text = String(response?.verbalResponse ?? '').trim();
      if (text) {
        speech.push({
          speakerId: response?.idEntity || '',
          text,
        });
      }
    });
  });

  toArray(combatResult?.combatRound?.participantResults).forEach((participant) => {
    toArray(participant?.results).forEach((entry) => {
      const outcome = entry?.outcome;
      const text = String(outcome?.text ?? '').trim();
      if (outcome?.spoken && text) {
        speech.push({
          speakerId: outcome.speakerId || participant?.combatantId || '',
          text,
        });
      }
    });
  });

  return speech;
};

const buildNpcResponsesForNarration = (npcResponseGroups = []) =>
  toArray(npcResponseGroups).map((group) => ({
    ...group,
    result: group?.result
      ? {
        ...group.result,
        responses: toArray(group.result.responses).map((response) => {
          const verbalResponse = String(response?.verbalResponse ?? '').trim();
          return verbalResponse
            ? {
              ...response,
              verbalResponse: null,
            }
            : response;
        }),
      }
      : group?.result,
  }));

const buildPlayNarrationPayload = ({
  explorer,
  currentPlaceContext,
  scenePlaces = [],
  gameplayContext,
  currentLocation,
  currentInteriorPlaceId = '',
  history = [],
  recentNarration = [],
  instruction = '',
  playerAction = null,
  rollData = null,
  movementResult = null,
  combatResult = null,
  spellAcquisitionResult = null,
  tradeResults = [],
  contractResults = [],
  npcResponses = [],
  displayedSystemMessages = [],
  reason = 'completion',
}) => {
  const currentPlaceLabel = getPlaceLabel(currentPlaceContext?.place);
  const isInitialScene = reason === 'initial_scene';
  const currentTimeLabel = formatGameTime(gameplayContext?.currentTimeMinutes ?? DEFAULT_START_TIME_MINUTES);
  const nearbyPlaces = buildNearbyPlacesForNarration({
    currentLocation,
    currentPlaceContext,
    scenePlaces,
    movementResult,
  });
  const nearbyBeings = buildNearbyBeingsForNarration({
    currentLocation,
    currentPlaceContext,
    currentInteriorPlaceId,
  });
  const movementResultForNarration = buildMovementResultForNarration(movementResult);
  const currentLocationForNarration = buildCurrentLocationForNarration(currentLocation);
  const rollDataForNarration = buildRollDataForNarration(rollData);
  const playerActionForNarration = buildPlayerActionForNarration(playerAction);
  const npcResponsesForNarration = buildNpcResponsesForNarration(npcResponses);

  return {
    reason,
    isInitialScene,
    explorer: summarizeBeingForNarration(explorer),
    currentPlace: {
      place: summarizePlaceForNarration(currentPlaceContext?.place),
      subPlaces: toArray(currentPlaceContext?.subPlaces).map(summarizePlaceForNarration),
      items: toArray(currentPlaceContext?.place?.containedItems).map(summarizeItemForNarration),
      services: toArray(currentPlaceContext?.services),
    },
    beingsInLocation: toArray(currentPlaceContext?.beingsInLocation).map(summarizeBeingForNarration),
    nearbyPlaces,
    nearbyBeings,
    currentLocation: currentLocationForNarration,
    time: {
      currentTimeLabel,
      timeOfDay: currentTimeLabel,
      publicTimeLabel: currentTimeLabel,
    },
    vitalCondition: gameplayContext?.vitalCondition ?? null,
    consumptionResult: gameplayContext?.lastTurnResult?.consumptionResult ?? null,
    itemHandoffResults: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.itemHandoffResults),
    publicEvents: isInitialScene ? undefined : gameplayContext?.lastTurnResult?.publicEvents,
    spatialFrame: gameplayContext?.spatialFrame ?? null,
    activityEvents: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.activityEvents),
    observationOutcomes: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.observationOutcomes),
    sharedPlans: toArray(gameplayContext?.lastTurnResult?.sharedPlans),
    activityChanges: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.activityChanges),
    skillChecks: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.skillChecks),
    resumeOptions: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.resumeOptions),
    routineEvents: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.routineEvents),
    hazardEvents: isInitialScene ? [] : toArray(gameplayContext?.lastTurnResult?.hazardEvents),
    hazardContext: gameplayContext?.hazardContext ?? null,
    activityInterruption: isInitialScene ? null : gameplayContext?.lastTurnResult?.activityInterruption ?? null,
    weatherContext: buildWeatherContext(gameplayContext),
    history: toArray(history),
    recentNarration: toArray(recentNarration),
    instruction,
    firstCallOutput: playerActionForNarration,
    playerAction: playerActionForNarration,
    rollData: rollDataForNarration,
    movementResult: movementResultForNarration,
    combatResult,
    spellAcquisitionResult,
    combatRound: combatResult?.combatRound ?? null,
    tradeResults: toArray(tradeResults),
    tradeResult: toArray(tradeResults)[0] ?? null,
    contractResults: toArray(contractResults),
    contractResult: toArray(contractResults)[0] ?? null,
    secondCallOutput: npcResponsesForNarration,
    npcResponses: npcResponsesForNarration,
    displayedNpcSpeech: getDisplayedNpcSpeechForNarration({ npcResponseGroups: npcResponses, combatResult }),
    displayedSystemMessages,
    narrationInstruction: buildNarrationInstruction({
      isInitialScene,
      currentPlaceLabel,
      currentLocation,
    }),
  };
};

const getNarrationDescription = (narration) => String(narration?.description ?? '').trim();

const extractRollData = (npcResponseGroups) => {
  const rollGroups = toArray(npcResponseGroups)
    .map((group) => ({
      intent: group?.intent ?? '',
      debug: group?.result?.debug ?? null,
    }))
    .filter((group) => group.intent && group.debug);

  return rollGroups.length > 0 ? rollGroups : null;
};

const findLoadedPlaceById = (currentPlaceContext, placeId) => {
  const normalizedPlaceId = String(placeId ?? '').trim();
  if (!normalizedPlaceId) {
    return null;
  }

  return [
    currentPlaceContext?.place,
    ...toArray(currentPlaceContext?.subPlaces),
  ].find((place) => getEntityId(place) === normalizedPlaceId) || null;
};

const hasLoadedPlaceOccupancy = (place) =>
  Array.isArray(place?.savedInhabitants) ||
  Array.isArray(place?.containedVisitors) ||
  Array.isArray(place?.containedItems);

const loadPlaceContextAfterMovement = async ({
  movementResult,
  currentPlaceContext,
  knownScenePlaces = [],
  rootPlace,
  previousInteriorPlaceId = '',
  nextInteriorPlaceId = '',
  npcRoutineState = null,
}) => {
  const rootPlaceId = getEntityId(rootPlace);
  const resultPlaceId = String(movementResult?.placeId || movementResult?.currentLocation?.placeId || '').trim();
  const currentContextPlaceId = getEntityId(currentPlaceContext?.place);

  if (rootPlaceId && resultPlaceId === rootPlaceId && currentContextPlaceId !== rootPlaceId) {
    return loadCurrentPlaceContext(rootPlace, npcRoutineState);
  }

  if (
    nextInteriorPlaceId &&
    currentContextPlaceId !== nextInteriorPlaceId &&
    (movementResult?.actionComplete || resultPlaceId === nextInteriorPlaceId)
  ) {
    const enteredPlaceCandidate = findLoadedPlaceById(currentPlaceContext, nextInteriorPlaceId) ||
      toArray(knownScenePlaces).find((place) => getEntityId(place) === nextInteriorPlaceId);
    const enteredPlace = hasLoadedPlaceOccupancy(enteredPlaceCandidate)
      ? enteredPlaceCandidate
      : await callCdo(cdoPlace.select.bind(cdoPlace), nextInteriorPlaceId);
    return loadCurrentPlaceContext(enteredPlace, npcRoutineState);
  }

  if (!movementResult?.actionComplete) {
    return currentPlaceContext;
  }

  if (previousInteriorPlaceId && !nextInteriorPlaceId && rootPlace) {
    return loadCurrentPlaceContext(rootPlace, npcRoutineState);
  }

  return currentPlaceContext;
};

const buildCommerceFrame = ({ currentPlaceContext, nearbyBeings = [], currentTarget = null }) => {
  const currentPlace = currentPlaceContext?.place || null;
  const currentPlaceId = getEntityId(currentPlace);
  const affiliatedResponders = uniqueById([
    currentTarget,
    ...toArray(nearbyBeings),
  ].filter(Boolean)).filter((being) => {
    if (!currentPlaceId) {
      return true;
    }
    const affiliatedPlaceIds = getAffiliatedPlaceIds(being);
    return affiliatedPlaceIds.length === 0 || affiliatedPlaceIds.includes(currentPlaceId);
  });

  const stockItems = toArray(currentPlace?.containedItems).map((item) => ({
    ...item,
    holderId: currentPlaceId,
    holderName: getPlaceLabel(currentPlace),
  }));

  return {
    currentPlace: currentPlace
      ? {
        idEntity: currentPlaceId,
        name: currentPlace.name || currentPlace.entityName || '',
        title: currentPlace.title || currentPlace.entityTitle || '',
        description: currentPlace.description || currentPlace.entityDesc || '',
        placeType: currentPlace.placeType || '',
        buildingType: currentPlace.buildingType || '',
      }
      : null,
    currentTarget,
    stockItems,
    services: toArray(currentPlaceContext?.services),
    responders: affiliatedResponders,
    guidance: 'Use this only as current factual commerce context. Do not treat it as state; recent history decides whether the player is continuing, switching item, switching service, or asking a fresh question.',
  };
};

const findPrimaryFocusForAction = (playerAction, beingsInLocation) => {
  const targetIds = new Set(getResultTargetEntityIds(playerAction));
  if (targetIds.size === 0) {
    return null;
  }

  return toArray(beingsInLocation).find((being) => targetIds.has(getEntityId(being))) || null;
};

export const safeSpeakerLabel = label => {
  const text = String(label || '').trim();
  return text && !/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(text)
    ? text : 'the nearby person';
};

export const buildEntityNameById = (...entityGroups) => {
  const nameById = new Map();

  entityGroups.flatMap(toArray).forEach((entity) => {
    const idEntity = getEntityId(entity);
    if (idEntity) {
      const label = entity?.knownLabel || entity?.publicLabel || entity?.name || entity?.title || entity?.entityName || entity?.entityTitle;
      if (label && label !== idEntity && safeSpeakerLabel(label) === label) nameById.set(idEntity, label);
    }
  });

  return nameById;
};

export const getNpcSpeechMessages = (npcResponseGroups, nameById) => {
  const messages = [];

  toArray(npcResponseGroups).forEach((group) => {
    toArray(group?.result?.responses).forEach((response, index) => {
      const verbalResponse = String(response?.verbalResponse ?? '').trim();
      if (!verbalResponse) {
        return;
      }

      const label = nameById.get(response?.idEntity);
      const speaker = safeSpeakerLabel(label && label !== response?.idEntity ? label : null);
      messages.push({
        role: 'npc',
        speaker,
        content: verbalResponse,
        key: `${group?.intent || 'npc'}:${response?.idEntity || index}:${index}`,
      });
    });
  });

  return messages;
};

export const resolveNpcSpeakerNames = async ({ groups, nameById, loadBeing, knownNameIds, playerId }) => {
  const missingIds = [...new Set(toArray(groups).flatMap(group => toArray(group?.result?.responses))
    .filter(response => String(response?.verbalResponse || '').trim())
    .map(response => response.idEntity)
    .filter(id => id && (!nameById.get(id) || nameById.get(id) === id || safeSpeakerLabel(nameById.get(id)) !== nameById.get(id))))];
  await Promise.allSettled(missingIds.map(async id => {
    const being = await loadBeing(id);
    if (getEntityId(being) !== id) return;
    const labels = buildEntityNameById([maskBeingIdentityForPc(being, knownNameIds, playerId)]);
    if (labels.has(id)) nameById.set(id, labels.get(id));
  }));
  // A failed label lookup must not discard a resolved reply or expose its ID.
  return nameById;
};

function DebugJsonBlock({ title, value }) {
  return (
    <section className="play2-debug-section">
      <h4>{title}</h4>
      <pre>{JSON.stringify(value ?? null, null, 2)}</pre>
    </section>
  );
}

function NpcResponseDebugList({ groups, entityNameById }) {
  if (toArray(groups).length === 0) {
    return <div className="play2-empty-result">No NPC response output yet.</div>;
  }

  return (
    <div className="play2-debug-npc-list">
      {toArray(groups).map((group) => (
        <article key={group?.intent || 'npc-response'} className="play2-debug-npc-group">
          <h5>{group?.intent || 'NPC Response'}</h5>
          {toArray(group?.result?.responses).length === 0 ? (
            <div className="play2-empty-result">No response returned for this intent.</div>
          ) : (
            toArray(group?.result?.responses).map((response, index) => (
              <div key={`${group?.intent}:${response?.idEntity}:${index}`} className="play2-debug-npc-response">
                <strong>{safeSpeakerLabel(entityNameById.get(response?.idEntity))}</strong>
                {response?.verbalResponse && <p>{response.verbalResponse}</p>}
                <DebugJsonBlock title="Response" value={response} />
              </div>
            ))
          )}
          <DebugJsonBlock title="Debug" value={group?.result?.debug} />
        </article>
      ))}
    </div>
  );
}

function Play2DebugView({
  isOpen,
  onClose,
  interactionState,
  narrationContext,
  currentPlaceContext,
  playerCharacter,
  movementState,
  combatState,
  settlementScene,
  routineEvaluation,
  turnTrace,
  playerId,
  turnId,
  skillProgression,
}) {
  const [encounterDiagnostics, setEncounterDiagnostics] = useState(null);
  useEffect(() => {
    if (!isOpen || !playerId) return undefined;
    let cancelled = false;
    cdoGameplay.encounterDiagnostics({ playerId, baseTurnId: turnId || '' }, (error, result) => {
      if (!cancelled) setEncounterDiagnostics(error || result?.error ? { error: error?.message || result.error?.message || 'Diagnostics unavailable.' } : result);
    });
    return () => { cancelled = true; };
  }, [isOpen, playerId, turnId]);
  const entityNameById = buildEntityNameById(
    [playerCharacter],
    currentPlaceContext?.beingsInLocation,
    currentPlaceContext?.subPlaces,
    [currentPlaceContext?.place]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="play2-debug-overlay" role="dialog" aria-modal="true" aria-label="Play2 debug view">
      <div className="play2-debug-modal">
        <div className="play2-debug-header">
          <h3>Debug View</h3>
          <button type="button" className="secondary-button" onClick={onClose}>
            Close
          </button>
        </div>

        <SkillProgressDetails progression={skillProgression} secondarySkills={playerCharacter?.secondarySkills || []} />
        <section className="play2-debug-section">
          <h4>First Call</h4>
          <IntentResultList result={interactionState.lastPlayerAction} />
        </section>

        <section className="play2-debug-section">
          <h4>NPC Responses</h4>
          <NpcResponseDebugList groups={interactionState.npcResponseGroups} entityNameById={entityNameById} />
          {interactionState.npcPlaceholder && (
            <div className="play2-placeholder-row">{interactionState.npcPlaceholder}</div>
          )}
        </section>

        <DebugJsonBlock title="Roll Data" value={interactionState.rollData} />
        <DebugJsonBlock title="Turn Trace" value={turnTrace} />
        <DebugJsonBlock title="Trade Results" value={interactionState.tradeResults} />
        <DebugJsonBlock title="Contract Results" value={interactionState.contractResults} />
        <DebugJsonBlock title="Spell Acquisition Result" value={interactionState.spellAcquisitionResult} />
        <DebugJsonBlock title="Combat Participation" value={interactionState.combatParticipationResult} />
        <DebugJsonBlock title="Combat State" value={combatState} />
        <DebugJsonBlock title="Combat Result" value={interactionState.combatResult} />
        <DebugJsonBlock title="Movement State" value={movementState} />
        <DebugJsonBlock title="Shared Plans" value={narrationContext?.sharedPlans} />
        <DebugJsonBlock title="Routine Evaluation" value={routineEvaluation} />
        <DebugJsonBlock title="Encounter Checks and Tables" value={encounterDiagnostics} />
        <DebugJsonBlock title="Observation Outcomes" value={narrationContext?.observationOutcomes} />
        <DebugJsonBlock title="Settlement Scene" value={settlementScene} />
        <DebugJsonBlock title="Narration Context" value={narrationContext} />
        <DebugJsonBlock title="Last Narration" value={interactionState.lastNarration} />
      </div>
    </div>
  );
}

const formatCurrentWeather = (currentWeather) => {
  if (!currentWeather) {
    return 'Unknown';
  }

  const summary = currentWeather.summary || currentWeather.condition || '';
  const temperature = Number.isFinite(Number(currentWeather.temperatureF))
    ? `${Math.round(Number(currentWeather.temperatureF))}F`
    : '';

  return [summary, temperature].filter(Boolean).join(', ') || 'Unknown';
};

const resolveStatusValue = (being, keys, fallback = 100) => {
  for (const key of keys) {
    if (being?.[key] === undefined || being?.[key] === null || being?.[key] === '') {
      continue;
    }

    const value = Number(being?.[key]);
    if (Number.isFinite(value)) {
      return Math.max(0, value);
    }
  }

  return fallback;
};

const normalizeResultIntent = (intent) => {
  if (typeof intent === 'string') {
    return {
      intent,
      perceiveSubAction: null,
    };
  }

  return {
    intent: intent?.intent || '',
    perceiveSubAction: intent?.perceiveSubAction ?? null,
  };
};

const getResultIntents = (result) =>
  (Array.isArray(result?.intents)
    ? result.intents
    : result?.primaryIntent
      ? [result.primaryIntent]
      : []
  ).map(normalizeResultIntent).filter((intent) => intent.intent);

const getTimeAdvanceIntent = (playerAction) =>
  getResultIntents(playerAction).find((intent) => TIME_ADVANCE_INTENTS.has(intent.intent))?.intent || '';

const getPlayerActionTimeAdvanceMinutes = (playerAction) => {
  const timeAdvanceIntent = getTimeAdvanceIntent(playerAction);
  if (!timeAdvanceIntent) {
    return getResultIntents(playerAction).some(entry => entry.intent === 'consume') ? 1 : 0;
  }

  const explicitMinutes = Number(playerAction?.waitDurationMinutes);
  if (Number.isFinite(explicitMinutes) && explicitMinutes > 0) {
    return Math.min(Math.max(1, Math.round(explicitMinutes)), MAX_TIME_ADVANCE_MINUTES);
  }

  return DEFAULT_TIME_ADVANCE_MINUTES[timeAdvanceIntent] || DEFAULT_TIME_ADVANCE_MINUTES.wait;
};

const hasSocialResponseIntent = (playerAction, responseIntents = null) => {
  const responseIntentNames = toArray(responseIntents).map((intent) => normalizeLookupValue(intent?.intent ?? intent));
  return responseIntentNames.some((intent) => SOCIAL_RESPONSE_INTENTS.has(intent)) ||
    getResultIntents(playerAction).some((intent) => SOCIAL_RESPONSE_INTENTS.has(intent.intent));
};

const buildNoVisibleOutcomeMessage = ({ playerAction, responseIntents = null, nearbyBeings = [], npcResponseGroups = [] }) => {
  const silentReply = hasSocialResponseIntent(playerAction, responseIntents) &&
    toArray(npcResponseGroups).some(group => group.result?.noResponse === true || toArray(group.result?.responses).length > 0);
  return {
    role: silentReply ? 'assistant' : 'system',
    responseType: 'no_visible_outcome',
    severity: 'info',
    content: silentReply ? 'No response.' : hasSocialResponseIntent(playerAction, responseIntents)
      ? toArray(nearbyBeings).length > 0
        ? 'No nearby person is clearly selected to respond.'
        : 'No one nearby is available to respond.'
      : 'Nothing obvious changes.',
  };
};

const appendNoVisibleOutcomeMessage = (messages, messagesBeforeTurn, fallbackMessage) => {
  const baselineLength = toArray(messagesBeforeTurn).length + 1;
  return toArray(messages).length > baselineLength
    ? messages
    : [
      ...toArray(messages),
      fallbackMessage || {
        role: 'system',
        responseType: 'no_visible_outcome',
        severity: 'info',
        content: 'Nothing obvious changes.',
      },
    ];
};

export function TradeConfirmationModal({ pendingTradeConfirmation, onConfirm, onCancel, isSubmitting }) {
  const confirmation = pendingTradeConfirmation?.confirmation;
  if (!confirmation) {
    return null;
  }

  const giveLines = getTradeConfirmationLines(confirmation.itemsFromPc, confirmation.currencyFromPc);
  const exchanges = Array.isArray(confirmation.exchanges) ? confirmation.exchanges : [confirmation];
  const isCommission = (exchange) => exchange.contractDetails?.status !== 'completed' && isItemCommission(exchange.contractDetails);
  const commissionedItems = exchanges.filter(isCommission).flatMap(exchange =>
    toArray(exchange.itemsToPc).filter(item => !item.itemId));
  const receiveLines = exchanges.flatMap(exchange => getTradeConfirmationLines(
    isCommission(exchange) ? toArray(exchange.itemsToPc).filter(item => item.itemId) : exchange.itemsToPc,
    exchange.currencyToPc,
  ));
  const contracts = exchanges.filter(exchange => exchange.contractDetails);
  const contractSummary = String(confirmation.contractSummary || '').trim();
  const title = contractSummary ? 'Confirm Agreement' : 'Confirm Trade';
  const paymentSummary = confirmation.paymentSummary;
  const paymentLine = (amount) => [
    formatTradeCurrencyRows(amount?.fromPc),
    amount?.toPc?.length ? `${formatTradeCurrencyRows(amount.toPc)} to you` : '',
  ].filter(Boolean).join('; ') || 'None';

  return (
    <div className="play2-trade-confirm-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="play2-trade-confirm-modal">
        <div className="play2-trade-confirm-header">
          <h3>{title}</h3>
          <button
            type="button"
            className="play2-trade-confirm-close"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel trade"
          >
            Close
          </button>
        </div>
        {contractSummary ? (
          <div className="play2-trade-confirm-summary">
            <p>{contractSummary}</p>
            {contracts.map((exchange, index) => (
              <ContractTerms key={index} details={exchange.contractDetails} showAgreement={contracts.length > 1} />
            ))}
          </div>
        ) : null}
        {paymentSummary ? (
          <dl className="play2-trade-confirm-summary">
            <dt>Total price</dt><dd>{paymentLine(paymentSummary.totalPrice)}</dd>
            <dt>Due now</dt><dd>{paymentLine(paymentSummary.dueNowPrice)}</dd>
            <dt>Remaining payment</dt><dd>{paymentLine(paymentSummary.remainingPrice)}</dd>
            {paymentSummary.tip?.length > 0 ? <><dt>Tip</dt><dd>{formatTradeCurrencyRows(paymentSummary.tip)}</dd></> : null}
          </dl>
        ) : null}
        <div className="play2-trade-confirm-grid">
          <section>
            <h4>{contracts.length > 0 ? 'You Give Now' : 'You Give'}</h4>
            {contracts.length > 0 && giveLines.length === 0 ? <p>Nothing due now.</p> : null}
            {giveLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </section>
          <section>
            {receiveLines.length > 0 || commissionedItems.length === 0 ? <>
              <h4>{contracts.length > 0 ? 'You Receive Now' : 'You Receive'}</h4>
              {receiveLines.map((line, index) => <p key={index}>{line}</p>)}
            </> : null}
            {commissionedItems.length > 0 ? <>
              <h4>Commissioned item—delivered on completion</h4>
              {commissionedItems.map((item, index) => <p key={index}>{formatTradeItems([item])}</p>)}
            </> : null}
          </section>
        </div>
        <div className="play2-trade-confirm-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Confirming...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

const resolveOptionalStatusValue = (being, keys) => {
  for (const key of keys) {
    if (being?.[key] === undefined || being?.[key] === null || being?.[key] === '') {
      continue;
    }

    const value = Number(being?.[key]);
    if (Number.isFinite(value) && value >= 0) {
      return value;
    }
  }

  return null;
};

function IntentResultList({ result }) {
  const resultIntents = getResultIntents(result);
  const socialContext = toArray(result?.socialContext).filter(Boolean);
  const targetEntityIds = toArray(result?.targetEntityIds).filter(Boolean);

  if (resultIntents.length === 0) {
    return <div className="play2-empty-result">No first-call result yet.</div>;
  }

  return (
    <div className="play2-intent-list">
      {resultIntents.map((intent) => (
        <span key={`${intent.intent}:${intent.perceiveSubAction || ''}`} className="play2-intent-chip">
          {intent.intent}
          {intent.perceiveSubAction ? `: ${intent.perceiveSubAction}` : ''}
        </span>
      ))}
      {socialContext.length > 0 && (
        <div className="play2-context-list">
          {socialContext.map((context) => (
            <span key={context}>{context}</span>
          ))}
        </div>
      )}
      {targetEntityIds.length > 0 && (
        <div className="play2-context-list">
          {targetEntityIds.map((idEntity) => (
            <span key={idEntity}>Target: {idEntity}</span>
          ))}
        </div>
      )}
    </div>
  );
}

const getInitials = (value) => {
  const parts = String(value ?? '').trim().split(' ').filter(Boolean);
  if (parts.length === 0) {
    return 'PC';
  }

  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
};

const getPendingMovementDescription = (movementState) => {
  const targetName = String(movementState?.lastMovementTarget?.targetName ?? '').trim();
  if (targetName) {
    return `Still moving toward ${targetName}.`;
  }

  return 'Movement is still in progress.';
};

function PlaceMapShape({ node, hitArea = false }) {
  const commonProps = {
    fill: hitArea ? 'transparent' : node.fill,
    stroke: hitArea ? 'transparent' : node.stroke,
    strokeWidth: hitArea ? Math.max(10, node.strokeWidth || 0) : node.strokeWidth,
    pointerEvents: hitArea ? (node.pathData || node.shapeType === 'path' ? 'stroke' : 'all') : 'none',
    'aria-hidden': hitArea ? true : undefined,
    className: `play2-map-shape ${hitArea ? 'play2-map-hit-area' : node.isFocus ? 'play2-map-shape-focus' : ''}`,
    style: !hitArea && node.scaleStroke ? { vectorEffect: 'none' } : undefined,
  };

  if (node.shapeType === 'polygon' && node.points) {
    return <polygon points={node.points} {...commonProps} />;
  }

  if (node.pathData) return <path d={node.pathData} {...commonProps} fill="none" strokeLinecap="round" strokeLinejoin="round" />;

  if (node.shapeType === 'path' && node.points) {
    return (
      <polyline
        points={node.points}
        {...commonProps}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  }

  if (['sphere', 'cylinder'].includes(node.shapeType)) {
    return (
      <circle
        cx={node.centerX}
        cy={node.centerY}
        r={node.radius}
        {...commonProps}
      />
    );
  }

  return (
    <rect
      x={node.x}
      y={node.y}
      width={node.width}
      height={node.height}
      {...commonProps}
    />
  );
}

export function PlaceMapModal({
  isOpen,
  onClose,
  place,
  mapPlan,
  zoom,
  onZoomIn,
  onZoomOut,
  playerPortraitSrc,
  playerLabel,
  beingPortraitsById,
  placeLabelFor,
  legend,
  placeTooltipFor,
}) {
  const frameRef = useRef(null);
  const [hoveredPlace, setHoveredPlace] = useState(null);
  const showPlaceLabel = (event, node) => {
    const bounds = frameRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setHoveredPlace({
      label: placeTooltipFor ? placeTooltipFor({ idEntity: node.id }) : `${placeLabelFor({ idEntity: node.id })} — ${toArray(mapPlan?.searchCoverage).filter(entry => entry.status === 'examined'
        && toArray(entry.sourceIds).includes(node.id)).map(entry => `searched for ${entry.objectiveText}`).filter((value, index, values) => values.indexOf(value) === index).join('; ') || 'seen; no completed frontage examination recorded'}`,
      x: Math.max(8, Math.min(event.clientX - bounds.left + 12, bounds.width - 248)),
      y: Math.max(8, event.clientY - bounds.top - 44),
    });
  };
  const viewBox = useMemo(() => buildMapViewBox(mapPlan, zoom), [mapPlan, zoom]);
  const minimumZoom = mapPlan?.playerCentered ? REGIONAL_MAP_MIN_ZOOM : PLACE_OVERVIEW_MIN_ZOOM;
  const normalizedZoom = clampNumber(Number(zoom) || 1, minimumZoom, PLACE_OVERVIEW_MAX_ZOOM);
  const inverseZoom = 1 / normalizedZoom;
  const visibleLabels = toArray(mapPlan?.nodes).filter(node => node.showLabel);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="play2-map-overlay" role="dialog" aria-modal="true" aria-label="Settlement top-down view">
      <div className="play2-map-modal">
        <div className="play2-map-header">
          <div>
            <h3>{getPlaceLabel(place)}</h3>
          </div>
          <button type="button" className="secondary-button" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="play2-map-toolbar">
          <button
            type="button"
            className="secondary-button"
            onClick={onZoomOut}
            disabled={zoom <= minimumZoom}
            aria-label="Zoom out"
          >
            -
          </button>
          <span>Zoom {zoom.toFixed(zoom < 0.01 ? 4 : 2)}x</span>
          {legend}
          {mapPlan?.searchCoverage?.length > 0 && <span>Search: green = examined · amber = partial · blue dashes = planned</span>}
          {mapPlan?.radiusFeet && <span>Radius {mapPlan.radiusFeet / normalizedZoom < 5280
            ? `${Math.round(mapPlan.radiusFeet / normalizedZoom)} feet`
            : `${(mapPlan.radiusFeet / 5280 / normalizedZoom).toFixed(1)} miles`}</span>}
          <button
            type="button"
            className="secondary-button"
            onClick={onZoomIn}
            disabled={zoom >= PLACE_OVERVIEW_MAX_ZOOM}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>

        <MapScrollViewport frameRef={frameRef} plan={mapPlan} viewBox={viewBox} onPointerLeave={() => setHoveredPlace(null)}>
          {scrollViewBox => <>
          {mapPlan ? (
            <svg
              className="play2-map-svg"
              viewBox={`${scrollViewBox.x} ${scrollViewBox.y} ${scrollViewBox.width} ${scrollViewBox.height}`}
              role="img"
              aria-label={`Top-down map of ${getPlaceLabel(place)} with player location.`}
            >
              <rect x={scrollViewBox.x} y={scrollViewBox.y} width={scrollViewBox.width} height={scrollViewBox.height} fill="#07111d" />
              {mapPlan.nodes.map((node) => (
                <g key={node.id} aria-label={placeLabelFor({ idEntity: node.id })}
                  pointerEvents={node.hoverable === false ? 'none' : undefined}
                  onPointerMove={node.hoverable === false ? undefined : (event) => showPlaceLabel(event, node)}
                  onPointerLeave={node.hoverable === false ? undefined : () => setHoveredPlace(null)}>
                  <PlaceMapShape node={node} />
                  {node.hoverable !== false && <PlaceMapShape node={node} hitArea />}
                </g>
              ))}
              {toArray(mapPlan.searchCoverage).map((entry, index) => (
                <polyline key={`search-${index}`} points={entry.points} fill="none"
                  stroke={entry.status === 'examined' ? '#64d98b' : entry.status === 'partial' ? '#f5bb56' : '#5eb7ff'}
                  strokeWidth={4 * inverseZoom} strokeDasharray={entry.status === 'planned' ? `${8 * inverseZoom} ${5 * inverseZoom}` : undefined}
                  opacity="0.85" aria-label={entry.label}><title>{entry.label}</title></polyline>
              ))}
              {toArray(mapPlan.traversedRoutes).length > 0 && <g aria-label="Recently traversed route" pointerEvents="none">
                {mapPlan.traversedRoutes.map((route, index) => <g key={index}>
                  {mapPlan.traversalCasing !== false && <polyline points={route.points} fill="none" stroke="#07111d" strokeWidth={6 * inverseZoom} />}
                  <polyline points={route.points} fill="none" stroke="#fb923c" strokeWidth={3 * inverseZoom} strokeLinejoin="round" />
                  {route.arrows.map((arrow, i) => <path key={i} d="M -5 -4 L 0 0 L -5 4" fill="none" stroke="#fb923c" strokeWidth="2"
                    transform={`translate(${arrow.x} ${arrow.y}) rotate(${arrow.angle}) scale(${inverseZoom})`} />)}
                </g>)}
              </g>}
              {visibleLabels.map(node => (
                <text key={`${node.id}-label`} x="0" y="0"
                  transform={`translate(${node.centerX} ${node.centerY}) scale(${inverseZoom})`}
                  textAnchor="middle" dominantBaseline="central" className="play2-map-label">
                  {placeLabelFor({ idEntity: node.id })}
                </text>
              ))}
              {toArray(mapPlan.beingPins).map((pin) => {
                const portraitState = beingPortraitsById?.[pin.id] ?? {};
                return (
                  <g key={pin.id} className="play2-map-being-pin" data-visibility={pin.visibility} transform={`translate(${pin.x} ${pin.y})`}>
                    <g transform={`scale(${inverseZoom})`}>
                      <circle className="play2-map-being-halo" cx="0" cy="0" r="10" />
                      <circle className="play2-map-being-face" cx="0" cy="0" r="7.5" />
                      {portraitState.src ? (
                        <image
                          className="play2-map-being-thumbnail"
                          href={portraitState.src}
                          x="-6"
                          y="-6"
                          width="12"
                          height="12"
                          preserveAspectRatio="xMidYMid slice"
                        />
                      ) : (
                        <text x="0" y="0" textAnchor="middle" dominantBaseline="central">
                          {pin.initials}
                        </text>
                      )}
                      {pin.visibility && <circle className="play2-map-being-hit-area" cx="0" cy="0" r="10" fill="transparent" pointerEvents="all" />}
                      <title>{pin.visibility === 'remembered' ? `${pin.label} — last seen here at ${String(Math.floor(pin.lastSeenMinute / 60) % 24).padStart(2, '0')}:${String(Math.floor(pin.lastSeenMinute) % 60).padStart(2, '0')}` : pin.label}</title>
                    </g>
                  </g>
                );
              })}
              {mapPlan.pcPin && (
                <g className="play2-map-pc-pin" transform={`translate(${mapPlan.pcPin.x} ${mapPlan.pcPin.y})`}>
                  <g transform={`scale(${inverseZoom})`}>
                    <line className="play2-map-pc-stem" x1="0" y1="-4" x2="0" y2="-18" />
                    <circle className="play2-map-pc-point" cx="0" cy="0" r="3.5" />
                    <circle className="play2-map-pc-halo" cx="0" cy="-31" r="14" />
                    <circle className="play2-map-pc-face" cx="0" cy="-31" r="10.5" />
                    {playerPortraitSrc ? (
                      <image
                        className="play2-map-pc-thumbnail"
                        href={playerPortraitSrc}
                        x="-8.5"
                        y="-39.5"
                        width="17"
                        height="17"
                        preserveAspectRatio="xMidYMid slice"
                      />
                    ) : (
                      <text x="0" y="-31" textAnchor="middle" dominantBaseline="central">
                        {getInitials(playerLabel)}
                      </text>
                    )}
                    <title>{playerLabel || 'Player character'}</title>
                  </g>
                </g>
              )}
            </svg>
          ) : (
            <div className="play2-map-empty">No top-down view is available for this place.</div>
          )}
          {hoveredPlace && (
            <div className="play2-map-tooltip" role="tooltip"
              style={{ left: hoveredPlace.x, top: hoveredPlace.y }}>
              {hoveredPlace.label}
            </div>
          )}
          </>}
        </MapScrollViewport>
      </div>
    </div>
  );
}

const requestPlayCdo = callCdo;

function Play2({ playerId = '', onBootstrap = null, characterSheetProps = null, onCharacterRefresh = null, onExitGame = null }) {
  const startupPlayerId = String(playerId || characterSheetProps?.value?.idEntity || readCookie(ADENREL_CHARACTER_COOKIE)).trim();
  const bootstrapIdentity = `${playerSelectionKey()}:${startupPlayerId}`;
  const onBootstrapRef = useRef(onBootstrap);
  onBootstrapRef.current = onBootstrap;
  const backgroundReadsRef = useRef(createBackgroundReads(2));
  const canUseDebug = localDebugEnabled();
  const [placeChain, setPlaceChain] = useState([]);
  const [placeKnowledge, setPlaceKnowledge] = useState({ knowledge: {}, places: [] });
  const placeKnowledgeRef = useRef({ knowledge: {}, places: [] });
  const placeIdentityRef = useRef(null);
  const identityCatalogRef = useRef([]);
  const identityPlayerRef = useRef('');
  const knowledgeCheckRef = useRef(null);
  const [knowledgeReview, setKnowledgeReview] = useState({ playerId: '', status: 'idle' });
  const acceptPlaceKnowledge = useCallback((next) => {
    if (!next) return;
    const knowledge = { ...placeKnowledgeRef.current.knowledge };
    Object.entries(next.knowledge || {}).forEach(([id, entry]) => {
      knowledge[id] = { ...entry, locationKnown: Boolean(entry.locationKnown || knowledge[id]?.locationKnown) };
    });
    const previousVisibility = placeKnowledgeRef.current.placeVisibility;
    next = { ...next, knowledge,
      ...(previousVisibility && (!next.placeVisibility || previousVisibility.minute > next.placeVisibility.minute)
        ? { placeVisibility: previousVisibility } : {}) };
    placeKnowledgeRef.current = next;
    const publicById = new Map((next.places || []).map(place => [place.idEntity, place]));
    const catalog = identityCatalogRef.current.map(place => ({ ...place,
      publicLabel: publicById.get(place.idEntity)?.publicLabel || place.publicLabel,
      publicDescription: publicById.get(place.idEntity)?.publicDescription || place.publicDescription,
    }));
    placeIdentityRef.current = placeIdentity.createPlaceIdentity(catalog, next.knowledge || {}, placeIdentity.visibleSignText(next.observedSigns));
    setPlaceKnowledge(next);
  }, []);
  const requestKnowledgeCheck = useCallback((playerId) => {
    knowledgeCheckRef.current ||= createKnowledgeCheckQueue({
      load: playerId => requestPlayCdo(cdoGameplay.placeKnowledge.bind(cdoGameplay), { playerId, processPending: true }),
      accept: acceptPlaceKnowledge, currentPlayerId: () => identityPlayerRef.current,
      onState: setKnowledgeReview,
    });
    knowledgeCheckRef.current.request(playerId);
  }, [acceptPlaceKnowledge]);
  useEffect(() => () => {
    knowledgeCheckRef.current?.dispose();
    knowledgeCheckRef.current = null;
  }, []);
  const callCdo = useCallback(async (method, ...args) => {
    const result = await requestPlayCdo(method, ...args);
    if (result?.placeKnowledge) acceptPlaceKnowledge(result.placeKnowledge);
    else if (result?.finalized?.placeKnowledge) acceptPlaceKnowledge(result.finalized.placeKnowledge);
    const knowledge = result?.placeKnowledge || result?.finalized?.placeKnowledge;
    if (knowledge?.knowledgeCheckPending && !args[0]?.deferAssessment) {
      requestKnowledgeCheck(args[0]?.playerId || identityPlayerRef.current);
    }
    return result;
  }, [acceptPlaceKnowledge, requestKnowledgeCheck]);
  const callPlayAI = useCallback((method, data) => callCdo(method,
    placeIdentityRef.current.sanitize({ ...data, playerId: data.playerId || identityPlayerRef.current || data.explorer?.idEntity })
  ), [callCdo]);
  const [playerCharacter, setPlayerCharacter] = useState(null);
  const [gameplayContext, setGameplayContext] = useState(null);
  const [npcRoutineState, setNpcRoutineState] = useState(null);
  const routineBeingProfilesRef = useRef({});
  const [currentPlaceContext, setCurrentPlaceContext] = useState(null);
  const [hydratedPlaceDetailsById, setHydratedPlaceDetailsById] = useState({});
  const [knownScenePlaces, setKnownScenePlaces] = useState([]);
  const [explorerLocation, setExplorerLocation] = useState(null);
  const [movementState, setMovementState] = useState(createEmptyMovementState);
  const [combatState, setCombatState] = useState(null);
  const [narrationContext, setNarrationContext] = useState(null);
  const actionRequestRef = useRef(null);
  useEffect(() => () => actionRequestRef.current?.abort?.(), []);
  const [placeImagesById, setPlaceImagesById] = useState({});
  const [playerPortrait, setPlayerPortrait] = useState({ src: '', isLoading: false, error: '' });
  const [beingPortraitsById, setBeingPortraitsById] = useState({});
  const [pcKnownBeingNameIds, setPcKnownBeingNameIds] = useState({});
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [actionProgress, setActionProgress] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [interactionState, setInteractionState] = useState(createEmptyInteractionState);
  useEffect(() => {
    if (placeKnowledge.narrationCorrections?.length) {
      setInteractionState(current => applyNarrationCorrections(current, placeKnowledge.narrationCorrections));
    }
  }, [interactionState.messages, placeKnowledge.narrationCorrections]);
  const [isCharacterSheetOpen, setIsCharacterSheetOpen] = useState(false);
  const [isDebugViewOpen, setIsDebugViewOpen] = useState(false);
  const [pendingTradeConfirmation, setPendingTradeConfirmation] = useState(null);
  const [mapModalPlaceId, setMapModalPlaceId] = useState('');
  const [placeMapZoom, setPlaceMapZoom] = useState(1);
  const placeImageRequestsRef = useRef(new Set());
  const playerPortraitRequestRef = useRef('');
  const beingPortraitRequestsRef = useRef(new Set());
  const placeDetailRequestsRef = useRef(new Set());
  const nameKnowledgeRequestsRef = useRef(new Set());
  const chatLogRef = useRef(null);
  const actionInputRef = useRef(null);

  const playerCharacterId = String(playerCharacter?.idEntity ?? '').trim();
  identityPlayerRef.current = playerCharacterId;
  useEffect(() => {
    // Resume queued and legacy unassessed evidence after the scene has loaded.
    if (playerCharacterId && !isBootstrapping) requestKnowledgeCheck(playerCharacterId);
  }, [playerCharacterId, isBootstrapping, requestKnowledgeCheck]);
  const identityCatalog = useMemo(() => uniqueById([
    ...placeChain, ...knownScenePlaces, currentPlaceContext?.place,
    ...toArray(currentPlaceContext?.subPlaces), ...Object.values(hydratedPlaceDetailsById),
    ...toArray(placeKnowledge.places),
  ].filter(Boolean)), [placeChain, knownScenePlaces, currentPlaceContext, hydratedPlaceDetailsById, placeKnowledge.places]);
  identityCatalogRef.current = identityCatalog;
  const placePresentation = useMemo(() => {
    const publicPlaceViews = new Map(toArray(placeKnowledge.places).map(place => [getEntityId(place), place]));
    return placeIdentity.createPlaceIdentity(identityCatalog.map(place => ({
      ...place, publicLabel: publicPlaceViews.get(getEntityId(place))?.publicLabel || place.publicLabel,
      publicDescription: publicPlaceViews.get(getEntityId(place))?.publicDescription || place.publicDescription,
    })), placeKnowledge.knowledge, placeIdentity.visibleSignText(placeKnowledge.observedSigns));
  }, [identityCatalog, placeKnowledge]);
  placeIdentityRef.current = placePresentation;
  const visibleMessages = useMemo(() => interactionState.messages
    .filter(message => !message.pendingNarration || !interactionState.isSubmitting)
    .map(message => ({
      ...message,
      content: message.role === 'assistant'
        ? placePresentation.sanitizePhysicalText(message.content)
        : placePresentation.sanitizeText(message.content),
    })), [interactionState.messages, interactionState.isSubmitting, placePresentation]);
  const pendingMovementDescription = useMemo(() => movementState.pendingRoute.length > 0
    ? getPendingMovementDescription(placePresentation.sanitize(movementState)) : '', [movementState, placePresentation]);

  const pcKnownBeingNameIdSet = useMemo(
    () => new Set(Object.keys(pcKnownBeingNameIds).filter((beingId) => pcKnownBeingNameIds[beingId] === true)),
    [pcKnownBeingNameIds]
  );
  const effectiveCurrentPlaceContext = useMemo(
    () => applyRoutineStateToPlaceContext(
      { ...buildEffectiveCurrentPlaceContext(currentPlaceContext, hydratedPlaceDetailsById),
        enclosedPlaceIds: placeKnowledge.placeVisibility?.enclosedPlaceIds || [] }, npcRoutineState,
      explorerLocation, explorerLocation?.placeId || movementState.placeId, routineBeingProfilesRef.current
    ),
    [currentPlaceContext, hydratedPlaceDetailsById, npcRoutineState, explorerLocation, movementState.placeId, placeKnowledge.placeVisibility]
  );
  const settlementScene = useMemo(() => buildMinimalSettlementScene(placeChain, knownScenePlaces), [knownScenePlaces, placeChain]);
  const rawCurrentLocationPlaceId = String(explorerLocation?.placeId ?? '').trim();
  const contextPlace = effectiveCurrentPlaceContext?.place;
  const contextPlaceId = getEntityId(contextPlace);
  const currentLocationPoint = explorerLocation?.location ? normalizeLocation(explorerLocation.location) : null;
  const currentLocationPlace = rawCurrentLocationPlaceId
    ? uniqueById([
      contextPlace,
      ...toArray(effectiveCurrentPlaceContext?.subPlaces),
      ...toArray(knownScenePlaces),
      ...toArray(placeChain),
    ]).find((place) => getEntityId(place) === rawCurrentLocationPlaceId)
    : null;
  const staleEnclosedLocationPlaceId = Boolean(
    rawCurrentLocationPlaceId &&
    currentLocationPlace &&
    currentLocationPoint &&
    isEnclosedOccupantPlace(currentLocationPlace) &&
    !pointWithinHorizontalBounds(currentLocationPoint, getShapeBounds(currentLocationPlace), 2)
  );
  const currentLocationPlaceId = staleEnclosedLocationPlaceId
    ? getSettlementRootPlaceId(settlementScene)
    : rawCurrentLocationPlaceId;
  const movementInteriorPlaceId = getMovementInteriorPlaceId({
    movementState: currentLocationPlaceId ? { placeId: currentLocationPlaceId } : movementState,
    settlementScene,
  });
  const contextInteriorPlaceId = getPlaceContextInteriorPlaceId({
    currentPlaceContext: effectiveCurrentPlaceContext,
    settlementScene,
  });
  const activeInteriorPlaceId = (currentLocationPlaceId || String(movementState?.placeId ?? '').trim())
    ? movementInteriorPlaceId
    : contextInteriorPlaceId;
  const activePlaceCardGroups = useMemo(() => {
    const activePlace = staleEnclosedLocationPlaceId && contextPlaceId === rawCurrentLocationPlaceId
      ? null
      : effectiveCurrentPlaceContext?.place;
    const knownPlaces = uniqueById([
      ...toArray(placeChain),
      ...toArray(knownScenePlaces),
      activePlace,
    ]);
    const containingPlaces = uniqueById([
      ...toArray(placeChain),
      ...buildPlaceAncestorChain(activePlace, knownPlaces),
      activePlace,
    ].filter(place => place && placeContainsPosition(place, explorerLocation)));
    const cards = buildVisiblePlaceCards({ containingPlaces,
      places: [...knownPlaces, ...toArray(effectiveCurrentPlaceContext?.subPlaces), ...toArray(placeKnowledge.places)],
      visibility: placeKnowledge.placeVisibility, currentLocation: explorerLocation,
      currentMinute: gameplayContext?.currentTimeMinutes ?? DEFAULT_START_TIME_MINUTES });
    const containingIds = new Set(containingPlaces.map(getEntityId));
    return { all: cards, containing: cards.filter(place => containingIds.has(getEntityId(place))),
      nearby: cards.filter(place => !containingIds.has(getEntityId(place))) };
  }, [
    contextPlaceId,
    effectiveCurrentPlaceContext?.place,
    knownScenePlaces,
    placeChain,
    rawCurrentLocationPlaceId,
    staleEnclosedLocationPlaceId,
    explorerLocation,
    effectiveCurrentPlaceContext?.subPlaces,
    placeKnowledge,
    gameplayContext?.currentTimeMinutes,
  ]);
  const activePlaceCards = activePlaceCardGroups.all;
  const subPlaceImageIds = useMemo(() => settlementSubPlaceIds(getSettlementRootPlaceId(settlementScene), [
    ...placeChain, ...knownScenePlaces, ...activePlaceCards,
    ...(effectiveCurrentPlaceContext?.place ? [{ ...effectiveCurrentPlaceContext.place,
      composedOfChildren: effectiveCurrentPlaceContext.subPlaces || [] }] : []),
  ]), [settlementScene, placeChain, knownScenePlaces, activePlaceCards, effectiveCurrentPlaceContext]);
  const rawActiveBeingCards = useMemo(() => {
    const pcId = getEntityId(playerCharacter);
    const beings = getVisibleBeingsForCurrentLocation({
      currentPlaceContext: effectiveCurrentPlaceContext,
      currentLocation: explorerLocation,
      currentInteriorPlaceId: activeInteriorPlaceId,
    });

    return mergeCombatStateIntoBeings(beings, combatState)
      .filter((being) => getEntityId(being) && getEntityId(being) !== pcId);
  }, [activeInteriorPlaceId, combatState, effectiveCurrentPlaceContext, explorerLocation, playerCharacter]);
  const activeBeingCards = useMemo(
    () => maskBeingsForPc(rawActiveBeingCards, pcKnownBeingNameIdSet, playerCharacterId),
    [pcKnownBeingNameIdSet, playerCharacterId, rawActiveBeingCards]
  );
  const currentTimeDisplay = formatGameTime(gameplayContext?.currentTimeMinutes ?? DEFAULT_START_TIME_MINUTES);
  const mapModalPlace = useMemo(() => {
    if (!mapModalPlaceId) {
      return null;
    }

    return uniqueById([
      ...toArray(knownScenePlaces),
      ...toArray(activePlaceCards),
    ]).find((place) => getEntityId(place) === mapModalPlaceId) || null;
  }, [activePlaceCards, knownScenePlaces, mapModalPlaceId]);
  const placeMapPlan = useMemo(() => buildPlaceMapPlan({
    focusPlace: mapModalPlace,
    places: uniqueById([
      ...toArray(knownScenePlaces),
      ...toArray(activePlaceCards),
      ...toArray(placeKnowledge.places),
    ]),
    knowledge: placeKnowledge.knowledge,
    searchCoverage: placeKnowledge.searchMapOverlay,
    knownMapPlaceIds: [...toArray(placeKnowledge.knownMapPlaceIds),
      ...visiblePlaceIdsAt(placeKnowledge.placeVisibility, explorerLocation, gameplayContext?.currentTimeMinutes ?? DEFAULT_START_TIME_MINUTES),
      ...toArray(movementState.lastMovementResult?.observedPlaces).map(getEntityId)],
    explorerLocation,
    currentInteriorPlaceId: activeInteriorPlaceId,
    beings: activeBeingCards,
    beingAnchorPlace: effectiveCurrentPlaceContext?.place,
  }), [
    activeInteriorPlaceId,
    activeBeingCards,
    activePlaceCards,
    effectiveCurrentPlaceContext?.place,
    explorerLocation,
    knownScenePlaces,
    mapModalPlace,
    placeKnowledge,
    movementState.lastMovementResult,
    gameplayContext?.currentTimeMinutes,
  ]);
  const applyCompletedTurn = useCallback(async (snapshot, result, activePlayer = playerCharacter) => {
    const learnedBeingIds = [...toArray(result.knownBeingIds), ...toArray(result.knownNameUpdates)
      .filter(entry => entry.learnerBeingId === playerCharacterId)
      .map(entry => entry.revealedBeingId)];
    if (learnedBeingIds.length) {
      learnedBeingIds.forEach(beingId => nameKnowledgeRequestsRef.current.add(beingId));
      setPcKnownBeingNameIds(current => ({ ...current,
        ...Object.fromEntries(learnedBeingIds.map(beingId => [beingId, true])),
      }));
    }
    const committedKnownBeingNameIdSet = new Set([...pcKnownBeingNameIdSet, ...learnedBeingIds]);
    const context = result.sessionData.gameplayContext;
    // A departing person may no longer appear in beingsInLocation. Preserve
    // the player's known label for the narrator without revealing hidden names.
    if (context.lastTurnResult?.routineEvents) {
      const replacements = buildHiddenBeingNameReplacements(Object.values(routineBeingProfilesRef.current), committedKnownBeingNameIdSet, playerCharacterId);
      context.lastTurnResult.routineEvents = context.lastTurnResult.routineEvents.map(event => {
        const subject = maskBeingIdentityForPc(
          routineBeingProfilesRef.current[event.beingId] || { idEntity: event.beingId, description: event.publicDescription },
          committedKnownBeingNameIdSet, playerCharacterId
        );
        return sanitizePromptDataForHiddenBeingNames({ ...event, subject: {
          idEntity: subject.idEntity, name: subject.name, knownLabel: subject.knownLabel,
          publicLabel: subject.publicLabel, identityKnownToPc: subject.identityKnownToPc,
        } }, replacements);
      });
      result.notices = toArray(result.notices).map(text => sanitizeNarrationTextForHiddenBeingNames(text, replacements));
      context.lastTurnResult.hazardEvents = sanitizePromptDataForHiddenBeingNames(toArray(context.lastTurnResult.hazardEvents), replacements);
      context.hazardContext = sanitizePromptDataForHiddenBeingNames(context.hazardContext, replacements);
      context.lastTurnResult.activityInterruption = sanitizePromptDataForHiddenBeingNames(context.lastTurnResult.activityInterruption, replacements);
      for (const field of ['activityChanges', 'activityEvents', 'skillChecks', 'resumeOptions', 'observationOutcomes', 'sharedPlans', 'itemHandoffResults', 'publicEvents']) {
        context.lastTurnResult[field] = sanitizePromptDataForHiddenBeingNames(context.lastTurnResult[field], replacements);
      }
      if (context.vitalCondition) context.vitalCondition.requiredNarration = toArray(context.vitalCondition.requiredNarration)
        .map(text => sanitizeNarrationTextForHiddenBeingNames(text, replacements));
      if (result.sessionData.interactionState) {
        // Committed NPC speech is evidence, including names just introduced.
        result.sessionData.interactionState.messages = toArray(result.sessionData.interactionState.messages).map(message =>
          message.role === 'npc' ? message : sanitizePromptDataForHiddenBeingNames(message, replacements));
        result.sessionData.interactionState.history = result.sessionData.interactionState.messages;
      }
    }
    let suppliedPlayer = toArray(result.hazardResourceUses).reduce(applyConsumptionToInventory,
      applyConsumptionToInventory(activePlayer, result.consumptionResult));
    suppliedPlayer = applyItemHandoffsToInventory(suppliedPlayer, result.itemHandoffResults);
    const character = mergePlayVitals(suppliedPlayer, context);
    setGameplayContext(context);
    setNpcRoutineState(result.sessionData.npcRoutineState || null);
    setPlayerCharacter(character);
    setExplorerLocation(result.locationData.currentLocation);
    setMovementState(result.sessionData.movementState);
    setCombatState(result.sessionData.combatState);
    setInteractionState({ ...result.sessionData.interactionState, isSubmitting: true });
    if (snapshot?.currentPlaceContext) setCurrentPlaceContext(snapshot.currentPlaceContext);
    if (typeof onCharacterRefresh === 'function') await onCharacterRefresh(character);
    return { result, context, character, combat: result.sessionData.combatState, knownBeingNameIds: committedKnownBeingNameIdSet };
  }, [
    playerCharacter,
    playerCharacterId,
    onCharacterRefresh,
    pcKnownBeingNameIdSet,
  ]);

  const projectRoutineContext = useCallback(async (placeContext, location, movement, prepared) => {
    if (!prepared?.finalized) throw new Error('The server did not return a completed turn. Reload the saved session.');
    const preview = prepared;
    const hydrated = await hydrateRoutinePlaceContext(placeContext, preview.npcRoutineState, location, movement.placeId,
      routineBeingProfilesRef.current, loadBeingDetailsByReference);
    return { ...hydrated, activityInterruption: preview.activityInterruption,
      previewTimeMinutes: preview.currentTimeMinutes, hazardContext: preview.hazardContext, hazardEvents: preview.hazardEvents };
  }, []);

  const loadSavedPlaceImage = useCallback(async (placeId) => {
    const normalizedPlaceId = String(placeId ?? '').trim();
    if (!normalizedPlaceId || placeImageRequestsRef.current.has(normalizedPlaceId)) {
      return;
    }

    placeImageRequestsRef.current.add(normalizedPlaceId);
    setPlaceImagesById((current) => ({
      ...current,
      [normalizedPlaceId]: current[normalizedPlaceId] ?? { src: '', isLoading: true, error: '' },
    }));

    try {
      const response = await backgroundReadsRef.current(`place:${normalizedPlaceId}`, () => callCdo(cdoPlace.selectApproachImage.bind(cdoPlace), normalizedPlaceId));
      setPlaceImagesById((current) => ({
        ...current,
        [normalizedPlaceId]: { src: toDataImage(response), isLoading: false, error: '' },
      }));
    } catch (error) {
      setPlaceImagesById((current) => ({
        ...current,
        [normalizedPlaceId]: {
          src: '',
          isLoading: false,
          error: error?.message || 'Failed to load place image.',
        },
      }));
    }
  }, [callCdo]);

  const loadPortraitRelationships = useCallback(beingId => callCdo(cdoGameplay.relationships.bind(cdoGameplay), {
    playerId: playerCharacterId, beingId,
  }), [callCdo, playerCharacterId]);

  const loadSavedPlayerPortrait = useCallback(async (beingId) => {
    const normalizedBeingId = String(beingId ?? '').trim();
    if (!normalizedBeingId || playerPortraitRequestRef.current === normalizedBeingId) {
      return;
    }

    playerPortraitRequestRef.current = normalizedBeingId;
    setPlayerPortrait({ src: '', isLoading: true, error: '' });

    try {
      const response = await backgroundReadsRef.current(`portrait:${normalizedBeingId}`, () => callCdo(cdoBeing.selectPortraitImage.bind(cdoBeing), normalizedBeingId));
      setPlayerPortrait({ src: toDataImage(response), isLoading: false, error: '' });
    } catch (error) {
      setPlayerPortrait({
        src: '',
        isLoading: false,
        error: error?.message || 'Failed to load portrait.',
      });
    }
  }, [callCdo]);

  const loadSavedBeingPortrait = useCallback(async (being) => {
    const backgroundReads = backgroundReadsRef.current;
    const normalizedBeingId = getEntityId(being);
    if (!normalizedBeingId || beingPortraitRequestsRef.current.has(normalizedBeingId)) {
      return;
    }

    beingPortraitRequestsRef.current.add(normalizedBeingId);
    setBeingPortraitsById((current) => ({
      ...current,
      [normalizedBeingId]: current[normalizedBeingId] ?? { src: '', isLoading: true, isGenerating: false, error: '' },
    }));

    try {
      const portrait = await loadOrGenerateBeingPortrait({
        being,
        loadPortrait: (beingId) => backgroundReads(`portrait:${beingId}`, () => callCdo(cdoBeing.selectPortraitImage.bind(cdoBeing), beingId)),
        generatePortrait: (payload) => {
          if (backgroundReads.isDisposed()) throw new Error('Scene loading cancelled.');
          setBeingPortraitsById((current) => ({
            ...current,
            [normalizedBeingId]: { src: '', isLoading: false, isGenerating: true, error: '' },
          }));
          return callPlayAI(cdoAI.generateImage.bind(cdoAI), payload);
        },
        savePortrait: (payload) => {
          if (backgroundReads.isDisposed()) throw new Error('Scene loading cancelled.');
          return callCdo(cdoBeing.savePortraitImage.bind(cdoBeing), payload);
        },
      });

      if (!portrait.wasGenerated) {
        setBeingPortraitsById((current) => ({
          ...current,
          [normalizedBeingId]: { src: portrait.src, isLoading: false, isGenerating: false, error: '' },
        }));
        return;
      }

      setBeingPortraitsById((current) => ({
        ...current,
        [normalizedBeingId]: {
          src: portrait.src,
          isLoading: false,
          isGenerating: false,
          error: '',
        },
      }));
    } catch (error) {
      setBeingPortraitsById((current) => ({
        ...current,
        [normalizedBeingId]: {
          src: current[normalizedBeingId]?.src || '',
          isLoading: false,
          isGenerating: false,
          error: error?.message || 'Failed to load portrait.',
        },
      }));
    }
  }, [callCdo, callPlayAI]);

  const openPlaceMap = useCallback((placeId) => {
    const normalizedPlaceId = String(placeId ?? '').trim();
    if (!normalizedPlaceId) {
      return;
    }

    setMapModalPlaceId(normalizedPlaceId);
    const place = uniqueById([
      ...toArray(knownScenePlaces),
      ...toArray(activePlaceCards),
    ]).find((entry) => getEntityId(entry) === normalizedPlaceId);
    setPlaceMapZoom(/^(world|continent|nation|country|region)$/i.test(place?.placeType || '')
      ? REGIONAL_MAP_INITIAL_ZOOM : 1);
  }, [activePlaceCards, knownScenePlaces]);

  const closePlaceMap = useCallback(() => {
    setMapModalPlaceId('');
    setPlaceMapZoom(1);
  }, []);

  const zoomPlaceMapIn = useCallback(() => {
    setPlaceMapZoom((current) =>
      Math.min(PLACE_OVERVIEW_MAX_ZOOM, current * PLACE_OVERVIEW_ZOOM_STEP)
    );
  }, []);

  const zoomPlaceMapOut = useCallback(() => {
    setPlaceMapZoom((current) =>
      Math.max(placeMapPlan?.playerCentered ? REGIONAL_MAP_MIN_ZOOM : PLACE_OVERVIEW_MIN_ZOOM, current / PLACE_OVERVIEW_ZOOM_STEP)
    );
  }, [placeMapPlan?.playerCentered]);

  useEffect(() => {
    let isCancelled = false;
    let bootstrapRequest;
    const loadingStart = performance.now();
    const backgroundReads = createBackgroundReads(2);
    backgroundReadsRef.current = backgroundReads;

    const loadContext = async () => {
      setIsBootstrapping(true);
      setStatusMessage('Loading...');
      setErrorMessage('');
      setInteractionState(createEmptyInteractionState());
      setPendingTradeConfirmation(null);
      setCurrentPlaceContext(null);
      setHydratedPlaceDetailsById({});
      setKnownScenePlaces([]);
      placeKnowledgeRef.current = { knowledge: {}, places: [] };
      setPlaceKnowledge(placeKnowledgeRef.current);
      setExplorerLocation(null);
      setMovementState(createEmptyMovementState());
      setCombatState(null);
      setNarrationContext(null);
      setPlaceImagesById({});
      setPlayerPortrait({ src: '', isLoading: false, error: '' });
      setBeingPortraitsById({});
      setPcKnownBeingNameIds({});
      placeImageRequestsRef.current.clear();
      playerPortraitRequestRef.current = '';
      beingPortraitRequestsRef.current.clear();
      placeDetailRequestsRef.current.clear();
      nameKnowledgeRequestsRef.current.clear();

      try {
        const cookieCharacterId = startupPlayerId;
        if (!cookieCharacterId) throw new Error('Select a character before loading the game.');
        bootstrapRequest = acquirePlayBootstrap(bootstrapIdentity,
          done => cdoGameplay.bootstrap({ playerId: cookieCharacterId }, done));
        const bootstrap = await bootstrapRequest.promise;
        if (isCancelled) return;
        const expanded = expandBootstrapPlaces(bootstrap);
        const nextPlaceChain = expanded.placeChain;
        const nextPlayerCharacter = bootstrap.player;
        const nextCurrentPlace = nextPlaceChain[SETTLEMENT_INDEX];
        const nextGameplayContext = { ...bootstrap.context.sessionData?.gameplayContext, ...bootstrap.context };
        const initialRoutineState = nextGameplayContext.sessionData?.npcRoutineState || null;
        const restoredSession = getRestoredGameplaySessionState(nextGameplayContext);
        let activeCurrentPlaceContext = expanded.currentPlaceContext;
        const nextKnownScenePlacesWithInfrastructure = expanded.places;
        const nextInfrastructurePlaces = expanded.places.filter(isRoadOrRiverOverviewPlace);
        const nextExplorerLocation = restoredSession?.currentLocation || bootstrap.initialLocation || resolveExplorerStartLocation(
          nextPlayerCharacter, nextCurrentPlace, nextInfrastructurePlaces);
        const initialPlaceKnowledge = restoredSession?.currentLocation
          ? { ...bootstrap.placeKnowledge, places: expanded.places }
          : await callCdo(cdoGameplay.placeKnowledge.bind(cdoGameplay), {
            deferAssessment: true, playerId: cookieCharacterId, currentLocation: nextExplorerLocation,
            currentTimeMinutes: nextGameplayContext.sessionData?.gameplayContext?.currentTimeMinutes,
            placeChain: nextPlaceChain, currentPlace: activeCurrentPlaceContext, scenePlaces: expanded.places,
          });
        if (isCancelled) return;
        acceptPlaceKnowledge(initialPlaceKnowledge);
        const bootstrapKnownNames = Object.fromEntries(Object.entries(bootstrap.knownBeingNameIds || {}).filter(([, known]) => known));
        Object.keys(bootstrap.knownBeingNameIds || {}).forEach(id => nameKnowledgeRequestsRef.current.add(id));
        onBootstrapRef.current?.(bootstrap);
        const nextMovementState = restoredSession
          ? normalizeMovementSessionState(restoredSession.movementState, restoredSession.placeId || nextExplorerLocation?.placeId)
          : normalizeMovementSessionState(createEmptyMovementState(), nextExplorerLocation?.placeId || getEntityId(activeCurrentPlaceContext?.place));
        const nextMovementInteriorPlaceId = getMovementInteriorPlaceId({
          movementState: nextMovementState,
          settlementScene: buildMinimalSettlementScene(nextPlaceChain, nextKnownScenePlacesWithInfrastructure),
        });
        routineBeingProfilesRef.current = bootstrap.profilesById || Object.fromEntries(
          Object.values(activeRoutineScene(initialRoutineState)?.beingsById || {}).map(npc => [npc.being.idEntity, npc.being]));
        activeCurrentPlaceContext = await hydrateRoutinePlaceContext(activeCurrentPlaceContext, initialRoutineState,
          nextExplorerLocation, nextMovementState.placeId, routineBeingProfilesRef.current, loadBeingDetailsByReference);
        setNpcRoutineState(initialRoutineState);
        const nextGameplayContextForState = restoredSession?.gameplayContext || nextGameplayContext;
        const nextCombatState = restoredSession?.combatState || null;
        const restoredInteractionState = restoredSession?.interactionState;
        const restoredMessages = toArray(initialPlaceKnowledge.history ?? restoredInteractionState?.messages);
        const restoredHistory = toArray(initialPlaceKnowledge.history ?? restoredInteractionState?.history);
        const restoredRecentNarration = initialPlaceKnowledge.history ? buildRecentNarration(restoredMessages) : toArray(restoredInteractionState?.recentNarration);
        if (restoredMessages.length > 0 || restoredSession?.pendingResponse) {
          const nextInteractionState = {
            ...createEmptyInteractionState(),
            ...restoredInteractionState,
            messages: restoredMessages,
            history: restoredHistory.length > 0 ? restoredHistory : restoredMessages,
            recentNarration: restoredRecentNarration.length > 0
              ? restoredRecentNarration
              : buildRecentNarration(restoredMessages),
            isSubmitting: false,
            error: '',
          };

          if (isCancelled) {
            return;
          }

          setPlaceChain(nextPlaceChain);
          setPlayerCharacter(getEffectiveCombatExplorer(mergePlayVitals(nextPlayerCharacter, nextGameplayContextForState), nextCombatState) || null);
          setGameplayContext(nextGameplayContextForState || null);
          setCurrentPlaceContext(activeCurrentPlaceContext);
          setKnownScenePlaces(nextKnownScenePlacesWithInfrastructure);
          setExplorerLocation(nextExplorerLocation);
          setPcKnownBeingNameIds(bootstrapKnownNames);
          setMovementState(nextMovementState);
          setCombatState(nextCombatState);
          setNarrationContext(restoredSession?.narrationContext || null);
          setInteractionState(restorePendingResponse(nextInteractionState, restoredSession?.pendingResponse));
          setPendingTradeConfirmation(restoredSession?.pendingTradeConfirmation || null);
          setStatusMessage('');
          return;
        }

        const initialVisibleBeings = uniqueById([
          ...toArray(activeCurrentPlaceContext?.beingsInLocation),
          ...getNearbyHydratedSubPlaceOccupants({
            currentPlaceContext: activeCurrentPlaceContext,
            currentLocation: nextExplorerLocation,
            currentInteriorPlaceId: nextMovementInteriorPlaceId,
          }),
        ]);
        const nextKnownBeingNameIds = bootstrapKnownNames;
        const nextKnownBeingNameIdSet = new Set(Object.keys(nextKnownBeingNameIds));
        const maskedInitialPlaceContext = maskPlaceContextForPc(
          activeCurrentPlaceContext,
          nextKnownBeingNameIdSet,
          getEntityId(nextPlayerCharacter)
        );
        const initialHiddenNameReplacements = buildHiddenBeingNameReplacements(
          initialVisibleBeings,
          nextKnownBeingNameIdSet,
          getEntityId(nextPlayerCharacter)
        );

        if (!isCancelled) {
          setStatusMessage('Writing opening narration...');
        }

        const initialNarrationPayload = buildPlayNarrationPayload({
          explorer: nextPlayerCharacter,
          currentPlaceContext: maskedInitialPlaceContext,
          scenePlaces: nextKnownScenePlacesWithInfrastructure,
          gameplayContext: nextGameplayContextForState,
          currentLocation: nextExplorerLocation,
          currentInteriorPlaceId: nextMovementInteriorPlaceId,
          reason: 'initial_scene',
        });
        const initialNarration = await callPlayAI(cdoAI.playNarration.bind(cdoAI), initialNarrationPayload);
        const initialNarrationText = sanitizeNarrationTextForHiddenBeingNames(
          getNarrationDescription(initialNarration),
          initialHiddenNameReplacements
        );

        if (!initialNarrationText) {
          throw new Error('Narration AI returned no opening description.');
        }

        const initialMessages = [{ role: 'assistant', content: initialNarrationText }];
        const initialRecentNarration = buildRecentNarration(initialMessages);
        const initialNarrationForState = {
          ...initialNarration,
          description: initialNarrationText,
        };
        const initialNarrationContext = {
          ...initialNarrationPayload,
          history: initialMessages,
          recentNarration: initialRecentNarration,
          lastNarration: initialNarrationForState,
        };
        const initialInteractionState = {
          ...createEmptyInteractionState(),
          messages: initialMessages,
          history: initialMessages,
          recentNarration: initialRecentNarration,
          lastNarration: initialNarrationForState,
        };

        await saveGameplaySessionSnapshot({
          playerId: getEntityId(nextPlayerCharacter),
          gameplayContext: nextGameplayContextForState,
          currentLocation: nextExplorerLocation,
          movementState: nextMovementState,
          combatState: null,
          interactionState: initialInteractionState,
          narrationContext: initialNarrationContext,
          pendingTradeConfirmation: null,
          currentPlaceContext: activeCurrentPlaceContext,
        });

        if (isCancelled) {
          return;
        }

        setPlaceChain(nextPlaceChain);
        setPlayerCharacter(getEffectiveCombatExplorer(mergePlayVitals(nextPlayerCharacter, nextGameplayContextForState), nextCombatState) || null);
        setGameplayContext(nextGameplayContextForState || null);
        setCurrentPlaceContext(activeCurrentPlaceContext);
        setKnownScenePlaces(nextKnownScenePlacesWithInfrastructure);
        setExplorerLocation(nextExplorerLocation);
        setPcKnownBeingNameIds(nextKnownBeingNameIds);
        setMovementState(nextMovementState);
        setCombatState(null);
        setNarrationContext(initialNarrationContext);
        setInteractionState(initialInteractionState);
        setStatusMessage('');
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(error?.message || 'Failed to load Play2 context.');
          setStatusMessage('');
        }
      } finally {
        if (!isCancelled) {
          setIsBootstrapping(false);
          const selectedAt = performance.getEntriesByName?.('adenrel.play.selected').slice(-1)[0]?.startTime;
          performance.measure?.('adenrel.play.ready', { start: selectedAt ?? loadingStart });
          if (canUseDebug) console.debug('Play load ready', { durationMs: Math.round(performance.now() - loadingStart) });
        }
      }
    };

    loadContext();

    return () => {
      isCancelled = true;
      bootstrapRequest?.release();
      backgroundReads.dispose();
    };
  }, [acceptPlaceKnowledge, callCdo, callPlayAI, startupPlayerId, bootstrapIdentity, canUseDebug]);

  useEffect(() => {
    if (
      isBootstrapping ||
      !currentPlaceContext?.place ||
      !placeChain[SETTLEMENT_INDEX] ||
      !settlementScene
    ) {
      return undefined;
    }

    const rootPlace = placeChain[SETTLEMENT_INDEX];
    const rootPlaceId = getSettlementRootPlaceId(settlementScene) || getEntityId(rootPlace);
    const targetPlaceId = movementInteriorPlaceId || rootPlaceId;
    const currentContextPlaceId = getEntityId(currentPlaceContext.place);
    if (!targetPlaceId || currentContextPlaceId === targetPlaceId) {
      return undefined;
    }

    let isCancelled = false;
    const reconcilePlaceContext = async () => {
      try {
        const targetPlace = targetPlaceId === rootPlaceId
          ? rootPlace
          : findLoadedPlaceById(currentPlaceContext, targetPlaceId) ||
            knownScenePlaces.find((place) => getEntityId(place) === targetPlaceId) ||
            await callCdo(cdoPlace.select.bind(cdoPlace), targetPlaceId);
        if (!targetPlace?.idEntity) {
          return;
        }

        const nextPlaceContext = await loadCurrentPlaceContext(targetPlace);
        if (isCancelled) {
          return;
        }

        setCurrentPlaceContext(nextPlaceContext);
        setKnownScenePlaces((current) => uniqueById([
          ...toArray(current),
          ...buildKnownScenePlaces(placeChain, nextPlaceContext),
        ]));
      } catch (error) {
        console.warn('Unable to reconcile Play2 place context with movement placeId.', error);
      }
    };

    reconcilePlaceContext();

    return () => {
      isCancelled = true;
    };
  }, [
    currentPlaceContext,
    isBootstrapping,
    knownScenePlaces,
    movementInteriorPlaceId,
    placeChain,
    settlementScene,
    callCdo,
  ]);

  useEffect(() => {
    activePlaceCards.forEach((place) => {
      const placeId = String(place?.idEntity ?? '').trim();
      if (placeId) {
        loadSavedPlaceImage(placeId);
      }
    });
  }, [activePlaceCards, loadSavedPlaceImage]);

  useEffect(() => {
    if (!playerCharacterId || rawActiveBeingCards.length === 0) {
      return undefined;
    }

    let isCancelled = false;
    const missingBeingIds = rawActiveBeingCards
      .map(getEntityId)
      .filter((beingId) =>
        beingId &&
        beingId !== playerCharacterId &&
        pcKnownBeingNameIds[beingId] !== true &&
        !nameKnowledgeRequestsRef.current.has(beingId)
      );

    if (missingBeingIds.length === 0) {
      return undefined;
    }

    missingBeingIds.forEach((beingId) => nameKnowledgeRequestsRef.current.add(beingId));
    Promise.all(missingBeingIds.map(async (beingId) => {
      try {
        const known = await callCdo(cdoEvent.pcKnowsBeingName.bind(cdoEvent), {
          playerId: playerCharacterId,
          beingId,
        });
        return { beingId, known: known === true };
      } catch {
        return { beingId, known: false };
      }
    })).then((results) => {
      if (isCancelled) {
        return;
      }

      const knownIds = results.filter((entry) => entry.known).map((entry) => entry.beingId);
      if (knownIds.length === 0) {
        return;
      }

      setPcKnownBeingNameIds((current) => ({
        ...current,
        ...Object.fromEntries(knownIds.map((beingId) => [beingId, true])),
      }));
    });

    return () => {
      isCancelled = true;
    };
  }, [pcKnownBeingNameIds, playerCharacterId, rawActiveBeingCards, callCdo]);

  useEffect(() => {
    if (!currentPlaceContext?.place || !explorerLocation?.location || currentPlaceContext.place.gameplayGeometryLoaded) {
      return undefined;
    }

    let isCancelled = false;
    const point = normalizeLocation(explorerLocation.location);
    const childPlacesToLoad = toArray(currentPlaceContext.subPlaces).filter((place) => {
      const placeId = getEntityId(place);
      return (
        placeId &&
        !hydratedPlaceDetailsById[placeId] &&
        !placeDetailRequestsRef.current.has(placeId)
      );
    });

    loadPlaceDetailsInProximityOrder({
      places: childPlacesToLoad,
      point,
      shouldContinue: () => !isCancelled,
      onPlaceStart: (place) => {
        const placeId = getEntityId(place);
        if (!placeId || hydratedPlaceDetailsById[placeId] || placeDetailRequestsRef.current.has(placeId)) {
          return false;
        }

        placeDetailRequestsRef.current.add(placeId);
        return true;
      },
      onPlaceLoaded: (loadedPlace) => {
        const loadedPlaceId = getEntityId(loadedPlace);
        if (!loadedPlaceId) {
          return;
        }

        setHydratedPlaceDetailsById((current) => ({
          ...current,
          [loadedPlaceId]: loadedPlace,
        }));
        setKnownScenePlaces((current) =>
          mergeHydratedPlaceDetails(uniqueById([...toArray(current), loadedPlace]), { [loadedPlaceId]: loadedPlace })
        );
      },
    });

    return () => {
      isCancelled = true;
    };
  }, [
    currentPlaceContext?.place,
    currentPlaceContext?.subPlaces,
    explorerLocation?.location,
    hydratedPlaceDetailsById,
  ]);

  useEffect(() => {
    if (playerCharacterId) {
      loadSavedPlayerPortrait(playerCharacterId);
    }
  }, [loadSavedPlayerPortrait, playerCharacterId]);

  useEffect(() => {
    rawActiveBeingCards.forEach((being) => {
      const beingId = getEntityId(being);
      if (beingId) {
        loadSavedBeingPortrait(being);
      }
    });
  }, [loadSavedBeingPortrait, rawActiveBeingCards]);

  useEffect(() => {
    // Narration can replace a hidden fallback without changing the stored message count.
    const chatLog = chatLogRef.current;
    if (!chatLog) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      chatLog.scrollTop = chatLog.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [visibleMessages]);

  useEffect(() => {
    if (interactionState.isSubmitting || isCharacterSheetOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      actionInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [interactionState.isSubmitting, interactionState.messages.length, isCharacterSheetOpen]);

  const submitDraftInteraction = useCallback(async (draft, executionControl = null) => {
    const instruction = draft.trim();
    if (!instruction || interactionState.isSubmitting || pendingTradeConfirmation || gameplayContext?.health?.combatStatus === 'dead') {
      return;
    }

    if (!playerCharacterId) {
      setInteractionState((current) => ({
        ...current,
        error: 'Player character is not loaded.',
      }));
      return;
    }

    const playPlaceContext = effectiveCurrentPlaceContext || currentPlaceContext;

    if (!playPlaceContext || !gameplayContext || !explorerLocation || !settlementScene) {
      setInteractionState((current) => ({
        ...current,
        error: 'Play context is still loading.',
      }));
      return;
    }

    const aftermathRetry = combatState?.aftermathPending ? combatState.aftermathRequest : null;
    const previousAttempt = interactionState.messages.slice(-1)[0];
    const retryingAction = previousAttempt?.pendingAction
      && JSON.stringify(previousAttempt.executionControl || null) === JSON.stringify(executionControl || null)
      && placeIdentityRef.current.sanitizeText(previousAttempt.content).trim().replace(/\s+/g, ' ').toLowerCase()
        === placeIdentityRef.current.sanitizeText(instruction).trim().replace(/\s+/g, ' ').toLowerCase();
    const messagesBeforeTurn = (retryingAction ? interactionState.messages.slice(0, -1) : interactionState.messages)
      .filter(message => !message.pendingAction)
      .map(({ pendingNarration, ...message }) => message);
    const submittedText = aftermathRetry?.text || (retryingAction && previousAttempt.requestText ? previousAttempt.requestText : instruction);
    const responseRetry = retryingAction && previousAttempt.responseRetry ? { ...previousAttempt.responseRetry,
      requestId: previousAttempt.responseRetry.requestId || window.crypto.randomUUID() } : null;
    const userMessage = { role: 'user', content: instruction,
      ...(responseRetry ? { responseRetry } : {}),
      ...(executionControl ? { executionControl } : {}), turnId: aftermathRetry?.turnId || (retryingAction ? previousAttempt.turnId
      : window.crypto?.randomUUID?.() || `turn-${Date.now()}-${Math.random().toString(36).slice(2)}`) };

    setActionProgress({ turnId: userMessage.turnId, phase: 'preparing' });
    setInteractionState((current) => ({
      ...current,
      draft: '',
      isSubmitting: true,
      error: '',
      messages: [...messagesBeforeTurn, { ...userMessage, pendingAction: true }],
    }));

    try {
      const nearbyBeings = uniqueById([
        ...toArray(playPlaceContext?.beingsInLocation),
        ...getNearbyHydratedSubPlaceOccupants({
          currentPlaceContext: playPlaceContext,
          currentLocation: explorerLocation,
          currentInteriorPlaceId: activeInteriorPlaceId,
        }),
      ]);
      const turnHiddenNameReplacements = buildHiddenBeingNameReplacements(
        uniqueById([...nearbyBeings, ...rawActiveBeingCards]),
        pcKnownBeingNameIdSet,
        playerCharacterId
      );
      const maskedPlayPlaceContext = maskPlaceContextForPc(playPlaceContext, pcKnownBeingNameIdSet, playerCharacterId);
      const maskedNearbyBeings = maskBeingsForPc(nearbyBeings, pcKnownBeingNameIdSet, playerCharacterId);
      const sanitizedMessagesBeforeTurn = sanitizeMessagesForHiddenBeingNames(
        messagesBeforeTurn,
        turnHiddenNameReplacements
      );
      const sanitizedRecentNarration = sanitizeMessagesForHiddenBeingNames(
        interactionState.recentNarration,
        turnHiddenNameReplacements
      );
      const movementFrame = buildMovementFrame({
        explorerLocation,
        movementState,
        currentInteriorPlaceId: activeInteriorPlaceId,
        currentPlaceContext: maskedPlayPlaceContext,
        placeChain,
        knownScenePlaces,
      });
      const combatFrame = buildCombatFrame({
        playerCharacter,
        combatState,
        nearbyBeings: maskedNearbyBeings,
        explorerLocation,
        movementState,
        currentInteriorPlaceId: activeInteriorPlaceId,
        currentPlaceContext: maskedPlayPlaceContext,
        placeChain,
        knownScenePlaces,
      });
      const commerceFrame = buildCommerceFrame({
        currentPlaceContext: maskedPlayPlaceContext,
        nearbyBeings: maskedNearbyBeings,
      });
      const socialFrameNearbyTargets = getSocialFrameTargets(maskedNearbyBeings);
      const socialFrameCurrentTarget = resolveCurrentSocialFrameTarget(
        interactionState.currentSocialTarget,
        socialFrameNearbyTargets
      );
      const executionPreview = await callPlayAI((data, done) => (actionRequestRef.current = cdoGameplay.resolveAction(data, done, progress =>
        setActionProgress(current => ({ ...current, progress: { ...current?.progress, ...progress } })))), {
        playerId: playerCharacterId, baseTurnId: aftermathRetry?.baseTurnId ?? gameplayContext?.turnId ?? '',
        turnId: userMessage.turnId,
        text: submittedText,
        ...(responseRetry ? { responseRetry } : {}),
        ...(executionControl ? { executionControl } : {}),
        history: sanitizedMessagesBeforeTurn,
        presentation: { currentPlace: summarizePlaceForNarration(maskedPlayPlaceContext?.place),
          places: placeChain.map(place => summarizePlaceForNarration(placeIdentityRef.current.sanitize(place))) },
        recentNarration: sanitizedRecentNarration,
        identityPolicy: buildNpcResponseIdentityPolicy(nearbyBeings, pcKnownBeingNameIdSet, playerCharacterId),
        movementFrame,
        combatFrame,
        commerceFrame,
        timeFrame: buildPlayerActionTimeFrame(gameplayContext),
        hazardFrame: gameplayContext.hazardContext,
        socialFrame: {
          currentTarget: socialFrameCurrentTarget,
          nearbyTargets: socialFrameNearbyTargets,
        },
      });
      const rawPlayerAction = executionPreview.playerAction;
      const playerAction = rawPlayerAction;
      if (playerAction?.overrideResponse?.message) {
        const systemMessage = {
          role: 'system',
          content: playerAction.overrideResponse.message,
          responseType: playerAction.overrideResponse.type || 'invalid_intent',
          severity: playerAction.overrideResponse.severity || 'warning',
        };
        const finalMessages = [
          ...messagesBeforeTurn,
          userMessage,
          systemMessage,
        ];
        const nextInteractionState = {
          ...interactionState,
          draft: '',
          isSubmitting: false,
          error: '',
          lastPlayerAction: playerAction,
          npcResponseGroups: [],
          npcPlaceholder: '',
          narrationPlaceholder: '',
          lastNarration: null,
          history: finalMessages,
          recentNarration: buildRecentNarration(finalMessages),
          rollData: null,
          tradeResults: [],
          contractResults: [],
          combatResult: null,
          combatParticipationResult: null,
          currentSocialTarget: socialFrameCurrentTarget,
          messages: finalMessages,
        };

        setInteractionState(nextInteractionState);
        return;
      }
      let movementResult = null;
      let combatResult = null;
      let combatParticipationResult = null;
      let spellAcquisitionResult = executionPreview.spellAcquisitionResult || null;
      let nextExplorerLocation = explorerLocation;
      let nextMovementState = movementState;
      let nextCombatState = combatState?.active === true ? combatState : null;
      let nextGameplayContext = gameplayContext;
      let activePlaceContext = playPlaceContext;
      let activePlayerCharacter = playerCharacter;
      const isCombatRound = Boolean(executionPreview.combatResult);
      if (gameplayContext?.health?.combatStatus === 'incapacitated' && (
        getPlayerActionTimeAdvanceMinutes(playerAction) <= 0 ||
        getResultIntents(playerAction).some(entry => !['wait', 'rest', 'sleep'].includes(entry.intent))
      )) throw new Error('You are unconscious and cannot act. You can only allow time to pass.');

      if (!executionPreview.finalized) throw new Error('The server did not return a completed turn. Reload the saved session.');

      if (executionPreview.finalized) {
        nextMovementState = executionPreview.movementState || nextMovementState;
        nextExplorerLocation = executionPreview.currentLocation || nextExplorerLocation;
        nextGameplayContext = { ...nextGameplayContext, currentTimeMinutes: executionPreview.currentTimeMinutes,
          currentTimeLabel: formatGameTime(executionPreview.currentTimeMinutes), spatialFrame: executionPreview.spatialFrame };
      }
      if (executionPreview.movementResult) {
        movementResult = executionPreview.movementResult;
        nextExplorerLocation = movementResult.currentLocation || explorerLocation;
        nextMovementState = {
          ...nextMovementState,
          placeId: movementResult.placeId || '',
          pendingRoute: movementResult.actionComplete ? [] : toArray(movementResult.pendingRoute),
          lastMovementResult: movementResult,
          lastMovementTarget: movementResult.movementTarget || playerAction.movementTarget || null,
        };
        const movementResultInteriorPlaceId = getMovementInteriorPlaceId({
          movementState: nextMovementState,
          settlementScene,
        });
        activePlaceContext = await loadPlaceContextAfterMovement({
          movementResult,
          currentPlaceContext: playPlaceContext,
          knownScenePlaces,
          rootPlace: placeChain[SETTLEMENT_INDEX],
          previousInteriorPlaceId: activeInteriorPlaceId,
          nextInteriorPlaceId: movementResultInteriorPlaceId,
          npcRoutineState,
        });
      }

      const nextMovementInteriorPlaceId = getMovementInteriorPlaceId({
        movementState: nextMovementState,
        settlementScene,
      });
      if (!isCombatRound) activePlaceContext = await projectRoutineContext(activePlaceContext,
        nextExplorerLocation, nextMovementState, executionPreview);
      if (!isCombatRound && activePlaceContext.activityInterruption) {
        const actualMinute = activePlaceContext.previewTimeMinutes;
        playerAction.survivalActivity = { ...playerAction.survivalActivity,
          requestedMinutes: playerAction.survivalActivity?.requestedMinutes ?? executionPreview.movementResult?.requestedMinutes ?? playerAction.waitDurationMinutes ?? 0,
          durationMinutes: actualMinute - gameplayContext.currentTimeMinutes, interrupted: true,
          interruption: activePlaceContext.activityInterruption };
        nextGameplayContext = { ...nextGameplayContext, currentTimeMinutes: actualMinute, currentTimeLabel: formatGameTime(actualMinute) };
      }
      nextGameplayContext = { ...nextGameplayContext, hazardContext: activePlaceContext.hazardContext ?? nextGameplayContext.hazardContext };
      const activeNearbyBeings = uniqueById([
        ...toArray(activePlaceContext?.beingsInLocation),
        ...getNearbyHydratedSubPlaceOccupants({
          currentPlaceContext: activePlaceContext,
          currentLocation: nextExplorerLocation,
          currentInteriorPlaceId: nextMovementInteriorPlaceId,
        }),
      ]);

      if (isCombatRound) {
        combatParticipationResult = executionPreview.combatParticipationResult;
        combatResult = executionPreview.combatResult;
        nextCombatState = combatResult?.combatState
          ? {
            ...combatResult.combatState,
            active: combatResult.active === true,
          }
          : null;
        nextExplorerLocation = buildCombatExplorerLocation({
          combatResult,
          fallbackLocation: nextExplorerLocation,
          movementState: nextMovementState,
        });

        const combatTimeMinutes = Number(combatResult?.currentTimeMinutes);
        if (Number.isFinite(combatTimeMinutes)) {
          nextGameplayContext = {
            ...nextGameplayContext,
            currentTimeMinutes: combatTimeMinutes,
            currentTimeLabel: formatGameTime(combatTimeMinutes),
          };
        }

        activePlayerCharacter = mergeCombatantIntoBeing(
          activePlayerCharacter,
          getCombatStateCombatant(nextCombatState, playerCharacterId)
        );
        activePlaceContext = await projectRoutineContext(activePlaceContext,
          nextExplorerLocation, nextMovementState, executionPreview);
        nextGameplayContext = { ...nextGameplayContext, hazardContext: activePlaceContext.hazardContext ?? nextGameplayContext.hazardContext };
      }

      const primaryFocus = findPrimaryFocusForAction(playerAction, activeNearbyBeings);
      const npcResponseGroups = toArray(executionPreview.npcResponseGroups);
      const responseIntents = npcResponseGroups.map(group => group.intent);
      let npcPlaceholder = '';
      const tradeResults = toArray(executionPreview.tradeResults);
      const contractResults = toArray(executionPreview.contractResults);

      const finalized = await applyCompletedTurn({
        gameplayContext: nextGameplayContext,
        currentLocation: nextExplorerLocation,
        movementState: nextMovementState,
        combatState: nextCombatState,
        interactionState: { ...interactionState, draft: '', messages: [...messagesBeforeTurn, userMessage], lastPlayerAction: playerAction },
        currentPlaceContext: activePlaceContext,
      }, executionPreview.finalized, executionPreview.playerCharacter || activePlayerCharacter);
      const committedKnownBeingNameIdSet = finalized.knownBeingNameIds;
      nextGameplayContext = finalized.context;
      spellAcquisitionResult = finalized.result.spellAcquisitionResult || spellAcquisitionResult;
      nextExplorerLocation = finalized.result.locationData.currentLocation;
      nextMovementState = finalized.result.sessionData.movementState;
      if (movementResult) movementResult = nextMovementState.lastMovementResult;
      activePlayerCharacter = finalized.character;
      nextCombatState = finalized.combat;
      activePlaceContext = applyRoutineStateToPlaceContext(activePlaceContext, finalized.result.sessionData.npcRoutineState,
        nextExplorerLocation, nextMovementState.placeId, routineBeingProfilesRef.current);

      if (tradeResults.length > 0 || contractResults.length > 0) {
        const tradeConfirmation = finalized.result.commerceConfirmation?.confirmation
          || executionPreview.commerceConfirmation?.confirmation || firstTransactionConfirmation(tradeResults, contractResults);

        if (tradeConfirmation) {
          const rollData = buildRollData({ npcResponseGroups, movementResult, combatResult, spellAcquisitionResult });
          const confirmationNearbyBeings = uniqueById([
            ...toArray(activePlaceContext?.beingsInLocation),
            ...getNearbyHydratedSubPlaceOccupants({
              currentPlaceContext: activePlaceContext,
              currentLocation: nextExplorerLocation,
              currentInteriorPlaceId: nextMovementInteriorPlaceId,
            }),
          ]);
          const maskedConfirmationNearbyBeings = maskBeingsForPc(
            confirmationNearbyBeings,
            committedKnownBeingNameIdSet,
            playerCharacterId
          );
          const confirmationSocialTargets = getSocialFrameTargets(maskedConfirmationNearbyBeings);
          const nextSocialTarget = resolveNextSocialTarget(playerAction, confirmationSocialTargets, npcResponseGroups, socialFrameCurrentTarget);
          const entityNameById = buildEntityNameById(
            [activePlayerCharacter],
            maskBeingsForPc(nearbyBeings, committedKnownBeingNameIdSet, playerCharacterId),
            maskedConfirmationNearbyBeings,
            [maskBeingIdentityForPc(primaryFocus, committedKnownBeingNameIdSet, playerCharacterId)],
            maskBeingsForPc(Object.values(nextCombatState?.combatantsById || {}), committedKnownBeingNameIdSet, playerCharacterId)
          );
          const npcSpeechMessages = combatResult
            ? getCombatSpeechMessages(combatResult, entityNameById, playerCharacterId, turnHiddenNameReplacements)
            : getNpcSpeechMessages(npcResponseGroups, await resolveNpcSpeakerNames({
              groups: npcResponseGroups, nameById: entityNameById,
              loadBeing: id => callCdo(cdoBeing.select.bind(cdoBeing), id),
              knownNameIds: committedKnownBeingNameIdSet, playerId: playerCharacterId,
            }));
          const previewMessages = appendNoVisibleOutcomeMessage([
            ...messagesBeforeTurn,
            userMessage,
            ...npcSpeechMessages,
          ], messagesBeforeTurn, buildNoVisibleOutcomeMessage({
            playerAction,
            responseIntents,
            nearbyBeings: confirmationNearbyBeings,
            npcResponseGroups,
          }));
          const recentNarration = buildRecentNarration(previewMessages);
          const nextInteractionState = {
            ...interactionState,
            draft: '',
            isSubmitting: false,
            error: '',
            lastPlayerAction: playerAction,
            npcResponseGroups,
            npcPlaceholder,
            narrationPlaceholder: '',
            history: previewMessages,
            recentNarration,
            rollData,
            tradeResults,
            contractResults,
            spellAcquisitionResult,
            spellAcquisitionState: spellAcquisitionResult?.nextAcquisitionState ?? interactionState.spellAcquisitionState ?? null,
            combatResult,
            combatParticipationResult,
            currentSocialTarget: nextSocialTarget,
            messages: previewMessages,
          };
          const nextPendingTradeConfirmation = {
            confirmation: tradeConfirmation,
            confirmationId: finalized.result.commerceConfirmation?.confirmationId || executionPreview.commerceConfirmation?.confirmationId,
            continuation: {
              instruction,
              messagesBeforeTurn,
              userMessage,
              sanitizedMessagesBeforeTurn,
              sanitizedRecentNarration,
              playerAction,
              movementResult,
              combatResult,
              combatParticipationResult,
              spellAcquisitionResult,
              npcResponseGroups,
              npcPlaceholder,
              nextExplorerLocation,
              nextMovementState,
              nextCombatState,
              nextGameplayContext,
              activePlaceContext,
              activePlayerCharacter,
              primaryFocus,
              currentSocialTarget: nextSocialTarget,
              rollData,
              previewMessages,
            },
          };

          setGameplayContext(nextGameplayContext);
          setPlayerCharacter(activePlayerCharacter || null);
          setCurrentPlaceContext(activePlaceContext);
          setKnownScenePlaces((current) => uniqueById([
            ...toArray(current),
            ...buildKnownScenePlaces(placeChain, activePlaceContext),
          ]));
          setExplorerLocation(nextExplorerLocation);
          setMovementState(nextMovementState);
          setCombatState(nextCombatState);
          setInteractionState(nextInteractionState);
          setPendingTradeConfirmation(nextPendingTradeConfirmation);
          return;
        }

        if (!executionPreview.playerCharacter && (tradeResults.some((result) => result?.executed === true) || contractResults.some((result) => result?.executed === true))) {
          activePlayerCharacter = mergePlayVitals(await callCdo(cdoBeing.select.bind(cdoBeing), playerCharacterId), nextGameplayContext);
          if (typeof onCharacterRefresh === 'function') {
            await onCharacterRefresh(activePlayerCharacter);
          }
          activePlaceContext = await loadCurrentPlaceContext(activePlaceContext.place, finalized.result.sessionData.npcRoutineState);
          activePlaceContext = await hydrateRoutinePlaceContext(activePlaceContext, finalized.result.sessionData.npcRoutineState,
            nextExplorerLocation, nextMovementState.placeId, routineBeingProfilesRef.current, loadBeingDetailsByReference, { refresh: true });
        }
      }

      const narrationNearbyBeings = uniqueById([
        ...toArray(activePlaceContext?.beingsInLocation),
        ...getNearbyHydratedSubPlaceOccupants({
          currentPlaceContext: activePlaceContext,
          currentLocation: nextExplorerLocation,
          currentInteriorPlaceId: nextMovementInteriorPlaceId,
        }),
      ]);
      const activeHiddenNameReplacements = buildHiddenBeingNameReplacements(
        narrationNearbyBeings,
        committedKnownBeingNameIdSet,
        playerCharacterId
      );
      const maskedActivePlaceContext = maskPlaceContextForPc(
        activePlaceContext,
        committedKnownBeingNameIdSet,
        playerCharacterId
      );
      const rollData = buildRollData({ npcResponseGroups, movementResult, combatResult, spellAcquisitionResult });
      const narrationPayload = buildPlayNarrationPayload({
        explorer: activePlayerCharacter,
        currentPlaceContext: maskedActivePlaceContext,
        scenePlaces: uniqueById([
          ...toArray(knownScenePlaces),
          ...buildKnownScenePlaces(placeChain, activePlaceContext),
        ]),
        gameplayContext: nextGameplayContext,
        currentLocation: nextExplorerLocation,
        currentInteriorPlaceId: nextMovementInteriorPlaceId,
        history: [...sanitizedMessagesBeforeTurn, userMessage],
        recentNarration: sanitizedRecentNarration,
        instruction,
        playerAction,
        rollData,
        movementResult,
        combatResult,
        spellAcquisitionResult,
        tradeResults,
        contractResults,
        npcResponses: npcResponseGroups,
        displayedSystemMessages: [],
        reason: 'completion',
      });
      const narration = executionPreview.narration || { description: '' };
      const narrationText = sanitizeNarrationTextForHiddenBeingNames(
        getNarrationDescription(narration),
        activeHiddenNameReplacements
      );

      const maskedRefreshedNearbyBeings = maskBeingsForPc(
        narrationNearbyBeings,
        committedKnownBeingNameIdSet,
        playerCharacterId
      );
      const refreshedSocialTargets = getSocialFrameTargets(maskedRefreshedNearbyBeings);
      const nextSocialTarget = resolveNextSocialTarget(playerAction, refreshedSocialTargets, npcResponseGroups, socialFrameCurrentTarget);
      const entityNameById = buildEntityNameById(
        [activePlayerCharacter],
        maskBeingsForPc(nearbyBeings, committedKnownBeingNameIdSet, playerCharacterId),
        maskedRefreshedNearbyBeings,
        [maskBeingIdentityForPc(primaryFocus, committedKnownBeingNameIdSet, playerCharacterId)],
        maskBeingsForPc(Object.values(nextCombatState?.combatantsById || {}), committedKnownBeingNameIdSet, playerCharacterId)
      );
      const npcSpeechMessages = combatResult
        ? getCombatSpeechMessages(combatResult, entityNameById, playerCharacterId, activeHiddenNameReplacements)
        : getNpcSpeechMessages(npcResponseGroups, await resolveNpcSpeakerNames({
          groups: npcResponseGroups, nameById: entityNameById,
          loadBeing: id => callCdo(cdoBeing.select.bind(cdoBeing), id),
          knownNameIds: committedKnownBeingNameIdSet, playerId: playerCharacterId,
        }));
      const nameRevelationRecord = { recorded: [], knownNameUpdates: [] };
      const narrationForState = {
        ...narration,
        description: narrationText,
        recordedNameRevelations: nameRevelationRecord,
      };
      const finalMessages = appendNoVisibleOutcomeMessage([
        ...messagesBeforeTurn,
        userMessage,
        ...npcSpeechMessages,
        ...(narrationText ? [{ role: 'assistant', content: narrationText, turnId: userMessage.turnId }] : []),
      ], messagesBeforeTurn, buildNoVisibleOutcomeMessage({
        playerAction,
        responseIntents,
        nearbyBeings: narrationNearbyBeings,
        npcResponseGroups,
      }));
      const recentNarration = buildRecentNarration(finalMessages);
      const nextNarrationContext = {
        ...narrationPayload,
        history: finalMessages,
        recentNarration,
        lastNarration: narrationForState,
      };
      const nextInteractionState = {
        ...interactionState,
        draft: '',
        isSubmitting: false,
        error: '',
        lastPlayerAction: playerAction,
        npcResponseGroups,
        npcPlaceholder,
        narrationPlaceholder: '',
        lastNarration: narrationForState,
        history: finalMessages,
        recentNarration,
        rollData,
        tradeResults,
        contractResults,
        spellAcquisitionResult,
        spellAcquisitionState: spellAcquisitionResult?.nextAcquisitionState ?? interactionState.spellAcquisitionState ?? null,
        combatResult,
        combatParticipationResult,
        currentSocialTarget: nextSocialTarget,
        messages: finalMessages,
      };

      setGameplayContext(nextGameplayContext);
      setPlayerCharacter(activePlayerCharacter || null);
      setCurrentPlaceContext(activePlaceContext);
      setKnownScenePlaces((current) => uniqueById([
        ...toArray(current),
        ...buildKnownScenePlaces(placeChain, activePlaceContext),
      ]));
      setExplorerLocation(nextExplorerLocation);
      setMovementState(nextMovementState);
      setCombatState(nextCombatState);
      setInteractionState(nextInteractionState);
      setNarrationContext(nextNarrationContext);
    } catch (error) {
      setInteractionState(current => recoverActionFailure(current, { error, turnId: userMessage.turnId, submittedText }));
    } finally { actionRequestRef.current = null; setActionProgress(null); }
  }, [
    combatState,
    currentPlaceContext,
    effectiveCurrentPlaceContext,
    explorerLocation,
    gameplayContext,
    interactionState,
    knownScenePlaces,
    activeInteriorPlaceId,
    movementState,
    onCharacterRefresh,
    pendingTradeConfirmation,
    pcKnownBeingNameIdSet,
    applyCompletedTurn,
    projectRoutineContext,
    npcRoutineState,
    placeChain,
    playerCharacter,
    playerCharacterId,
    rawActiveBeingCards,
    settlementScene,
    callPlayAI,
    callCdo,
  ]);

  const cancelPendingTrade = useCallback(async () => {
    if (!pendingTradeConfirmation || interactionState.isSubmitting) return;
    setInteractionState(current => ({ ...current, isSubmitting: true, error: '' }));
    try {
      const result = await callCdo(cdoGameplay.resolveCommerce.bind(cdoGameplay), {
        playerId: playerCharacterId, baseTurnId: gameplayContext?.turnId || '',
        confirmationId: pendingTradeConfirmation.confirmationId, accept: false,
      });
      const messages = [...toArray(interactionState.messages).filter(message => !message.pendingNarration),
        { role: 'assistant', content: result.narration?.description || 'You decline the exchange.' }];
      const nextInteractionState = { ...interactionState, messages, history: messages, isSubmitting: false, error: '' };
      setGameplayContext(result.gameplayContext || gameplayContext);
      if (result.playerCharacter) {
        const character = mergePlayVitals(result.playerCharacter, result.gameplayContext || gameplayContext);
        setPlayerCharacter(character);
        onCharacterRefresh?.(character);
      }
      setPendingTradeConfirmation(null);
      setInteractionState(nextInteractionState);
    } catch (error) {
      setInteractionState(current => ({ ...current, isSubmitting: false, error: error.message }));
    }
  }, [pendingTradeConfirmation, interactionState, playerCharacterId, gameplayContext, callCdo, onCharacterRefresh]);

  const confirmPendingTrade = useCallback(async () => {
    if (!pendingTradeConfirmation || interactionState.isSubmitting) {
      return;
    }

    setActionProgress(null);
    const continuation = { instruction: interactionState.lastPlayerAction?.instruction || '',
      messagesBeforeTurn: interactionState.messages, previewMessages: interactionState.messages,
      sanitizedMessagesBeforeTurn: interactionState.history, sanitizedRecentNarration: interactionState.recentNarration,
      playerAction: interactionState.lastPlayerAction, npcResponseGroups: interactionState.npcResponseGroups || [],
      nextGameplayContext: gameplayContext, nextExplorerLocation: explorerLocation, nextMovementState: movementState,
      nextCombatState: combatState, activePlaceContext: effectiveCurrentPlaceContext || currentPlaceContext,
      activePlayerCharacter: playerCharacter, currentSocialTarget: interactionState.currentSocialTarget,
      ...pendingTradeConfirmation.continuation };
    setInteractionState((current) => ({
      ...current,
      isSubmitting: true,
      error: '',
    }));

    try {
      const settlement = await callCdo(cdoGameplay.resolveCommerce.bind(cdoGameplay), {
        playerId: playerCharacterId, baseTurnId: gameplayContext?.turnId || '',
        confirmationId: pendingTradeConfirmation.confirmationId, accept: true,
      });
      const tradeResults = toArray(settlement.tradeResults);
      const contractResults = toArray(settlement.contractResults);
      continuation.nextGameplayContext = settlement.gameplayContext || continuation.nextGameplayContext;
      let activePlayerCharacter = settlement.playerCharacter || continuation.activePlayerCharacter || playerCharacter;
      let activePlaceContext = continuation.activePlaceContext || effectiveCurrentPlaceContext || currentPlaceContext;
      const continuationMovementInteriorPlaceId = getMovementInteriorPlaceId({
        movementState: continuation.nextMovementState,
        settlementScene,
      });

      if (!settlement.playerCharacter && (tradeResults.some((result) => result?.executed === true) || contractResults.some((result) => result?.executed === true))) {
        activePlayerCharacter = mergePlayVitals(await callCdo(cdoBeing.select.bind(cdoBeing), playerCharacterId), continuation.nextGameplayContext);
        if (activePlaceContext?.place) {
          activePlaceContext = await loadCurrentPlaceContext(activePlaceContext.place, npcRoutineState);
          activePlaceContext = await hydrateRoutinePlaceContext(activePlaceContext, npcRoutineState,
            continuation.nextExplorerLocation, continuation.nextMovementState.placeId, routineBeingProfilesRef.current, loadBeingDetailsByReference, { refresh: true });
        }
      }

      const narrationNearbyBeings = uniqueById([
        ...toArray(activePlaceContext?.beingsInLocation),
        ...getNearbyHydratedSubPlaceOccupants({
          currentPlaceContext: activePlaceContext,
          currentLocation: continuation.nextExplorerLocation,
          currentInteriorPlaceId: continuationMovementInteriorPlaceId,
        }),
      ]);
      const activeHiddenNameReplacements = buildHiddenBeingNameReplacements(
        narrationNearbyBeings,
        pcKnownBeingNameIdSet,
        playerCharacterId
      );
      const maskedActivePlaceContext = maskPlaceContextForPc(
        activePlaceContext,
        pcKnownBeingNameIdSet,
        playerCharacterId
      );
      const rollData = buildRollData({
        npcResponseGroups: continuation.npcResponseGroups,
        movementResult: continuation.movementResult,
        combatResult: continuation.combatResult,
        spellAcquisitionResult: continuation.spellAcquisitionResult,
      });
      const narrationPayload = buildPlayNarrationPayload({
        explorer: activePlayerCharacter,
        currentPlaceContext: maskedActivePlaceContext,
        scenePlaces: uniqueById([
          ...toArray(knownScenePlaces),
          ...buildKnownScenePlaces(placeChain, activePlaceContext),
        ]),
        gameplayContext: continuation.nextGameplayContext,
        currentLocation: continuation.nextExplorerLocation,
        currentInteriorPlaceId: continuationMovementInteriorPlaceId,
        history: [...toArray(continuation.sanitizedMessagesBeforeTurn), continuation.userMessage].filter(Boolean),
        recentNarration: continuation.sanitizedRecentNarration || [],
        instruction: continuation.instruction,
        playerAction: continuation.playerAction,
        rollData,
        movementResult: continuation.movementResult,
        combatResult: continuation.combatResult,
        spellAcquisitionResult: continuation.spellAcquisitionResult,
        tradeResults,
        contractResults,
        npcResponses: continuation.npcResponseGroups,
        displayedSystemMessages: [],
        reason: 'completion',
      });
      const narration = settlement.narration || { description: '' };
      const narrationText = sanitizeNarrationTextForHiddenBeingNames(
        getNarrationDescription(narration),
        activeHiddenNameReplacements
      );
      const maskedNarrationNearbyBeings = maskBeingsForPc(
        narrationNearbyBeings,
        pcKnownBeingNameIdSet,
        playerCharacterId
      );
      const nextSocialTarget = resolveCurrentSocialFrameTarget(
        continuation.currentSocialTarget,
        getSocialFrameTargets(maskedNarrationNearbyBeings)
      );
      const nameRevelationRecord = { recorded: [], knownNameUpdates: [] };
      const narrationForState = {
        ...narration,
        description: narrationText,
        recordedNameRevelations: nameRevelationRecord,
      };
      // Confirmation settles an existing exchange; it does not request a new
      // NPC reply. Empty follow-up narration needs no silence placeholder.
      const finalMessages = [
        ...toArray(continuation.previewMessages),
        ...(narrationText ? [{ role: 'assistant', content: narrationText }] : []),
      ];
      const recentNarration = buildRecentNarration(finalMessages);
      const nextNarrationContext = {
        ...narrationPayload,
        history: finalMessages,
        recentNarration,
        lastNarration: narrationForState,
      };
      const nextInteractionState = {
        ...interactionState,
        draft: '',
        isSubmitting: false,
        error: '',
        lastPlayerAction: continuation.playerAction,
        npcResponseGroups: continuation.npcResponseGroups,
        npcPlaceholder: continuation.npcPlaceholder,
        narrationPlaceholder: '',
        lastNarration: narrationForState,
        history: finalMessages,
        recentNarration,
        rollData,
        tradeResults,
        contractResults,
        spellAcquisitionResult: continuation.spellAcquisitionResult ?? null,
        spellAcquisitionState: continuation.spellAcquisitionResult?.nextAcquisitionState ?? interactionState.spellAcquisitionState ?? null,
        combatResult: continuation.combatResult,
        combatParticipationResult: continuation.combatParticipationResult,
        currentSocialTarget: nextSocialTarget,
        messages: finalMessages,
      };


      setPendingTradeConfirmation(null);
      setGameplayContext(continuation.nextGameplayContext);
      setPlayerCharacter(activePlayerCharacter || null);
      onCharacterRefresh?.(activePlayerCharacter);
      setCurrentPlaceContext(activePlaceContext);
      setKnownScenePlaces((current) => uniqueById([
        ...toArray(current),
        ...buildKnownScenePlaces(placeChain, activePlaceContext),
      ]));
      setExplorerLocation(continuation.nextExplorerLocation);
      setMovementState(continuation.nextMovementState);
      setCombatState(continuation.nextCombatState);
      setInteractionState(nextInteractionState);
      setNarrationContext(nextNarrationContext);
    } catch (error) {
      setInteractionState((current) => ({
        ...current,
        isSubmitting: false,
        error: error?.message || 'Failed to confirm trade.',
      }));
    } finally { setActionProgress(null); }
  }, [
    combatState,
    explorerLocation,
    gameplayContext,
    movementState,
    currentPlaceContext,
    effectiveCurrentPlaceContext,
    interactionState,
    knownScenePlaces,
    onCharacterRefresh,
    pcKnownBeingNameIdSet,
    pendingTradeConfirmation,
    npcRoutineState,
    placeChain,
    playerCharacter,
    playerCharacterId,
    settlementScene,
    callCdo,
  ]);

  const continueMovementInteraction = useCallback(() => {
    if (interactionState.isSubmitting) return;
    const targetName = movementState.lastMovementTarget?.targetName || 'the destination';
    void submitDraftInteraction(`Continue my paused journey toward ${targetName}, resuming its movement skill execution. Reassess my other concurrent activities and current conditions.`);
  }, [interactionState.isSubmitting, movementState.lastMovementTarget, submitDraftInteraction]);

  const interruptMovementInteraction = useCallback(() => {
    if (interactionState.isSubmitting) return;
    void submitDraftInteraction('Cancel my current journey and its movement skill execution. Keep my other activities unless they depend on that journey.');
  }, [interactionState.isSubmitting, submitDraftInteraction]);

  const renderPlaceCard = (rawPlace, index) => {
    const placeId = String(rawPlace?.idEntity ?? '');
    const label = String(rawPlace?.placeType || '').trim().toLowerCase() === 'world'
      ? 'World Map' : placePresentation.placeLabelFor(rawPlace);
    const imageState = placeImagesById[placeId] ?? {};
    return <PlayPlaceCard key={placeId || `place-${index}`} placeId={placeId} label={label} imageState={imageState}
      showImage={subPlaceImageIds.has(placeId)} onOpenMap={openPlaceMap} />;
  };

  const renderBeingCard = (being, partyMember = null, party = null) => {
    const beingId = getEntityId(being);
    const portraitState = beingPortraitsById[beingId] ?? {};
    return <PlayBeingCard key={beingId} label={getBeingLabel(being)} beingId={beingId} portrait={portraitState}
      loadRelationships={loadPortraitRelationships}
      headingNote={partyMember && (party?.leadActorId === beingId || !partyMember.present) &&
            <small className="party-card-note" title={!partyMember.present && partyMember.lastSeenMinute != null
              ? `Last seen at game minute ${partyMember.lastSeenMinute}` : undefined}>
              {[party?.leadActorId === beingId && 'Guide', !partyMember.present && 'Out of sight'].filter(Boolean).join(' · ')}
            </small>}
      vitals={partyMember && <PartyVitals vitals={partyMember.vitals} stale={!partyMember.present} />} />;
  };

  const renderPlayerCard = () => {
    const being = getEffectiveCombatExplorer(playerCharacter, combatState);
    const health = resolveStatusValue(being, ['currentHp', 'health', 'currentHealth', 'hitPoints', 'currentHitPoints'], 100);
    const maxHealth = resolveStatusValue(being, ['maxHp', 'maxHealth', 'maximumHealth', 'maxHitPoints', 'hitPointsMax'], 100);
    const maxMana = resolveOptionalStatusValue(being, ['maxPp', 'maxMagic', 'maximumMagic', 'maxMana', 'manaMax']);
    const mana = resolveOptionalStatusValue(being, ['currentPp', 'magic', 'currentMagic', 'mana', 'currentMana']) ?? 0;
    const hasMana = Number.isFinite(Number(maxMana)) && Number(maxMana) > 0;
    const stamina = resolveStatusValue(being, ['currentStamina', 'current_stamina'], 100);
    const maxStamina = resolveStatusValue(being, ['maxStamina', 'max_stamina'], 100);
    return <PlayPlayerCard label={being ? getBeingLabel(being) : 'Player Character'} portrait={playerPortrait}
      health={health} maxHealth={maxHealth} stamina={stamina} maxStamina={maxStamina} mana={mana} maxMana={maxMana} hasMana={hasMana}
      onOpenSheet={characterSheetProps ? () => setIsCharacterSheetOpen(true) : undefined} />;
  };

  const sceneParties = gameplayContext?.lastTurnResult?.partyRoster || gameplayContext?.partyRoster || [];
  const partyActorIds = new Set(sceneParties.flatMap(party => party.members.map(member => member.actorId)));
  const nearbyPlaceBeings = new Map(activePlaceCardGroups.nearby.map(place => [getEntityId(place), []]));
  const samePlaceBeings = [];
  for (const being of activeBeingCards) {
    if (partyActorIds.has(getEntityId(being))) continue;
    // Current occupancy, rather than residence or workplace, determines grouping.
    const occupants = nearbyPlaceBeings.get(being.containingPlaceId);
    if (occupants) occupants.push(being);
    else samePlaceBeings.push(being);
  }

  return (
    <main className="play2-page">
      <PlaySceneCards>
        {activePlaceCardGroups.containing.map(renderPlaceCard)}
        <PartyPanel parties={sceneParties} playerId={playerCharacterId}
          renderPlayer={renderPlayerCard} disabled={interactionState.isSubmitting || isBootstrapping || Boolean(pendingTradeConfirmation)}
          onInstruction={submitDraftInteraction} renderMember={(member, party) => renderBeingCard(
            activeBeingCards.find(being => getEntityId(being) === member.actorId) || { idEntity: member.actorId, publicLabel: member.label, name: member.label }, member, party)} />
        {!sceneParties.length && renderPlayerCard()}
        {samePlaceBeings.map(being => renderBeingCard(being))}
        {activePlaceCardGroups.nearby.flatMap((place, index) => [renderPlaceCard(place, index),
          ...nearbyPlaceBeings.get(getEntityId(place)).map(being => renderBeingCard(being))])}
      </PlaySceneCards>
      <HarvestInventory batches={gameplayContext?.lastTurnResult?.inventoryBatches || gameplayContext?.inventoryBatches || []}
        currentMinute={gameplayContext?.currentTimeMinutes}
        onInstruction={submitDraftInteraction} disabled={interactionState.isSubmitting || isBootstrapping || Boolean(pendingTradeConfirmation)} />

      <section className="play-conversation-panel play2-conversation-panel">
        <div className="play-status-stack">
          {statusMessage && <div className="panel-status">{statusMessage}</div>}
          {errorMessage && <div className="panel-status error-status">{errorMessage}</div>}
          {settlementScene && (
            <div className="play-session-meta play2-session-meta">
              <div className="panel-status play-time-status">Time of day: {currentTimeDisplay || 'Unknown'}</div>
              {canUseDebug && <button
                type="button"
                className="secondary-button play2-debug-button"
                onClick={() => setIsDebugViewOpen(true)}
              >
                Debug View
              </button>}
              <div className="panel-status play-weather-status">
                Weather: {formatCurrentWeather(gameplayContext?.currentWeather)}
              </div>
            </div>
          )}
        </div>

        <PlayChatLog messages={visibleMessages} hasMessages={interactionState.messages.length > 0} logRef={chatLogRef} speakerLabel={safeSpeakerLabel}
          emptyText={isBootstrapping || interactionState.isSubmitting ? 'Starting...' : 'No conversation yet.'} />

        <div className="play-composer">
          {interactionState.error && <div className="form-error">{interactionState.error}</div>}
          {!interactionState.isSubmitting && movementState.pendingRoute.length > 0
            && !(placeKnowledge.destinationSearch?.movementActivityId && placeKnowledge.destinationSearch.movementActivityId === movementState.journey?.activityId
              && placeKnowledge.destinationSearch?.status !== 'cancelled') && (
            <div className="play2-movement-prompt" role="status" aria-live="polite">
              <div className="play2-movement-prompt-text">
                <strong>{movementState.journey?.status === 'paused' ? 'Journey paused' : 'Journey in progress'}</strong>
                <span>{pendingMovementDescription}</span>
              </div>
              <div className="play2-movement-prompt-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={continueMovementInteraction}
                  disabled={isBootstrapping || interactionState.isSubmitting || Boolean(errorMessage) || Boolean(pendingTradeConfirmation) || ['dead', 'incapacitated'].includes(gameplayContext?.health?.combatStatus)}
                >
                  Continue
                </button>

                <button
                  type="button"
                  className="secondary-button"
                  onClick={interruptMovementInteraction}
                  disabled={isBootstrapping || interactionState.isSubmitting || Boolean(errorMessage) || Boolean(pendingTradeConfirmation) || gameplayContext?.health?.combatStatus === 'dead'}
                >
                  Cancel journey
                </button>
              </div>
            </div>
          )}
          {!interactionState.isSubmitting && (placeKnowledge.destinationSearch?.status === 'awaiting_recognition'
            || placeKnowledge.destinationSearch?.recognitionStatus === 'pending')
            && knowledgeReview.playerId === playerCharacterId && (
            <div role="status" aria-live="polite">
              {['processing', 'retrying'].includes(knowledgeReview.status) ? 'Reviewing observations…' : null}
            </div>
          )}
          {!interactionState.isSubmitting && visibleResumeOptions(placeKnowledge, gameplayContext?.lastTurnResult?.resumeOptions,
            movementState.journey?.activityId).map(option => (
            <button key={option.activityId || option.searchId} type="button" className="secondary-button"
              disabled={interactionState.isSubmitting || Boolean(pendingTradeConfirmation)
                || (option.executionControl?.operation === 'resume' && option.searchId
                  && (placeKnowledge.destinationSearch?.recognitionStatus === 'pending' || placeKnowledge.destinationSearch?.status === 'awaiting_recognition')
                  && knowledgeReview.playerId === playerCharacterId && knowledgeReview.status === 'processing')}
              onClick={() => void submitDraftInteraction(option.instruction || `I continue: ${option.label.replace(/^Continue\s*/i, '')}.`,
                option.executionControl || (option.activityId ? { operation: 'resume', activityId: option.activityId } : null))}>{option.label}</button>
          ))}
          {(combatState?.active === true || combatState?.sceneActive === true || combatState?.aftermathPending) && (
            <div className="play2-movement-prompt play2-combat-prompt" role="status" aria-live="polite">
              <div className="play2-movement-prompt-text">
                <strong>{combatState.aftermathPending ? 'Finishing the turn' : combatState.active ? 'Combat in progress' : 'Fighting nearby'}</strong>
                <span>{combatState.aftermathPending ? 'The actions are resolved. Retry to finish the scene.' : combatState.active ? 'Declare an action, words, retreat, surrender, or another attempt.' : 'You can watch, act independently, or rejoin the fighting.'}</span>
                {combatState.aftermathPending && <button type="button" disabled={interactionState.isSubmitting}
                  onClick={() => submitDraftInteraction(combatState.aftermathRequest?.text || 'Continue', combatState.aftermathRequest?.executionControl || null)}>Retry</button>}
              </div>
            </div>
          )}
          <PlayActionProgress active={interactionState.isSubmitting} playerId={playerCharacterId}
            turnId={actionProgress?.turnId} progress={actionProgress?.progress} />
          <PlayActionComposer
            draftOwnerId={playerCharacterId}
            isLoading={isBootstrapping}
            savedDraft={interactionState.draft}
            inputRef={actionInputRef}
            onSubmit={submitDraftInteraction}
            isSubmitting={interactionState.isSubmitting}
            actions={onExitGame ? <ExitGameButton onExit={onExitGame} /> : null}
            disabled={isBootstrapping || Boolean(errorMessage) || Boolean(pendingTradeConfirmation) || combatState?.aftermathPending || gameplayContext?.health?.combatStatus === 'dead' || gameplayContext?.skillProgression?.phase === 'allocationRequired'}
          >
          </PlayActionComposer>
        </div>
      </section>

      {isCharacterSheetOpen && characterSheetProps && (
        <div className="play-character-sheet-overlay" role="dialog" aria-modal="true" aria-label="Character sheet">
          <div className="play-character-sheet-modal">
            <button
              type="button"
              className="play-character-sheet-close"
              onClick={() => setIsCharacterSheetOpen(false)}
              aria-label="Close character sheet"
            >
              Close
            </button>
            <CharacterSheet
              {...characterSheetProps}
              portrait={playerPortrait.src ? { ...characterSheetProps?.portrait, src: playerPortrait.src, isSaved: true } : characterSheetProps?.portrait}
              currentGameMinute={gameplayContext?.currentTimeMinutes}
              vitals={getEffectiveCombatExplorer(playerCharacter, combatState)}
              mode="play"
              onContinue={() => setIsCharacterSheetOpen(false)}
            />
          </div>
        </div>
      )}

      <TradeConfirmationModal
        pendingTradeConfirmation={pendingTradeConfirmation}
        onConfirm={confirmPendingTrade}
        onCancel={cancelPendingTrade}
        isSubmitting={interactionState.isSubmitting}
      />

      <SkillProgressionPanel progression={gameplayContext?.skillProgression || playerCharacter?.skillProgression}
        onAllocate={async request => {
          const result = await callCdo(cdoGameplay.allocateProgression.bind(cdoGameplay), { ...request, playerId: playerCharacterId });
          setPlayerCharacter(result.playerCharacter);
          setGameplayContext(current => ({ ...current, skillProgression: result.projection }));
          setInteractionState(current => ({ ...current, messages: result.sessionData.interactionState.messages,
            history: result.sessionData.interactionState.history }));
          if (typeof onCharacterRefresh === 'function') await onCharacterRefresh(result.playerCharacter);
        }} />

      {mapModalPlaceId && <PlaceMapModal
        isOpen
        onClose={closePlaceMap}
        place={placeIdentityRef.current.sanitize(mapModalPlace)}
        mapPlan={placeIdentityRef.current.sanitize(placeMapPlan)}
        placeLabelFor={placePresentation.placeLabelFor}
        zoom={placeMapZoom}
        onZoomIn={zoomPlaceMapIn}
        onZoomOut={zoomPlaceMapOut}
        playerPortraitSrc={playerPortrait.src}
        playerLabel={playerCharacter ? getBeingLabel(playerCharacter) : 'Player Character'}
        beingPortraitsById={beingPortraitsById}
      />}

      {canUseDebug && isDebugViewOpen && <Play2DebugView
        isOpen
        onClose={() => setIsDebugViewOpen(false)}
        interactionState={placeIdentityRef.current.sanitize({
          ...interactionState, draft: actionInputRef.current?.value ?? interactionState.draft,
        })}
        narrationContext={placeIdentityRef.current.sanitize(narrationContext)}
        currentPlaceContext={placeIdentityRef.current.sanitize(effectiveCurrentPlaceContext)}
        playerCharacter={playerCharacter}
        movementState={placeIdentityRef.current.sanitize(movementState)}
        combatState={combatState}
        settlementScene={placeIdentityRef.current.sanitize(settlementScene)}
        routineEvaluation={gameplayContext?.lastTurnResult?.routineEvaluation}
        turnTrace={gameplayContext?.lastTurnResult?.trace}
        playerId={playerCharacterId}
        turnId={gameplayContext?.turnId}
        skillProgression={gameplayContext?.skillProgression || playerCharacter?.skillProgression}
      />}
    </main>
  );
}

export default Play2;
